import { environment } from "../../environments/environment";

export class Constants {
    //General Error Message
    /** User-facing fallback used by AlertService.error() when no real
     *  backend/API message is available. Surfaces a friendly, non-technical
     *  string instead of leaking raw HTTP status text like "Internal Server Error". */
    public static NOTE_ATTACHMENTS = {
        MAX_FILE_SIZE_MB: 40,
        /** Sub-label under the dropzone's title. States the only rule there is. */
        DROP_HINT: 'Maximum file size 40MB',
        /** The same rule inside the compact one-line field, after an em dash —
         *  console-v2's `.srd-drop` reads "… drag it here — max 40MB". */
        DROP_LINE_HINT: 'max 40MB',
        /**
         * The `accept` attribute every note file input carries.
         *
         * A HINT, not validation: it pre-selects the common formats in the OS
         * file dialog. Anything missing here is greyed out and cannot be
         * chosen, which is how GIF, WebP and AVIF came to be unattachable even
         * though nothing actually refused them — `NOTE_ATTACHMENT_PROFILE` has
         * no type allowlist and the API's note routes run `allowedTypes: ['*']`,
         * so all three upload, store and preview fine. Added here so the dialog
         * stops hiding them.
         *
         * SVG is deliberately still absent: it is active content and IS refused,
         * by `ACTIVE_CONTENT_EXTENSIONS` here and by the API's own guard.
         *
         * `.tif`/`.tiff` are listed for the same reason GIF/WebP/AVIF are: nothing
         * refuses a TIFF, but leaving it out greyed it out in the OS file dialog, so
         * a drag-and-drop was the only way to attach one. Its preview is produced in
         * the browser (`tiff-preview.util.ts`) — no browser but Safari decodes TIFF.
         *
         * `.csv` was the last of these: the required set is CSV / XLSX / PDF / DOCX and
         * it was the only one the dialog greyed out, on every note surface at once —
         * all five note file inputs bind this one string. Nothing refused a CSV
         * (`NOTE_ATTACHMENT_PROFILE` has no allowlist, the API's note routes run
         * `allowedTypes: ['*']`), and it even has a purpose-built preview
         * (`csv-preview.util.ts` renders it as a table, because no browser has a
         * viewer for `text/csv`) — so the only thing standing between a user and a
         * CSV attachment was this hint.
         */
        ACCEPT: '.pdf,.doc,.docx,.xls,.xlsx,.csv,.jpg,.jpeg,.png,.gif,.webp,.avif,.heic,.heif,.tif,.tiff',
    };

    public static SOMETHING_WENT_WRONG = 'Something went wrong.';
    public static FORBIDDEN = 'Forbidden request';
    public static INTERNAL_SERVER_ERROR = 'Internal server error.';

    /* ── transport failures ───────────────────────────────────────────────────
       One string per failure class, so no feature writes its own network copy.
       These replace Angular's `HttpErrorResponse.message`, which is a FRAMEWORK
       string of the form `Http failure response for {url}: {status} {statusText}`
       — it names the internal API host and route, which is reconnaissance
       material and means nothing to the reader.

       NO_INTERNET is only used when the browser positively reports itself
       offline. `status: 0` on its own does NOT mean offline: it means no
       response was received, which is equally a CORS rejection, a DNS or TLS
       failure, an ad-blocker, or an aborted request. On staging a misconfigured
       CORS policy looks exactly like a dead network, and telling a developer
       "no internet connection" sends them after a problem that isn't there —
       so UNREACHABLE deliberately does not assert a cause. */
    public static NO_INTERNET = 'No internet connection. Please check your network and try again.';
    public static UNREACHABLE = "Can't reach GatherCo right now. Check your connection and try again.";
    public static REQUEST_TIMEOUT = 'That took too long. Try again.';
    public static SERVER_ERROR = 'Something went wrong on our end. Try again in a moment.';
    public static BED_REQUEST = 'Bad request';
    public static REQUEST_NOT_FOUND = 'Request not found.';
    public static RECORD_NOT_FOUND = 'Record not found.';
    public static METHOD_NOT_ALLOW = 'Method not allowed.';
    public static NOT_ACCEPTABLE = 'Not acceptable';
    public static REQUEST_ENTITY_TOO_LARGE = 'Request entity too large.';
    public static UN_SUPPORT_MEDIA = 'Unsupported media type.';
    public static REQUEST_TIME_OUT = 'Request timeout';
    public static NOT_IMPLEMENTED = 'Not implemented';
    public static BED_GATEWAY = 'Bad gateway';
    public static SERVICE_UNAVAILABLE = 'Service unavailable';
    public static GATEWAY_TIME_OUT = 'Gateway timeout';

    public static VERIFICATION_REQUIRED = "Y";
    public static PASSWORD_UPDATE_MESSAGE = 'Password updated.';
    public static PRODUCT_IS_FEATURE_PROFILE_YES = 'YES';
    public static PRODUCT_IS_SALE_YES = 'YES';

    public static QUOTE_IS_EXPIRED_NO = false;
    public static QUOTE_IS_EXPIRED_YES = true;

    public static USER_STATUS_ACTIVE = 'A';
    public static USER_STATUS_INACTIVE = 'I';
    public static PAGE_SIZE_ALL = 'all';

    public static ACCOUNT_STATUS_ACTIVE = 'A';
    public static ACCOUNT_STATUS_SUSPENDED = 'I';

    public static PEBBL_FEEDBACK_STATUS_PENDING = 'pending';
    public static PEBBL_FEEDBACK_STATUS_COMPLETED = 'completed';
    public static PEBBL_FEEDBACK_RATING_THUMBS_DOWN = 'thumbs_down';
    public static IS_DELETED_NO = '0';

    // Where a thumbs-down was raised. The API tags website-chat rows agent_type = 'frontend';
    // anything else came from a staff member chatting inside the console. Email feedback is
    // triaged in its own view, so it never reaches the Pebbl Feedback list.
    public static PEBBL_FEEDBACK_AGENT_TYPE_FRONTEND = 'frontend';
    public static PEBBL_FEEDBACK_SOURCE_WEBSITE = 'Website';
    public static PEBBL_FEEDBACK_SOURCE_ADMIN_CONSOLE = 'Admin Console';

    // Pebbl Feedback routes & page chrome
    public static PEBBL_FEEDBACK_ROUTE = '/pebbl-feedback';
    public static PEBBL_KNOWLEDGE_FEEDBACK_ROUTE = '/pebbl-knowledge-feedback';
    public static PEBBL_FEEDBACK_PAGE_TITLE = 'Pebbl Feedback | GatherCo';
    public static PEBBL_KNOWLEDGE_FEEDBACK_PAGE_TITLE = 'Pebbl Knowledge Base | GatherCo';
    public static PEBBL_FEEDBACK_PAGE_HEADING = 'Pebbl Feedback';
    public static PEBBL_KNOWLEDGE_FEEDBACK_PAGE_HEADING = 'Pebbl Knowledge Base';

    // Pebbl Ingest job statuses (server-side).
    // Explicit literal types so discriminated-union narrowing (e.g. IngestJobStatus) is preserved.
    public static readonly PEBBL_INGEST_JOB_STATUS_PENDING: 'pending' = 'pending';
    public static readonly PEBBL_INGEST_JOB_STATUS_COMPLETED: 'completed' = 'completed';
    public static readonly PEBBL_INGEST_JOB_STATUS_FAILED: 'failed' = 'failed';

    public static CONTACT_STATUS_ACTIVE = 'A';
    public static CONTACT_STATUS_SUSPENDED = 'I';
    public static CONTACT_STATUS_DECLINED = 'D';
    public static CONTACT_STATUS_PENDING_APPROVAL = 'N';

    //STATUS
    public static PRODUCT_STATUS_ACTIVE = 'A';
    public static PRODUCT_MATERIAL = 'M';
    public static PRODUCT_FORMAT = 'F';
    public static PRODUCT_CATEGORY = 'C';
    public static QUOTE = 'quote';
    public static STATUS_ACTIVE_SHORT = 'A';
    public static IS_FEATURE_PROFILE_NO = 'NO';
    public static IS_FEATURE_PROFILE_YES = 'YES';

    public static ACTIVE = 'Active';
    public static INACTIVE = 'Inactive';
    public static SUSPEND = 'Suspended';
    public static DECLINED = 'Declined';
    public static FOLLOW_UP = 'Follow Up';
    public static PENDING_APPROVAL = 'Pending Approval'
    public static STATUS_PENDING_APPROVAL = 'I';

    public static TYPE_GENERAL = '1';
    public static TYPE_TRADE = '2';
    public static SURFACE_SORT_NAME = 'S';
    public static SURFACE_SAMPLE_SORT_NAME = 'SS';
    public static DECOR_SORT_NAME = 'D';
    public static PRODUCT_TYPE_SURFACE_ACCESSORY = 'SA';
    public static PRODUCT_TYPE_DECOR_ACCESSORY = 'DA';
    public static SAMPLE_PRODUCT_SORT_NAME = 'SM';

    public static ROLE_TRADE = 'Trade';
    public static ROLE_GENERAL = 'General';
    public static TYPE_WEBSITE_USER = 'Website';
    public static TYPE_SALESFORCE_USER = 'Salesforce';
    public static STATUS_CREATE_QUOTE = 1;
    public static STATUS_CREATE_SAMPLE = 2;
    public static STATUS_QUOTE_SHIPPING_REQUIRED = 4;

    public static ORDER_TYPE_PRODUCT = 'p';
    public static ORDER_TYPE_GUEST_USER = 'gu';

    public static STATUS_QUOTE_ACTIVE = 1;
    public static STATUS_QUOTE_ACCEPT = 2;
    public static STATUS_QUOTE_EXPIRED = 3;
    public static STATUS_REQUEST_DELIVERY_COST = 4;
    public static STATUS_PENDING_DELIVERY_COST = 5;
    public static STATUS_SEND_DELIVERY_REQUEST = 6;
    public static STATUS_PACKAGING = 8;
    public static STATUS_IN_TRANSIT = 10;
    public static ORDER_DEPOSIT_EXPIRY_DAYS = 7;

    public static QUOTES_TYPE_ID = 1;
    public static ORDERS_TYPE_ID = 2;

    public static CONTACT_TYPE_ID = 1;
    public static ACCOUNT_TYPE_ID = 2;

    // Message
    public static SUCCESS_MESSAGE_BUSINESS_ADDRESS_ADDED = 'Business address added.';
    public static SUCCESS_MESSAGE_BUSINESS_ADDRESS_UPDATED = 'Business address updated.';
    public static SUCCESS_MESSAGE_BUSINESS_ADDRESS_DELETE = 'Business address deleted.';
    public static SUCCESS_ACCOUNT_ADDRESS_DEFAULT = 'Default address updated.';
    public static SUCCESS_MESSAGE_RESEND_OTP = 'Verification code resent.';
    public static CONFIRMATION_MESSAGE_FOR_SUSPEND_ACCOUNT = 'Are you sure you want to suspend this account?';
    public static SUCCESS_MESSAGE_ACCOUNT_SUSPEND = 'Account suspended.';
    public static CONFIRMATION_MESSAGE_FOR_RESUME_ACCOUNT = "Are you sure you want to resume this account?";
    public static SUCCESS_MESSAGE_ACCOUNT_RESUME = 'Account resumed.';
    public static SUCCESS_MESSAGE_OF_ACCOUNT_ADDED = 'Account added.';
    public static SUCCESS_MESSAGE_OF_ACCOUNT_UPDATE = 'Account updated.';
    public static CONFIRMATION_MESSAGE_BUSINESS_ADDRESS_DELETE = 'Are you sure you want to delete this business address?';
    public static CONFIRMATION_MESSAGE_HOME_ADDRESS_DELETE = 'Are you sure you want to delete this home address?';
    // Pebbl confirm-dialog body copy (matches the internal-note delete tone).
    public static DELETE_BUSINESS_ADDRESS_BODY = 'This business address will be permanently removed. This can’t be undone.';
    public static DELETE_HOME_ADDRESS_BODY = 'This home address will be permanently removed. This can’t be undone.';
    public static CONFIRMATION_MESSAGE_CONTACT_SUSPEND = 'Are you sure you want to suspend this contact?';
    public static CONFIRMATION_MESSAGE_CONTACT_DECLINED = 'Are you sure you want to declined this contact?';
    public static CONFIRMATION_MESSAGE_CONTACT_DELETE = 'Are you sure you want to delete this contact?';
    public static SUCCESS_MESSAGE_OF_CONTACT_SUSPEND = 'Contact suspended.';
    public static SUCCESS_MESSAGE_OF_CONTACT_DECLINED = 'Contact declined.';
    public static SUCCESS_MESSAGE_OF_CONTACT_DELETED = 'Contact deleted.';
    public static CONFIRMATION_MESSAGE_OF_CONTACT_RESUME = 'Are you sure you want to resume this contact?';
    public static CONFIRMATION_MESSAGE_OF_CONTACT_ACTIVE = 'Are you sure you want to active this contact?';
    public static SUCCESS_MESSAGE_OF_CONTACT_RESUME = 'Contact resumed.';
    public static SUCCESS_MESSAGE_OF_CONTACT_ACTIVE = 'Contact is now active.';
    public static SUCCESS_MESSAGE_OF_CONTACT_ADDED = 'Contact added.';
    public static SUCCESS_MESSAGE_OF_CONTACT_UPDATE = 'Contact updated.';
    public static CONFIRMATION_MESSAGE_OF_APPROVE_ACCOUNT = 'Are you sure you want to approve this account?';
    public static SUCCESS_MESSAGE_OF_ACCOUNT_APPROVE = 'Account approved.';
    public static ERROR_MESSAGE_SELECT_ASSIGN_USER = 'Select a sales representative before approving the contact.'
    public static SUCCESS_MESSAGE_OF_PRODUCT_SHEET_DATA_UPLOADED = 'Product updated.';
    public static CONFIRMATION_MESSAGE_OF_MAKE_PRODUCT_AS_FEATURE = "Are you sure you want to add this product as Feature product?";
    public static SUCCESS_MESSAGE_OF_FEATURE_PRODUCT = 'Product add as Feature product.';
    public static CONFIRMATION_MESSAGE_OF_MAKE_PRODUCT_AS_ALL_IN_ONE = 'Are you sure you want add this product as All in one?';
    public static SUCCESS_MESSAGE_OF_PRODUCT_AS_ALL_IN_ONE = 'Product add as All in one.';
    public static CONFIRMATION_MESSAGE_OF_REMOVE_PRODUCT_FROM_FEATURE = 'Are you sure you want to remove this product as Feature product?';
    public static SUCCESS_MESSAGE_OF_REMOVE_PRODUCT_FROM_FEATURE = 'Product removed from Feature product.';
    public static CONFIRMATION_MESSAGE_OF_REMOVE_FROM_ALL_IN_ONE = 'Are you sure you want add this product as All in one?';
    public static SUCCESS_MESSAGE_OF_REMOVE_PRODUCT_FROM_ALL_IN_ONE = 'Product removed from All in one.';
    public static SUCCESS_MESSAGE_OF_PRODUCT_IMAGE_UPDATED = 'Product image order updated.';
    public static SUCCESS_MESSAGE_OF_ACCOUNT_LOGO_UPLOADED = 'Account logo uploaded.';
    public static SUCCESS_MESSAGE_OF_ACCOUNT_LOGO_REMOVED = 'Account logo has been removed successfully.';
    // Contact records say "logo" like accounts do, even though the API field is
    // `profile_picture`. Kept separate from the PROFILE_IMAGE pair below, which is the
    // signed-in user's own photo (edit-profile) and still reads "Profile image".
    public static SUCCESS_MESSAGE_OF_CONTACT_LOGO_UPLOADED = 'Contact logo has been uploaded successfully.';
    public static SUCCESS_MESSAGE_OF_CONTACT_LOGO_REMOVED = 'Contact logo has been removed successfully.';
    public static SUCCESS_MESSAGE_OF_PROFILE_IMAGE_UPLOADED = 'Profile image has been uploaded successfully.';
    public static SUCCESS_MESSAGE_OF_PROFILE_IMAGE_REMOVED = 'Profile image has been removed successfully.';
    public static SUCCESS_MESSAGE_RESET_PASSWORD = "Resent password mail sent to user.";
    public static SUCCESS_MESSAGE_SAVE_SETTING = "Setting data updated.";
    public static CONFIRM_DELETE_USER = 'Are you sure you want to delete this user?';
    public static SUCCESS_MESSAGE_DELETE_USER = 'User deleted.';
    public static SUCCESS_MESSAGE_SAVE_USER = 'User added.';
    public static SUCCESS_MESSAGE_UPDATE_USER = 'User updated.';
    public static CONFIRMATION_CART_PRODUCT_EXIST_TITLE = 'Alert';
    public static SUCCESS_MESSAGE_ADD_QUANTITY = "This product already exist in your quote. Would you like to increase the total qty?";
    public static SUCCESS_MESSAGE_CONTAINER_ADD_QUANTITY = "This product already exist in your container. Would you like to increase the total qty?";
    public static CONFIRM_DELETE_CARRIER = 'Are you sure you want to delete this carrier?';
    public static SUCCESS_MESSAGE_DELETE_CARRIER = 'Carrier deleted.';
    public static SUCCESS_MESSAGE_CONTACT_ADDRESS_ADDED = 'Contact address added.';
    public static SUCCESS_MESSAGE_CONTACT_ADDRESS_UPDATED = 'Contact address updated.';
    public static SUCCESS_MESSAGE_HOME_ADDRESS_DELETE = 'Home address deleted.';
    public static SUCCESS_MESSAGE_CONTACT_ADDRESS_DEFAULT = 'Default address updated.';

    public static SUCCESS_MESSAGE_ADD_QUANTITY_FOR_ACCESSORIES(profileName: String) {
        return `Product accessories ${profileName} already exist in your quote. Would you like to increase the total QTY?`
    };

    public static UNABLE_COMPLETED_ORDER_STATUS_CHANGE = 'You can’t update the order status. This order is already completed.';
    public static UNABLE_ORDER_STATUS_CHANGE = 'You can’t update the order status. This quote is not accepted.';
    public static UNABLE_COMPLETED_QUOTE_STATUS_CHANGE = 'You can’t update the quote status. This quote is already completed.';
    public static UNABLE_COMPLETED_SAMPLE_ORDER_STATUS_CHANGE = 'You can’t update the sample order status. This sample order is already completed.';
    public static UNABLE_QUOTE_STATUS_CHANGE = 'You can’t update the quote status. This quote is not accepted.';
    public static SUCCESS_MESSAGE_DELIVERY_ADDRESS_UPDATED = 'Delivery address updated.';
    public static SUCCESS_MESSAGE_LOGISTIC_DETAIL_UPDATED = 'Logistic detail updated.';
    public static UNABLE_QUOTE_STATUS_CHANGE_WITHOUT_DELIVERY = 'Add delivery details to update quote status.';
    public static UNABLE_QUOTE_DETAIL_CHANGE = 'This quote is archived - unarchive first.';
    public static UNABLE_ORDER_DETAIL_CHANGE = 'This order is archived - unarchive first.';
    public static UNABLE_SAMPLE_ORDER_DETAIL_CHANGE = 'This sample request is archived - unarchive first.';
    public static UNABLE_QUOTE_STATUS_CHANGE_WITH_NO_DELIVERY_CHARGE = 'You can’t update the quote status because the quote has no delivery charge added.';
    public static UNABLE_ORDER_STATUS_CHANGE_WITHOUT_JOB_CONFIRMATION = 'You can’t change the order status while job confirmation is pending.';
    public static UNABLE_QUOTE_STATUS_CHANGE_WITH_DRAFT_STATUS = 'This quote is saved as a draft - create a quote first.';
    public static UNABLE_QUOTE_STATUS_CHANGE_WITH_EXPIRED = 'This quote is expired - reactivate it first.';
    public static UNABLE_RESET_PASSWORD_LINK = 'You can’t send reset password link because the contact is not active.';
    public static UNABLE_CREATE_SAMPLE_REQUEST = 'You can’t create sample request because the contact is not active.';
    public static UNABLE_CREATE_QUOTE = 'You can’t create quote because the contact is not active.';
    public static UNABLE_SAMPLE_ORDER_STATUS_CHANGE_WITHOUT_PACKING_IMAGE = 'You can’t update the status of the sample request because packing images are not available for all products.';
    public static UNABLE_TO_CHANGE_DATE = 'You can’t change the date because the order is canceled.';
    public static UNABLE_DELIVERED_QUOTE_PRODUCT_CHANGE = 'You can’t update the product. This order is already delivered.';
    public static UNABLE_DELIVERED_ORDER_SRD_CHANGE = 'You can’t update the Stock Required Date (SRD) as this delivery has already been completed.';
    public static UNABLE_DELIVERED_QUOTE_PRODUCT_ADD = 'You can’t add the product. This order is already delivered.';
    public static UNABLE_COMPLETED_QUOTE_PRODUCT_ADD = 'You can’t add the product. This order is already completed.';
    public static UNABLE_COMPLETED_QUOTE_PRODUCT_CHANGE = 'You can’t update the product. This order is already completed.';
    public static UNABLE_TASK_STATUS_CHANGE = 'You can’t update the task status. This order is already delivered.';
    public static UNABLE_TO_CHANGE_STATUS_OTT = 'All products must be in the warehouse before change status to Out To Tender.';
    public static UNABLE_TO_CHANGE_DATE_FOR_OFFLINE_ORDER = 'You can’t change the date because the order does not have a delivery address.';
    public static UNABLE_TO_UPDATE_STATUS_FOR_OFFLINE_ORDER = 'You can’t update the status because the order does not have a delivery address.';
    public static UNABLE_UNASSIGNED_QUOTE_PRODUCT_CHANGE = 'You can’t update the product. This order is already in a delivery stage.';
    // Placeholder
    public static INPUT_PLACEHOLDER = 'Search';
    public static DROPDOWN_PLACEHOLDER = 'Select';
    public static ROLE_ADMIN = 'Admin';
    public static DROPDOWN_FILTER_BY_TYPE = 'Filter by type';
    public static DOWNLOAD_TYPE = 'Export';

    // Order type
    public static ORDER_TYPE_QUOTE = 'Quote';
    public static ORDER_TYPE_ORDER = 'Order';
    public static ORDER_TYPE_SAMPLE = 'Sample';

    public static ORDER_TYPE_QUOTE_SHORT = 'Q';
    public static ORDER_TYPE_ORDER_SHORT = 'O';
    public static ORDER_TYPE_SAMPLE_SHORT = 'SO';
    public static ORDER_TYPE_SALES_QUOTE_SHORT = 'SQ';
    // Unleashed-issued sales order number prefix (e.g. SO-00002432)
    public static SALES_ORDER_NUMBER_PREFIX = 'SO-';
    public static ORDER_TYPE_ACCOUNT_SHORT = 'AC';
    public static ORDER_TYPE_CONTACT_SHORT = 'C';
    public static ORDER_TYPE = 'S';
    public static QUOTE_PRODUCT_TYPE = 'QP';
    public static ORDER_SPLIT_FOR = 'D';

    // Payment rounding tolerance (in dollars). Residuals within this amount
    // between the system-calculated total and the client's net paid amount are
    // absorbed as a "Round Off" rather than prompting the customer to pay/refund.
    public static PAYMENT_ROUNDING_TOLERANCE = 0.03;

    public static TYPE_QUOTE = 'Quote';
    public static TYPE_ORDER = 'Order';
    public static TYPE_SAMPLE_REQUEST = 'Sample Request';

    public static FORK_TRUCK = 'Fork Truck';
    public static PRODUCT_RESOURCE_DOWNLOAD_SORT = 'PSR';
    public static TRUCK_TYPE_CLIENT_ORGANIZED = 'Client Organized';


    //Quote status
    // public static QUOTE_PENDING_SHORT = 'Q';
    // public static QUOTE_ACCEPT_SHORT = 'A';
    // public static QUOTE_SHIPPING_REQUIRED_SHORT = 'SR';
    // public static QUOTE_SHIPPING_PENDING_APPROVAL_SHORT = 'SPA';
    // public static QUOTE_SHIPPING_APPROVED_SHORT = 'SA';
    // public static QUOTE_FOLLOW_UP_SHORT = 'FU';
    // public static QUOTE_DEPOSIT_PENDING_SHORT = 'DP';
    // public static QUOTE_DEPOSIT_PAID_SHORT = 'DPD';
    // public static QUOTE_EXPIRED_SHORT = 'EXP';
    // public static QUOTE_ARCHIVED_SHORT = 'QA';

    public static QUOTE_CREATED_SHORT = 'QC';
    public static QUOTE_CREATED_ID = 2;
    public static QUOTE_ACCEPTED_SHORT = 'QA';
    public static QUOTE_MANUAL_DELIVERY_QUOTE_REQUIRED_SHORT = 'MDQR';
    public static QUOTE_MANUAL_DELIVERY_QUOTE_PENDING_SHORT = 'MDQP';
    public static QUOTE_MANUAL_DELIVERY_QUOTE_ADDED_SHORT = 'MDQAD';
    public static QUOTE_MANUAL_DELIVERY_QUOTE_APPLIED_SHORT = 'MDQAP';
    // public static QUOTE_FOLLOW_UP_SHORT = 'FU';
    public static QUOTE_DEPOSIT_PAYMENT_DUE_SHORT = 'DPDU';
    public static QUOTE_DEPOSIT_PAID_SHORT = 'DPD';
    public static QUOTE_EXPIRED_SHORT = 'EXP';
    public static QUOTE_ARCHIVED_SHORT = 'ARCH';
    public static QUOTE_AUTO_DELIVERY_QUOTE_APPLIED_SHORT = 'ADQAP';
    public static QUOTE_ORDER_CREATED_SHORT = 'OC';
    public static QUOTE_DRAFT_SHORT = 'DFT';
    public static QUOTE_DEPOSIT_PAID_ID = 6;
    public static CLIENT_ORGANIZED_ORDER_COLLECTION_SHORT = 'COOC';
    public static QUOTE_DEPOSIT_PAYMENT_PROCESSING_SHORT = 'DPPROC';
    public static QUOTE_FINAL_PAYMENT_PROCESSING_SHORT = 'FPPROC';
    public static QUOTE_FINAL_PAYMENT_AND_FULL_PROCESSING_SHORT = 'PPROC';
    public static QUOTE_FULL_PAYMENT_PROCESSING_SHORT = 'FUPPROC';
    public static QUOTE_EXPIRY_IMMANENT_SHORT = 'QEI';
    public static QUOTE_DEPOSIT_EXPIRY_IMMANENT_SHORT = 'DEI';
    public static QUOTE_REACTIVATED_SHORT = 'QRA';
    public static QUOTE_CANCELLED_SHORT = 'QCAN';
    public static QUOTE_DELIVERY_ADDRESS_REQUIRED_SHORT = 'DAR';
    public static QUOTE_CLOSED_SHORT = 'QCL';
    public static QUOTE_CONVERSION_SHORT = 'QCONP';
    public static QUOTE_UNCONVERTED_SHORT = 'QUNCON';
    // Order status
    public static ORDER_BAD_DEBT_SHORT = 'OBD';
    public static ORDER_RECEIVED_SHORT = 'OR';
    public static ORDER_IN_PRODUCTION_SHORT = 'IP';
    public static ORDER_IN_TRANSIT_SHORT = 'IT';
    public static ORDER_DOMESTIC_STOCK_TRANSFER_SHORT = 'DST';
    public static ORDER_DELAYED_IN_TRANSIT_SHORT = 'DIT';
    public static ORDER_CLEARED_CUSTOMS_SHORT = 'CC';
    public static ORDER_PACKING_SHORT = 'AAW';
    public static ORDER_FINAL_PAYMENT_PENDING_SHORT = 'FPP';
    public static ORDER_FINAL_PAYMENT_PAID_SHORT = 'FPPD';
    public static ORDER_FULL_PAYMENT_PAID_SHORT = 'FUPP';
    public static ORDER_GOODS_DISPATCHED_UNSPECIFIED_SHORT = 'GDUNS';
    public static ORDER_DELIVERY_UNCONFIRMED = 'DUNCONF';
    public static ORDER_DELIVERY_OUT_TO_TENDER_SHORT = 'DOTT';
    public static ORDER_DELIVERY_DISPATCHED_SHORT = 'DD';
    public static ORDER_GOODS_DISPATCHED_ACCEPTED_SHORT = 'GDA';
    public static ORDER_GOODS_DISPATCHED_DECLINED_SHORT = 'GDD';
    public static ORDER_DELIVERY_CLIENT_ORGANIZED_SHORT = 'DCO';
    public static ORDER_DELIVERY_CONFIRMED_SHORT = 'DCONF';
    public static ORDER_DELIVERD_SHORT = 'DEL';
    public static ORDER_COMPLETED_SHORT = 'C';
    public static ORDER_CANCELLED_SHORT = 'OCAN';
    public static ORDER_FOLLOW_UP_SHORT = 'AF';
    public static ORDER_NOMINATE_DELIVERY_DATE = 'ONDD';
    public static ORDER_FINAL_PAYMENT_PROCESSING = 'FPPROC';
    public static ORDER_DELIVERY_SEQUENCE_SHORT = 'DSEQ';
    public static ORDER_SPLIT_DELIVERY_SHORT = 'SD';
    public static ORDER_SPLIT_PRODUCTION_SHORT = 'SP';
    public static ORDER_PAYMENT_PROCESSING = 'PPROC';
    public static ORDER_ARCHIVED_SHORT = 'ARCH';

    public static ORDER_CANCELLED_ID = 39;
    public static ORDER_DELAYED_IN_TRANSIT_ID = 53;
    public static ORDER_AVAILABLE_AT_WAREHOUSE_ID = 29;
    public static ORDER_PAYMENT_PROCESSING_ID = 41;

    public static ORDER_SPLIT_DELIVERY_ID = 30;
    public static ORDER_DELIVERY_UNCONFIRMED_ID = 31;
    public static ORDER_DELIVERY_OUT_TO_TENDER_ID = 32;
    public static ORDER_DELIVERY_CONFIRMED_ID = 33;
    public static ORDER_DELIVERY_CLIENT_ORGANIZED_ID = 34;
    public static ORDER_DELIVERY_DISPATCHED_ID = 35;
    public static ORDER_DELIVERD_ID = 36;

    public static ORDER_AWAITING_FEEDBACK_ID = 37;
    public static ORDER_COMPLETED_ID = 38;
    public static ORDER_ORDER_CANCELLED_ID = 39;
    public static ORDER_ARCHIVED_ID = 42;

    public static PAYMENT_COMPLETE_SHORT = 'C';

    // Sample status
    public static SAMPLE_COMPLETE_SHORT = 'C';
    public static SAMPLE_FOLLOW_UP_SHORT = 'FU';
    public static SAMPLE_CANCELLED_SHORT = 'SCAN';

    // Carrier status id
    public static CARRIER_STATUS_UNSPECIFIED_ID = 1;
    public static CARRIER_STATUS_OUT_TO_TENDER_ID = 2;
    public static CARRIER_STATUS_DESPATCHED_ID = 3;
    public static CARRIER_STATUS_ACCEPTED_ID = 4;
    public static CARRIER_STATUS_DECLINED_ID = 5;
    public static CARRIER_STATUS_CLIENT_ORGANIZED_ID = 6;
    public static CARRIER_STATUS_DELIVERD_ID = 7;

    // Sample Order status
    public static SAMPLE_ORDER_REQUESTED_SHORT = 'SR';
    /* Status row IDs, for reading an activity row's own `old_value`/`new_value`
       snapshot — those record `status_id`, not `short_name`. 53 is pinned by the
       GC-353 migration, as the application references it by id. */
    public static SAMPLE_ORDER_REQUESTED_ID = 16;
    public static SAMPLE_ORDER_DELAYED_ID = 53;
    /* Failed to Reach Destination (`status` id 59), for reading an activity row's
       old_value — the undo is a move to Samples Despatched told apart by it. */
    public static SAMPLE_ORDER_FAILED_ID = 59;
    public static SAMPLE_ORDER_DELIVERED_ID = 19;
    public static SAMPLE_ORDER_FOLLOW_UP_ID = 20;
    /* GC-353 — console-v2's own confirmation copy for the delay flow. */
    public static SAMPLE_DELAY_MARKED = 'Marked Samples Delayed.';
    public static SAMPLE_DELAY_DATE_UPDATED =
        'Expected ready date updated - the follow-up task moved with it.';
    public static SAMPLE_DELAY_CLEARED =
        'The delay was cleared - the request is back at Samples Requested and the follow-up task is closed.';
    /* GC-353 Split Delivery — the delay follows the unpacked balance.
       Shown only when the request being split was delayed, because the rep now has two
       records and needs to know which one is promising the customer a date. The
       expected-ready date is interpolated by the caller, since the whole point of the
       message is that the promise did not change, only which record carries it. */
    public static SAMPLE_SPLIT_DELAY_MOVED =
        'Samples Delayed moved to the follow-on - every remaining quantity here is packed. '
        + 'The expected-ready date and the follow-up task went with it.';
    public static SAMPLE_ORDER_PACKING_SHORT = 'SPKG';
    public static SAMPLE_ORDER_DISPATCHED_SHORT = 'SDESP';
    public static SAMPLE_ORDER_DELIVERED_SHORT = 'SDEL';
    public static SAMPLE_ORDER_FOLLOW_UP_SHORT = 'SAF';
    public static SAMPLE_ORDER_COMPLETED_SHORT = 'SC';
    public static SAMPLE_ORDER_CANCELLED_SHORT = 'SCAN';
    /* The exception status between Samples Requested and Samples Packaged
       (`status` id 53, order_index 2, used_for 'SO'). Applied BY HAND — a split
       or a shortfall never sets it, because neither always means a delay. */
    public static SAMPLE_ORDER_DELAYED_SHORT = 'SDLY';
    /* The samples left the warehouse and never arrived (`status` id 59). Final:
       applied by hand from Despatched — or from Delivered / Awaiting Feedback
       confirmed in error, which takes those steps off — and undone back to
       Despatched. Never counted as completed or as a conversion. */
    public static SAMPLE_ORDER_FAILED_SHORT = 'SFAIL';
    public static SAMPLE_FAILED_REASONS = [
        'Lost in transit',
        'Returned to sender',
        'Damaged in transit',
        'Refused by the recipient',
        'Address incorrect or incomplete',
        'Other',
    ];
    public static SAMPLE_FAILED_REASON_OTHER = 'Other';
    /* The failed flow's toasts: plain sentences, like every other module's toast (user, Oct — a
       title + detail card was not wanted). No quote marks around the reasons (user, Oct). Marking
       names the reason; the undo does not (user's wording, 31 Oct). #REASON# / #FROM# / #TO# are
       reasons as listed in SAMPLE_FAILED_REASONS; *_NO_REASON covers a request that carries none. */
    public static SAMPLE_FAILED_MARKED =
        'Marked as Failed to Reach Destination (#REASON#). This won\'t count as a completed or converted request.';
    public static SAMPLE_FAILED_MARKED_NO_REASON =
        'Marked as Failed to Reach Destination. This won\'t count as a completed or converted request.';
    public static SAMPLE_FAILED_UNDONE =
        'Failed to Reach Destination status removed. The sample request is now back at Samples Despatched.';
    public static SAMPLE_FAILED_REASON_CHANGED = 'Reason updated from #FROM# to #TO#';
    public static SAMPLE_FAILED_REASON_SET = 'Reason set to #TO#';
    public static SAMPLE_FAILED_AND_NOTE_UPDATED = ', and the note has been updated.';
    public static SAMPLE_FAILED_AND_NOTE_REMOVED = ', and the note has been removed.';
    public static SAMPLE_FAILED_NOTE_UPDATED = 'The Failed to Reach Destination note has been updated.';
    public static SAMPLE_FAILED_NOTE_REMOVED = 'The Failed to Reach Destination note has been removed.';
    /* "Create new sample request": the clone of a failed request is sent, so the failed one is
       archived (it stays linked to the new one). #ORDER# is the failed request's number. */
    public static SAMPLE_FAILED_REPLACED_ARCHIVED = '#ORDER# has been archived and linked to the new sample request.';
    public static SAMPLE_FAILED_REPLACED_ARCHIVE_FAILED =
        'The new sample request was created, but #ORDER# could not be archived. Archive it from its own page.';
    public static SAMPLE_FAILED_BLOCKED =
        'Failed to reach its destination - the request is closed and is not counted as completed or converted.';
    public static SAMPLE_ORDER_CONVERSION_PENDING_SHORT = 'SCONP';
    public static SAMPLE_ORDER_CONVERTED_SHORT = 'SCON';
    public static SAMPLE_ORDER_UNCONVERTED_SHORT = 'SUNCON';

    public static QUOTE_WAREHOUSE_DATE_UPDATED = 'Warehouse date set.';
    public static ORDER_STOCK_REQUIRED_DATE_SET = 'Stock required date set.';
    public static ORDER_STOCK_REQUIRED_DATE_UPDATED = 'Stock required date updated.';
    public static ORDER_NOMINATED_DELIVERY_DATE_UPDATED = 'Nominated delivery date set.';
    public static ORDER_CARRIER_STATUS_UPDTAED = 'Carrier status has been updated successfully.';
    public static QUOTE_STATUS_CHANGE_SUCCESS = 'Quote status updated.';
    public static ORDER_STATUS_CHANGE_SUCCESS = 'Order status updated.';
    public static PAYMENT_STATUS_CHANGE_SUCCESS = 'Payment status updated.';
    public static SAMPLE_STATUS_CHANGE_SUCCESS = 'Sample status updated.';
    public static SAMPLE_REQUEST_STATUS_CHANGE_SUCCESS = 'Sample request status updated.';
    public static ORDER_WAREHOUSE_STATUS_UPDTAED = 'Warehouse status has been updated successfully.';
    public static ORDER_PICKUP_DATE_UPDATED = 'Pickup date updated.';

    public static LINK_QUOTES_ORDERS_SUCCESS_MESSAGE = 'Quotes, orders, and sample requests linked.';
    public static ANITICIPATED_WAREHOUSE_DATE_UPDATED_SUCCESS = 'Anticipated warehouse arrival date updated.';
    public static ANITICIPATED_PRODUCTION_COMPLETION_DATE_UPDATED_SUCCESS = 'Anticipated production completion date updated.';
    public static GOODS_DISPATCHED_ORDER_ARRAY = [
        {
            // id :'28',
            name: 'Delivery Unconfirmed',
            color: '#D90008',
            status: ['DUNCONF'],
            label_class: 'goods-despatch-unspecified-flag',
            disable_class: 'goods-despatched-unspecified-disable',
            background: '#D90008'
        },
        {
            name: 'Delivery Out To Tender',
            color: '#FF7600',
            status: ['DOTT'],
            label_class: 'goods-despatch-out-to-tender-flag',
            // disable_class: 'goods-despatched-out-to-tender-disable',
            disable_class: '',
            background: '#FF7600'
        },
        {
            name: 'Delivery Confirmed',
            color: '#499D63',
            status: ['DCONF'],
            label_class: 'goods-despatch-accepted-flag',
            disable_class: 'goods-despatched-accepted-disable',
            background: '#499D63'
        },
        {
            // id: '15',
            name: 'Delivery Despatched',
            color: '#499D63',
            status: ['DD'],
            label_class: 'goods-despatched-flag',
            disable_class: 'goods-despatched-confirmed-disable',
            background: '#499D63'
        },
        {
            // id :'37',
            name: 'Delivery Client Organized',
            color: '#499D63',
            status: ['DCO'],
            class: 'goods-despatched-client-organized-flag',
            label_class: 'goods-despatched-client-organized-flag',
            email_template_key: [],
            disable_class: 'goods-despatched-client-organized-disable',
            background: '#499D63'
        },
        {
            // id :'40',
            name: 'Delivered',
            color: '#dfbe74',
            status: ['DEL'],
            label_class: 'goods-despatch-delivered-flag',
            disable_class: 'goods-despatched-delivered-disable',
            background: '#dfbe74'
        },
    ];

    public static FINAL_PAYMENT_ORDER_ARRAY = [
        {
            name: 'Final Payment Outstanding',
            color: '#D90008',
            status: ['FPP'],
            label_class: 'final-payment-flag',
            disable_class: 'final-payment-pending-disable',
            background: '#D90008'
        },
        {
            name: 'Final Payment Paid',
            class: 'received',
            status: ['FPPD', 'FUPP'],
            label_class: 'final-payment-paid-flag',
            disable_class: 'final-payment-paid-disable',
            background: '#499D63',
            color: '#499D63'
        },
    ];

    public static ORDER_STATUS_ARRAY = [
        {
            name: 'Order Received',
            class: 'received',
            status: ['OR'],
            label_class: 'recived-flag',
            email_template_key: ['order-received', 'deposit-paid'],
            disable_class: 'order-received-disable',
            color: '#9a5fa8',
            background: '#9a5fa8'
        },
        {
            name: 'In Production',
            color: '#4EBAB6',
            status: ['IP'],
            label_class: 'production-flag',
            disable_class: 'in-production-disable',
            background: '#4EBAB6'
        },
        {
            name: 'In Transit',
            class: 'transit',
            status: ['IT'],
            label_class: 'transit-flag',
            email_template_key: ['in-transit'],
            disable_class: 'in-transit-disable',
            color: '#B98000',
            background: '#B98000'
        },
        {
            name: 'Customs Held',
            color: '#ff7600',
            status: ['CC'],
            label_class: 'cleared-flag',
            disable_class: 'custom-held-disable',
            background: '#ff7600'
        },
        {
            name: 'Available At Warehouse',
            class: 'packing',
            status: ['AAW', 'ONDD'],
            label_class: 'packaging-flag',
            email_template_key: ['stock-landed', 'nominate-delivery-date'],
            disable_class: 'available-at-warehouse-disable',
            // disable_class: '',
            color: '#6d709a',
            background: '#6d709a'
        },
        {
            name: 'Final Payment Outstanding',
            color: '#D90008',
            status: ['FPP'],
            label_class: 'final-payment-flag',
            email_template_key: ['final-payment-reminder'],
            disable_class: 'final-payment-pending-disable',
            background: '#D90008'
        },
        {
            name: 'Final Payment Paid',
            class: 'received',
            status: ['FPPD', 'FUPP'],
            label_class: 'final-payment-paid-flag',
            email_template_key: [], // 'nominate-delivery-date'
            disable_class: 'final-payment-paid-disable',
            background: '#499D63',
            color: '#499D63'
        },
        {
            name: 'Goods Despatch Unassigned',
            color: '#D90008',
            status: ['GDUNS'],
            class: 'goods-despatch-unspecified-flag',
            label_class: 'goods-despatch-unspecified-flag',
            email_template_key: [],
            disable_class: 'goods-despatched-unspecified-disable',
            background: '#D90008'
        },
        {
            name: 'Goods Despatch Out To Tender',
            color: '#FF7600',
            status: ['DOTT'],
            class: 'goods-despatch-out-to-tender-flag',
            label_class: 'goods-despatch-out-to-tender-flag',
            email_template_key: ['trade-user-carrier-added'],
            disable_class: '',
            //disable_class: 'goods-despatched-out-to-tender-disable',
            background: '#FF7600'
        },
        {
            name: 'Goods Despatch Accepted',
            color: '#499D63',
            status: ['GDA'],
            label_class: 'goods-despatch-accepted-flag',
            disable_class: 'goods-despatched-accepted-disable',
            background: '#499D63'
        },
        {
            name: 'Goods Despatch Declined',
            color: '#D90008',
            status: ['GDD'],
            label_class: 'goods-despatch-declined-flag',
            disable_class: 'goods-despatched-declined-disable',
            background: '#D90008'
        },
        {
            name: 'Goods Despatch Despatched',
            color: '#499D63',
            status: ['DD'],
            class: 'goods-despatched-flag',
            label_class: 'goods-despatched-flag',
            email_template_key: ['order-dispatched'],
            disable_class: 'goods-despatched-confirmed-disable',
            background: '#499D63'
        },
        {
            name: 'Goods Despatch Delivered',
            color: '#499D63',
            status: ['DEL'],
            class: 'goods-despatch-delivered-flag',
            label_class: 'goods-despatch-delivered-flag',
            disable_class: 'goods-despatch-delivered-disable',
            background: '#499D63'
        },
        {
            name: 'Goods Despatch Client Organized',
            color: '#499D63',
            status: ['DCO'],
            class: 'goods-despatched-client-organized-flag',
            label_class: 'goods-despatched-client-organized-flag',
            email_template_key: [],
            disable_class: 'goods-despatched-client-organized-disable',
            background: '#499D63'
        },
        {
            name: 'Delayed In Transit',
            color: '#499D63',
            status: ['DIT'],
            class: 'goods-despatched-client-organized-flag',
            label_class: 'goods-despatched-client-organized-flag',
            email_template_key: [],
            disable_class: 'goods-despatched-client-organized-disable',
            background: '#499D63'
        },
        // {
        //     name: 'Goods Despatched',
        //     color: '#B4BE3C',
        //     status: ['GD'],
        //     label_class: 'dispatched-flag',
        //     email_template_key: ['order-dispatched'],
        //     disable_class: 'goods-despatched-disable'
        // },
        {
            name: 'Follow Up',
            color: '#d51ed2',
            class: 'followup',
            status: ['FU'],
            label_class: 'followup-flag',
            email_template_key: [],
            disable_class: 'follow-up-disable',
            background: '#d51ed2'
        },
        {
            name: 'Complete',
            class: 'complete',
            status: ['C'],
            label_class: 'completed-flag',
            email_template_key: ['order-complete'],
            disable_class: 'complete-disable',
            color: '#222324',
            background: '#222324'
        },
        {
            name: 'Archived',
            class: '',
            status: ['QARCH'],
            label_class: '',
            email_template_key: [],
            color: '',
            background: ''
        },
        // {
        //     name: 'Bad Debt',
        //     class: '',
        //     status: ['OBD'],
        //     label_class: '',
        //     email_template_key: [],
        //     color: '',
        //     background: ''
        // },
    ];

    public static QUOTE_STATUS_IN_ZONE_ARRAY = [
        // {name: 'Quote', class: 'quote', status: ['Q'], label_class: 'quote-flag', disable_class: 'quote-disable'},
        // {
        //     name: 'Follow Up',
        //     color: '#6D709A',
        //     status: ['FU'],
        //     label_class: 'followup-flag',
        //     disable_class: 'follow-up-disable'
        // },
        // {
        //     name: 'Deposit Payment Pending',
        //     color: '#3D95ED',
        //     status: ['DP'],
        //     label_class: 'pending-flag',
        //     disable_class: 'deposit-pending-disable'
        // },
        // {
        //     name: 'Expired',
        //     color: '#D1102E',
        //     status: ['EXP'],
        //     label_class: 'expired-flag',
        //     disable_class: 'expired-disable'
        // },
        {
            name: 'Quote Created',
            class: 'quote',
            status: ['QC'],
            label_class: 'quote-flag',
            disable_class: 'quote-disable',
            color: '#9A5FA8',
            background: '#9A5FA8'
        },
        {
            name: 'Auto Delivery Quote Applied',
            color: '#499D63',
            class: 'auto-delivery-quote-applied',
            status: ['ADQAP'],
            label_class: 'applied-flag',
            disable_class: 'applied-disable',
            background: '#499D63'
        },
        {
            name: 'Follow Up',
            color: '#d51ed2',
            class: 'followup',
            status: ['FU'],
            label_class: 'followup-flag',
            disable_class: 'follow-up-disable',
            background: '#d51ed2'
        },
        {
            name: 'Quote Accepted',
            color: '#6d709a',
            class: 'quote-accepted',
            status: ['QA'],
            label_class: 'quote-accepted-flag',
            disable_class: 'quote-accepted-disable',
            background: '#6d709a'
        },
        {
            name: 'Deposit Payment Due',
            // color: '#3D95ED',
            class: 'deposit-payment-due',
            status: ['DPDU'],
            label_class: 'pending-flag',
            disable_class: 'deposit-pending-disable',
            background: '#d90008',
            color: '#d90008'
        },
        // {
        //     name: 'Order Created',
        //     color: '#7A8591',
        //     class: 'order-created',
        //     status: ['OC'],
        //     label_class: 'order-created-flag',
        //     disable_class: 'order-created-disable',
        //     background: '#7A8591'
        // },
        {
            name: 'Expired',
            class: 'expired',
            color: '#D42A3B',
            status: ['EXP'],
            label_class: 'expired-flag',
            disable_class: 'expired-disable',
            background: '#D42A3B'
        }
    ];

    public static MANUAL_DELIVERY_QUOTE_ARRAY = [
        {
            name: 'Manual Delivery Quote Required',
            color: '#D42A3B',
            status: ['MDQR'],
            label_class: 'pending-flag',
            disable_class: 'pending-disable',
            background: '#D42A3B'
        },
        {
            name: 'Delivery Address Required',
            color: '#D42A3B',
            status: ['DAR'],
            label_class: 'delivery-address-required-flag',
            disable_class: 'delivery-address-required-disable',
            background: '#D42A3B'
        },
        {
            name: 'Manual Delivery Quote Added',
            color: '#499D63',
            status: ['MDQAD'],
            label_class: 'added-flag',
            disable_class: 'added-disable',
            background: '#499D63'
        },
        {
            name: 'Manual Delivery Quote Applied',
            color: '#499D63',
            status: ['MDQAP'],
            label_class: 'applied-flag',
            disable_class: 'applied-disable',
            background: '#499D63'
        }
    ];

    public static QUOTE_STATUS_OUT_ZONE_ARRAY = [
        {
            name: 'Quote Created',
            class: 'quote',
            status: ['QC'],
            label_class: 'quote-flag',
            disable_class: 'quote-disable',
            color: '#9A5FA8',
            background: '#9A5FA8'
        },
        {
            name: 'Delivery Address Required',
            class: 'delivery-address-required',
            status: ['DAR'],
            label_class: 'delivery-address-required-flag',
            disable_class: 'delivery-address-required-disable',
            color: '#D42A3B',
            background: '#D42A3B'
        },
        {
            name: 'Manual Delivery Quote Required',
            class: 'manual-delivery-quote-required',
            status: ['MDQR'],
            label_class: 'manual-delivery-quote-pending-flag',
            disable_class: 'pending-disable',
            background: '#D42A3B',
            color: '#D42A3B'
        },
        {
            name: 'Manual Delivery Quote Added',
            class: 'manual-delivery-quote-added',
            status: ['MDQAD'],
            label_class: 'added-flag',
            disable_class: 'added-disable',
            background: '#499D63',
            color: '#499D63'
        },
        {
            name: 'Manual Delivery Quote Applied',
            color: '#499D63',
            class: 'manual-delivery-quote-applied',
            status: ['MDQAP'],
            label_class: 'applied-flag',
            disable_class: 'applied-disable',
            background: '#499D63'
        },
        {
            name: 'Follow Up',
            color: '#d51ed2',
            class: 'followup',
            status: ['FU'],
            label_class: 'followup-flag',
            disable_class: 'follow-up-disable',
            background: '#d51ed2'
        },
        {
            name: 'Quote Accepted',
            // color: '#6D709A',
            class: 'quote-accepted',
            status: ['QA'],
            label_class: 'quote-accepted-flag',
            disable_class: 'quote-accepted-disable',
            color: '#6d709a',
            background: '#6d709a'
        },
        {
            name: 'Deposit Payment Due',
            // color: '#3D95ED',
            class: 'deposit-payment-due',
            status: ['DPDU'],
            label_class: 'pending-flag',
            disable_class: 'deposit-pending-disable',
            background: '#3D95ED',
            color: '#3D95ED',
        },
        // {
        //     name: 'Order Created',
        //     color: '#7A8591',
        //     class: 'order-created',
        //     status: ['OC'],
        //     label_class: 'order-created-flag',
        //     disable_class: 'order-created-disable',
        //     background: '#7A8591'
        // },
        {
            name: 'Expired',
            class: 'expired',
            color: '#D42A3B',
            status: ['EXP'],
            label_class: 'expired-flag',
            disable_class: 'expired-disable',
            background: '#D42A3B'
        }
    ];

    public static QUOTE_STATUS_ARRAY = [
        // {name: 'Quote', class: 'quote', status: ['Q'], label_class: 'quote-flag'},
        // {name: 'Shipping Quote Required', color: '#D1102E', status: ['SR'], label_class: 'pending-flag'},
        // {name: 'Shipping Pending Approval', color: '#D1102E', status: ['SPA'], label_class: 'pending-flag'},
        // {name: 'Follow Up', color: '#6D709A', status: ['FU'], label_class: 'follow-up-flag'},
        // {
        //     name: 'Deposit Payment Pending',
        //     class: '#3D95ED',
        //     status: ['DP'],
        //     label_class: 'deposit-payment-pending-flag'
        // },
        // {name: 'Expired', color: '#D1102E', class: '#3D95ED', status: ['EXP'], label_class: 'pending-flag'},
        {
            name: 'Quote Created', class: 'quote', status: ['QC'], label_class: 'quote-flag', color: '#9A5FA8',
            background: '#9A5FA8'
        },
        {
            name: 'Draft', class: 'quote-draft', status: ['DFT'], label_class: 'quote-draft-flag', color: '#9A5FA8',
            background: '#229F9F'
        },
        {
            name: 'Manual Delivery Quote Required',
            status: ['MDQR'],
            label_class: 'pending-flag',
            background: '#D42A3B',
            color: '#D42A3B'
        },
        {
            name: 'Manual Delivery Quote Added', status: ['MDQAD'], label_class: 'added-flag', background: '#499D63',
            color: '#499D63'
        },
        {
            name: 'Manual Delivery Quote Applied',
            status: ['MDQAP'],
            label_class: 'applied-flag',
            background: '#499D63',
            color: '#499D63'
        },
        {
            name: 'Auto Delivery Quote Applied',
            status: ['ADQAP'],
            label_class: 'applied-flag',
            background: '#499D63',
            color: '#499D63'
        },
        { name: 'Follow Up', status: ['FU'], label_class: 'follow-up-flag', background: '#d51ed2', color: '#d51ed2' },
        {
            name: 'Quote Accepted',
            color: '#6d709a',
            status: ['QA'],
            label_class: 'quote-accepted-flag',
            background: '#6d709a'
        },
        {
            name: 'Deposit Payment Due',
            color: '#d90008',
            status: ['DPDU'],
            label_class: 'deposit-payment-pending-flag',
            background: '#d90008'
        },
        // {
        //     name: 'Order Created',
        //     color: '#7A8591',
        //     status: ['OC'],
        //     label_class: 'order-created-flag',
        //     background: '#7A8591'
        // },
        {
            name: 'Expired',
            color: '#D42A3B',
            class: '#3D95ED',
            status: ['EXP'],
            label_class: 'pending-flag',
            background: '#D42A3B'
        },
        { name: 'Archived', color: '#919192', status: ['QARCH'], label_class: 'archive-flag', background: 'none' }
    ];

    public static QUOTE_ORDER_STATUS_ARRAY = [
        {
            name: 'Quote Created',
            class: 'quote',
            status: ['QC'],
            label_class: 'quote-flag',
            color: '#9A5FA8',
            background: '#9A5FA8'
        },
        {
            name: 'Draft', class: 'quote-draft', status: ['DFT'], label_class: 'quote-draft-flag', color: '#9A5FA8',
            background: '#229F9F'
        },
        {
            name: 'Manual Delivery Quote Required',
            color: '#499D63',
            status: ['MDQR'],
            label_class: 'manual-delivery-quote-required-flag',
            email_template_key: ['shipping-quote-required'],
            background: '499D63'
        },
        {
            name: 'Manual Delivery Quote Added',
            color: '#499D63',
            status: ['MDQAD'],
            label_class: 'manual-delivery-quote-added-flag',
            background: '#499D63'
        },
        {
            name: 'Manual Delivery Quote Applied',
            color: '#499D63',
            status: ['MDQAP'],
            label_class: 'manual-delivery-quote-applied-flag',
            background: '#499D63'
        },
        {
            name: 'Auto Delivery Quote Applied',
            color: '#499D63',
            status: ['ADQAP'],
            label_class: 'auto-delivery-quote-applied-flag',
            background: '#499D63'
        },
        {
            name: 'Follow Up',
            color: '#d51ed2',
            status: ['FU'],
            label_class: 'follow-up-flag',
            background: '#d51ed2'
        },
        {
            name: 'Quote Accepted',
            color: '#6d709a',
            status: ['QA'],
            label_class: 'quote-accepted-flag',
            background: '#6d709a'
        },
        {
            name: 'Deposit Payment Due',
            color: '#d90008',
            class: '#d90008',
            status: ['DPDU'],
            label_class: 'pending-flag',
            background: '#d90008'
        },
        // {
        //     name: 'Order Created',
        //     color: '#7A8591',
        //     class: '#3D95ED',
        //     status: ['OC'],
        //     label_class: 'order-created-flag',
        //     background: '#7A8591'
        // },
        {
            name: 'Expired',
            color: '#D1102E',
            class: '#3D95ED',
            status: ['EXP'],
            label_class: 'pending-flag',
            background: '#D42A3B'
        },
        {
            name: 'Order Received',
            class: 'received',
            status: ['OR'],
            label_class: 'recived-flag',
            email_template_key: ['order-received', 'deposit-paid'],
            color: '#499D63',
            background: '#499D63'
        },
        {
            name: 'In Production',
            color: '#4EBAB6',
            status: ['IP'],
            label_class: 'production-flag',
            background: '#4EBAB6'
        },
        {
            name: 'In Transit',
            class: 'transit',
            status: ['IT'],
            label_class: 'transit-flag',
            email_template_key: ['in-transit'],
            color: '#B98000',
            background: '#B98000'
        },
        {
            name: 'Customs Held',
            color: '#ff7600',
            status: ['CC'],
            label_class: 'cleared-flag',
            background: '#ff7600'
        },
        {
            name: 'Arrived At Warehouse',
            class: 'packing',
            status: ['AAW'],
            label_class: 'packaging-flag',
            email_template_key: ['stock-landed'],
            color: '#4ebab6',
            background: '#4ebab6'
        },
        {
            name: 'Final Payment Outstanding',
            color: '#D90008',
            status: ['FPP'],
            label_class: 'final-payment-flag',
            email_template_key: ['final-payment-reminder'],
            background: '#D90008'
        },
        {
            name: 'Final Payment Paid',
            class: 'received',
            status: ['FPPD', 'FUPP'],
            label_class: 'final-payment-paid-flag',
            email_template_key: ['nominate-delivery-date'],
            color: '#499D63',
            background: '#499D63'
        },
        {
            name: 'Goods Despatch Unassigned',
            color: '#D90008',
            status: ['GDUNS'],
            class: 'goods-despatch-unspecified-flag',
            label_class: 'goods-despatch-unspecified-flag',
            email_template_key: [],
            background: '#D90008'
        },
        {
            name: 'Goods Despatch Out To Tender',
            color: '#FF7600',
            status: ['DOTT'],
            class: 'goods-despatch-out-to-tender-flag',
            label_class: 'goods-despatch-out-to-tender-flag',
            email_template_key: [],
            background: '#FF7600'
        },
        {
            name: 'Goods Despatch Accepted',
            color: '#FF7600',
            status: ['GDA'],
            label_class: 'goods-despatch-accepted-flag',
            disable_class: 'goods-despatched-accepted-disable',
            background: '#FF7600'
        },
        {
            name: 'Goods Despatch Declined',
            color: '#FF7600',
            status: ['GDD'],
            label_class: 'goods-despatch-declined-flag',
            disable_class: 'goods-despatched-declined-disable',
            background: '#FF7600'
        },
        {
            name: 'Goods Despatch Despatched',
            color: '#499D63',
            status: ['DD'],
            class: 'goods-despatched-flag',
            label_class: 'goods-despatched-flag',
            email_template_key: ['order-dispatched'],
            background: '#499D63'
        },
        {
            name: 'Goods Despatch Client Organized',
            color: '#499D63',
            status: ['DCO'],
            class: 'goods-despatched-client-organized-flag',
            label_class: 'goods-despatched-client-organized-flag',
            email_template_key: [],
            disable_class: 'goods-despatched-client-organized-disable',
            background: '#499D63'
        },
        // {
        //     name: 'Goods Dispatched',
        //     color: '#B4BE3C',
        //     status: ['GD'],
        //     label_class: 'dispatched-flag',
        //     email_template_key: ['order-dispatched']
        // },
        {
            name: 'Complete',
            class: 'complete',
            status: ['C'],
            label_class: 'completed-flag',
            email_template_key: ['order-complete'],
            color: '#222324',
            background: '#222324'
        },
        {
            name: 'Follow Up',
            color: '#d51ed2',
            class: 'followup',
            status: ['FU'],
            label_class: 'followup-flag',
            email_template_key: [],
            background: '#d51ed2'
        }
    ];

    public static SAMPLE_QUOTE_ORDER_STATUS_ARRAY = [
        {
            name: 'Quote Created',
            class: 'quote',
            status: ['QC'],
            label_class: 'quote-flag',
            type: 'Q',
            color: '#9A5FA8',
            background: '#9A5FA8'
        },
        {
            name: 'Draft', class: 'quote-draft', status: ['DFT'], label_class: 'quote-draft-flag', color: '#9A5FA8',
            background: '#229F9F'
        },
        {
            name: 'Manual Delivery Quote Required',
            color: '#499D63',
            status: ['MDQR'],
            label_class: 'manual-delivery-quote-required-flag',
            email_template_key: ['shipping-quote-required'],
            type: 'Q',
            background: '#499D63'
        },
        {
            name: 'Manual Delivery Quote Added',
            color: '#499D63',
            status: ['MDQAD'],
            label_class: 'manual-delivery-quote-added-flag',
            type: 'Q',
            background: '#499D63'
        },
        {
            name: 'Manual Delivery Quote Applied',
            color: '#499D63',
            status: ['MDQAP'],
            label_class: 'manual-delivery-quote-applied-flag',
            type: 'Q',
            background: '#499D63'
        },
        {
            name: 'Auto Delivery Quote Applied',
            color: '#499D63',
            status: ['ADQAP'],
            label_class: 'auto-delivery-quote-applied-flag',
            type: 'Q',
            background: '#499D63'
        },
        {
            name: 'Awaiting Feedback',
            color: '#d51ed2',
            status: ['FU'],
            label_class: 'follow-up-flag',
            type: 'Q',
            background: '#d51ed2'
        },
        {
            name: 'Delayed In Transit',
            color: '#d51ed2',
            status: ['DIT'],
            label_class: 'follow-up-flag',
            type: 'Q',
            background: '#d51ed2'
        },
        {
            name: 'Quote Accepted',
            color: '#6d709a',
            status: ['QA'],
            label_class: 'quote-accepted-flag',
            type: 'Q',
            background: '#6d709a'
        },
        {
            name: 'Deposit Payment Due',
            color: '#d90008',
            class: '#d90008',
            status: ['DPDU'],
            label_class: 'pending-flag',
            type: 'Q',
            background: '#d90008'
        },
        {
            name: 'Quote Expiry Immanent',
            color: '#d90008',
            class: '#d90008',
            status: ['QEI'],
            label_class: 'pending-flag',
            type: 'Q',
            background: '#d90008'
        },

        {
            name: 'Deposit Expiry Immanent',
            color: '#d90008',
            class: '#d90008',
            status: ['DEI'],
            label_class: 'pending-flag',
            type: 'Q',
            background: '#d90008'
        },
        // {
        //     name: 'Order Created',
        //     color: '#7A8591',
        //     class: '#3D95ED',
        //     status: ['OC'],
        //     label_class: 'order-created-flag',
        //     type: 'Q',
        //     background: '#7A8591'
        // },
        {
            name: 'Expired',
            color: '#D42A3B',
            class: '#3D95ED',
            status: ['EXP'],
            label_class: 'expired-flag',
            type: 'Q',
            background: '#D42A3B'
        },
        {
            name: 'Order Received',
            class: 'received',
            status: ['OR'],
            label_class: 'recived-flag',
            email_template_key: ['order-received', 'deposit-paid'],
            type: 'O',
            color: '#9a5fa8',
            background: '#9a5fa8'
        },
        {
            name: 'In Production',
            color: '#4EBAB6',
            status: ['IP'],
            label_class: 'production-flag',
            type: 'O',
            background: '#4EBAB6'
        },
        {
            name: 'In Transit',
            class: 'transit',
            status: ['IT'],
            label_class: 'transit-flag',
            email_template_key: ['in-transit'],
            type: 'O',
            color: '#B98000',
            background: '#B98000'
        },
        {
            name: 'Customs Held',
            color: '#ff7600',
            status: ['CC'],
            label_class: 'cleared-flag',
            type: 'O',
            background: '#ff7600'
        },
        {
            name: 'Arrived At Warehouse',
            class: 'packing',
            status: ['AAW'],
            label_class: 'packaging-flag',
            email_template_key: ['stock-landed'],
            type: 'O',
            color: '#4ebab6',
            background: '#4ebab6'
        },
        {
            name: 'Final Payment Outstanding',
            color: '#D90008',
            status: ['FPP'],
            label_class: 'final-payment-flag',
            email_template_key: ['final-payment-reminder'],
            type: 'O',
            background: '#D90008'
        },
        {
            name: 'Final Payment Paid',
            class: 'received',
            status: ['FPPD', 'FUPP'],
            label_class: 'final-payment-paid-flag',
            email_template_key: ['nominate-delivery-date'],
            type: 'O',
            color: '#499D63',
            background: '#499D63'
        },
        {
            name: 'Goods Despatch Unassigned',
            color: '#D42A3B',
            status: ['GDUNS'],
            class: 'goods-despatch-unspecified-flag',
            label_class: 'goods-despatch-unspecified-flag',
            email_template_key: [],
            type: 'O',
            background: '#D90008'
        },
        {
            name: 'Goods Despatch Out To Tender',
            color: '#FF7600',
            status: ['DOTT'],
            class: 'goods-despatch-out-to-tender-flag',
            label_class: 'goods-despatch-out-to-tender-flag',
            email_template_key: [],
            type: 'O',
            background: '#FF7600'
        },
        {
            name: 'Goods Despatch Accepted',
            color: '#FF7600',
            status: ['GDA'],
            label_class: 'goods-despatch-accepted-flag',
            disable_class: 'goods-despatched-accepted-disable',
            background: '#FF7600'
        },
        {
            name: 'Goods Despatch Declined',
            color: '#FF7600',
            status: ['GDD'],
            label_class: 'goods-despatch-declined-flag',
            disable_class: 'goods-despatched-declined-disable',
            background: '#FF7600'
        },
        {
            name: 'Goods Despatch Despatched',
            color: '#499D63',
            status: ['DD'],
            class: 'goods-despatched-flag',
            label_class: 'goods-despatched-flag',
            email_template_key: ['order-dispatched'],
            type: 'O',
            background: '#499D63'
        },
        {
            name: 'Goods Despatch Client Organized',
            color: '#499D63',
            status: ['DCO'],
            class: 'goods-despatched-client-organized-flag',
            label_class: 'goods-despatched-client-organized-flag',
            email_template_key: [],
            type: 'O',
            background: '#499D63'
        },
        {
            name: 'Goods Delivered',
            color: '#dfbe74',
            status: ['DEL'],
            label_class: 'goods-despatch-delivered-flag',
            disable_class: 'goods-despatched-delivered-disable',
            background: '#dfbe74'
        },
        // {
        //     name: 'Goods Dispatched',
        //     color: '#B4BE3C',
        //     status: ['GD'],
        //     label_class: 'dispatched-flag',
        //     email_template_key: ['order-dispatched']
        // },
        {
            name: 'Complete',
            class: 'complete',
            status: ['C'],
            label_class: 'completed-flag',
            email_template_key: ['order-complete'],
            type: 'O',
            color: '#222324',
            background: '#222324'
        },
        {
            name: 'Follow Up',
            color: '#d51ed2',
            class: 'followup',
            status: ['FU'],
            label_class: 'followup-flag',
            email_template_key: [],
            type: 'O',
            background: '#d51ed2'
        },
        {
            name: 'Samples Requested',
            class: 'received-sample',
            status: ['OR'],
            label_class: 'recived-flag-sample',
            disable_class: 'sample-request-disable',
            type: 'SO',
            color: '#9a5fa8',
            background: '#9a5fa8'
        },
        {
            name: 'Packaged',
            class: 'packing',
            status: ['PC'],
            label_class: 'packaging-flag',
            disable_class: 'package-disable',
            type: 'SO',
            color: '#4ebab6',
            background: '#4ebab6'
        },
        {
            name: 'Despatched',
            class: 'transit',
            status: ['OIW'],
            label_class: 'transit-flag',
            email_template_key: ['sample-dispatched'],
            disable_class: 'despatched-disable',
            type: 'SO',
            color: '#362e9b',
            background: '#362e9b'
        },
        {
            name: 'Delivered',
            class: 'complete',
            status: ['D'],
            label_class: 'completed-flag',
            email_template_key: ['sample-order-complete'],
            disable_class: 'delivered-disable',
            type: 'SO',
            color: '#222324',
            background: '#222324'
        },
        {
            name: 'Follow Up', class: 'followup', status: ['FU'], label_class: 'followup-flag',
            disable_class: 'follow-up-disable',
            type: 'SO',
            color: '#d51ed2',
            background: '#d51ed2'
        },
        { name: 'Archived', color: '#919192', status: ['QARCH'], label_class: 'archive-flag', background: 'none' }
    ];

    public static wareHouseArrivalValid = ['OR', 'DP', 'IP', 'IT', 'CC'];
    public static stockRequiredValid = ['OR', 'DP', 'IP', 'IT', 'CC', 'AAW', 'DOTT'];
    public static editableValid = ['OR', 'DP', 'IP', 'IT', 'CC', 'AAW', 'DOTT', 'FPP', 'FPPD', 'FUPP', 'DD'];

    // public static SAMPLE_ORDER_STATUS = [
    //     {name: 'Order Received', class: 'received-sample', status: ['OR'], label_class: 'recived-flag'},
    //     {name: 'In Transit', class: 'transit', status: ['IT'], label_class: 'transit-flag'},
    //     {name: 'Complete', class: 'complete', status: ['C'], label_class: 'completed-flag'},
    //     {name: 'Packaged', class: 'packing', status: ['PC'], label_class: 'packaging-flag'}
    // ];
    //
    // public static GENERAL_USER_SAMPLE_ORDER_STATUS_ARRAY = [{
    //     name: 'Sample Order',
    //     class: 'quote',
    //     status: ['Q', 'A'],
    //     label_class: 'quote-flag'
    // },
    //     {name: 'Payment Pending', color: '#3D95ED', status: ['FPP'], label_class: 'recived-flag'},
    //     {name: 'Order Received', class: 'received', status: ['OR'], label_class: 'recived-flag'},
    //     {name: 'Arrived At Warehouse', class: 'packing', status: ['AAW'], label_class: 'packaging-flag'},
    //     {name: 'In Transit', class: 'transit', status: ['IT'], label_class: 'transit-flag'},
    //     {name: 'Complete', class: 'complete', status: ['C'], label_class: 'completed-flag'}];

    public static TRADE_SAMPLE_ORDER_STATUS_ARRAY = [
        {
            name: 'Samples Requested',
            class: 'received-sample',
            status: ['OR'],
            label_class: 'recived-flag-sample',
            disable_class: 'sample-request-disable',
            color: '#9a5fa8',
            background: '#9a5fa8'
        },
        {
            name: 'Packaged',
            class: 'packing',
            status: ['PC'],
            label_class: 'packaging-flag',
            disable_class: 'package-disable',
            color: '#4ebab6',
            background: '#4ebab6'
        },
        {
            name: 'Despatched',
            class: 'transit',
            status: ['OIW'],
            label_class: 'transit-flag',
            email_template_key: ['sample-dispatched'],
            disable_class: 'despatched-disable',
            color: '#362e9b',
            background: '#362e9b'
        },
        {
            name: 'Delivered',
            class: 'complete',
            status: ['D'],
            label_class: 'completed-flag',
            email_template_key: ['sample-order-complete'],
            disable_class: 'delivered-disable',
            color: '#dfbe74',
            background: '#dfbe74'
        },
        {
            name: 'Follow Up', class: 'followup', status: ['FU'], label_class: 'followup-flag',
            disable_class: 'follow-up-disable',
            color: '#d51ed2',
            background: '#d51ed2'
        },
        {
            name: 'Complete', class: 'complete', status: ['C'], label_class: 'complete-flag',
            disable_class: 'complete-disable',
            color: '#222324',
            background: '#222324'
        },
        { name: 'Archived', color: '#919192', status: ['QARCH'], label_class: 'archive-flag', background: 'none' }
    ];

    public static SAMPLE_STATUS_DISPATCHED = 'Despatched';
    public static SAMPLE_STATUS_DELIVERED = 'Delivered';
    public static SAMPLE_STATUS_FOLLOWUP = 'Follow Up';
    public static SAMPLE_STATUS_COMPLETE = 'Complete';

    public static ORDER_STATUS_DESPATCH_CONFIRMED = 'Goods Despatch Despatched';
    public static ORDER_STATUS_DESPATCHED = 'Goods Despatched';
    public static ORDER_STATUS_COMPLETE = 'Complete';
    public static ORDER_STATUS_FOLLOWUP = 'Follow Up';
    public static ORDER_STATUS_IN_PRODUCTION = 'In Production';
    public static ORDER_STATUS_ORDER_RECEIVED = 'Order Received';
    public static ORDER_STATUS_IN_TRANSIT = 'In Transit';
    public static ORDER_STATUS_DOMESTIC_STOCK_TRANSFER = 'Domestic Stock Transfer';
    public static ORDER_STATUS_CUSTOMS_HELD = 'Customs Held';
    public static ORDER_STATUS_AVAILABLE_AT_WAREHOUSE = 'Available At Warehouse';
    public static ORDER_STATUS_PACKAGED = 'Packaged';
    public static ORDER_STATUS_PAYMENT_PROCESSING = 'Payment Processing';

    public static QUOTE_STATUS_SHIPPING_REQUIRED = 'Quote Shipping Required';
    public static QUOTE_STATUS_FINAL_PAYMENT = 'Final Payment Pending';
    public static QUOTE_STATUS_PAID_PAYMENT = 'Final Payment Paid';
    public static QUOTE_STATUS_PAYMENT_PENDING = 'Payment Pending';
    public static QUOTE_STATUS_QUOTE = 'Quote';
    public static QUOTE_STATUS_ORDER_RECEIVED = 'Order Received';
    public static QUOTE_STATUS_PACKAGING = 'Arrived At Warehouse';

    public static BADGE_TITLE_FINAL_PAYMENT_OUTSTANDING = 'Final Payment Outstanding';
    public static BADGE_TITLE_BALANCE_REMAINING = 'Balance Remaining';

    public static IS_ARCHIVE_YES = true;
    public static IS_ARCHIVE_NO = false;

    public static IS_ARCHIVE_TRUE = true;
    public static IS_ARCHIVE_FALSE = false;

    public static IS_BAD_DEBT_YES = 'Y';
    public static IS_BAD_DEBT_NO = 'N';

    public static IS_ZERO_DEBT_YES = 'Y';
    public static IS_ZERO_DEBT_NO = 'N';

    public static IS_UL_PUSHED_NO = 'N';

    public static CONFIRM_MESSAGE_CHANGE_STATUS = 'Are you sure you want to change this order’s status to ';

    public static QUOTE_IS_ARCHIVE_YES_CONFIRMATION_MESSAGE = 'Would you like to archive this quote?';
    public static QUOTE_IS_ARCHIVE_NO_CONFIRMATION_MESSAGE = 'Would you like to unarchive this quote?'
    public static QUOTE_SUCCESS_MESSAGE_IS_ARCHIVE = 'Quote archived.';
    public static QUOTE_SUCCESS_MESSAGE_IS_ARCHIVE_NO = 'Quote unarchived.';
    public static QUOTE_CONFIRM_MESSAGE_CHANGE_STATUS = 'Are you sure you want to change this quote’s status to ';

    public static ORDER_IS_ARCHIVE_YES_CONFIRMATION_MESSAGE = 'Would you like to archive this order?';
    public static ORDER_IS_ARCHIVE_NO_CONFIRMATION_MESSAGE = 'Would you like to unarchive this order?'
    public static ORDER_SUCCESS_MESSAGE_IS_ARCHIVE = 'Order archived.';
    public static ORDER_SUCCESS_MESSAGE_IS_ARCHIVE_NO = 'Order unarchived.';
    public static ORDER_CONFIRM_MESSAGE_CHANGE_STATUS = ' Are you sure you want to change this order’s status to ';
    public static CARRIER_CONFIRM_MESSAGE_CHANGE_STATUS = 'Are you sure you want to change this carrier’s status to ';

    public static ORDER_IS_CANCEL_YES_CONFIRMATION_MESSAGE = 'Would you like to cancel this order?';
    public static ORDER_SUCCESS_MESSAGE_IS_CANCEL = 'Order cancelled.';

    public static QUOTE_IS_CANCEL_YES_CONFIRMATION_MESSAGE = 'Would you like to cancel this quote?';
    public static QUOTE_SUCCESS_MESSAGE_IS_CANCEL = 'Quote cancelled.';

    public static SAMPLE_REQUEST_CONFIRM_MESSAGE_CHANGE_STATUS = 'Are you sure you want to change this sample request’s status to ';
    // console-v2's move-back confirm. The body is split around the stage name, which the confirm sets in bold;
    // the buttons name where each one leaves the request ("Stay on {current stage}" / "Move back").
    public static SAMPLE_REQUEST_MOVE_BACK_CONFIRM_TITLE = 'Move back to “#STAGE#”?';
    public static SAMPLE_REQUEST_MOVE_BACK_CONFIRM_LEAD = 'The dates recorded after that step are cleared, and ';
    public static SAMPLE_REQUEST_MOVE_BACK_CONFIRM_TAIL = ' keeps its original timestamp. Anything already sent to the client stays sent.';
    public static SAMPLE_REQUEST_MOVE_BACK_CONFIRM_STAY = 'Stay on #STAGE#';
    public static SAMPLE_REQUEST_MOVE_BACK_CONFIRM_OK = 'Move back';
    // Why a closed or archived request cannot be moved back: "This sample request is <b>status</b>" + the tail.
    public static SAMPLE_REQUEST_MOVE_BACK_BLOCKED_TITLE = 'Cannot Move Back';
    public static SAMPLE_REQUEST_MOVE_BACK_BLOCKED_LEAD = 'This sample request is ';
    public static SAMPLE_REQUEST_MOVE_BACK_BLOCKED_CANCELLED = ', so it cannot be moved back to an earlier stage. A cancelled request is closed and kept as a record of what happened.';
    public static SAMPLE_REQUEST_MOVE_BACK_BLOCKED_CONVERTED = ', so it cannot be moved back to an earlier stage. It has already been converted to a quote, and moving it back would no longer match that quote.';
    public static SAMPLE_REQUEST_MOVE_BACK_BLOCKED_CONVERSION_PENDING = ', so it cannot be moved back to an earlier stage while it is waiting to be converted to a quote. Moving it back now could leave the samples and the quote out of step.';
    public static SAMPLE_REQUEST_MOVE_BACK_BLOCKED_UNCONVERTED = ', so it cannot be moved back to an earlier stage. It was closed as not converting, and the reasons recorded are kept in History.';
    public static SAMPLE_REQUEST_MOVE_BACK_BLOCKED_FAILED = ', so it cannot be moved back to an earlier stage. If it was marked in error, undo it from the Failed button first - that returns it to Samples Despatched.';
    public static SAMPLE_REQUEST_MOVE_BACK_BLOCKED_ARCHIVED = ', so it cannot be moved back to an earlier stage. Archived requests are read-only.';
    // BigPost's API has no cancel or delete (v2 spec: suburbs, depots, getquote, createjob,
    // getstatus, gettracking), so every outcome sends staff to the BigPost portal.
    public static SAMPLE_REQUEST_MOVE_BACK_BIG_POST_REMOVED = 'BigPost job #JOB# removed';
    public static SAMPLE_REQUEST_MOVE_BACK_BIG_POST_DRAFT = 'BigPost does not let us delete jobs through its API - please delete it in the BigPost portal.';
    public static SAMPLE_REQUEST_MOVE_BACK_BIG_POST_BOOKED = 'It is already booked (#STATUS#). BigPost does not let us cancel jobs through its API - please cancel it manually in the BigPost portal.';
    public static SAMPLE_REQUEST_MOVE_BACK_BIG_POST_UNCHECKED = 'Its status could not be checked. Please check it in the BigPost portal and cancel it if needed.';
    public static SAMPLE_REQUEST_MOVE_BACK_UNLEASHED_LOCKED = 'Unleashed sales order #ORDER# is #STATUS#, so product and packing changes will not reach it. Please update it in Unleashed.';
    public static SAMPLE_REQUEST_UNLEASHED_SYNC_FAILED = 'Unleashed sales order #ORDER# could not be updated with this change. Please check it in Unleashed.';
    public static SAMPLE_REQUEST_IS_ARCHIVE_YES_CONFIRMATION_MESSAGE = 'Would you like to archive this sample request?';
    public static SAMPLE_REQUEST_IS_ARCHIVE_NO_CONFIRMATION_MESSAGE = 'Would you like to unarchive this sample request?'
    public static SAMPLE_REQUEST_SUCCESS_MESSAGE_IS_ARCHIVE = 'Sample request archived.';
    public static SAMPLE_REQUEST_SUCCESS_MESSAGE_IS_ARCHIVE_NO = 'Sample request unarchived.';
    public static SAMPLE_REQUEST_IS_CANCEL_YES_CONFIRMATION_MESSAGE = 'Would you like to cancel this sample request?';
    public static SAMPLE_REQUEST_SUCCESS_MESSAGE_IS_CANCEL = 'Sample request cancelled.';

    public static STATUS_QUOTE_PAYMENT_DP = 'DP';
    public static STATUS_QUOTE_PAYMENT_FL = 'FL';
    public static STATUS_QUOTE_PAYMENT_FP = 'FP';
    public static STATUS_QUOTE_PARTIAL_PAYMENT = 'PP';


    //text rate constants
    public static PRODUCT_TYPE_HERO_FAMILY_IMAGE = 'HFI';
    public static SUCCESS_MESSAGE_ADD_SHIPPING_PRICE = 'Shipping price added.';
    public static ERROR_MESSAGE_PRODUCT_IS_ALREADY_ADDED = 'Product is already added.';

    public static HERO_CATEGORY_SKU_YES = 'YES';

    /**
     * `filter[display_for]` on the v3 product list. Switches the query's grouping
     * from the type/collection/category join row to `product.id`, so the total and
     * the returned rows are both counted per PRODUCT.
     *
     * Without it a product mapped to more than one product type (52 of the 207
     * hero SKUs are in both Flooring and Walling) is counted twice in
     * X-Pagination-Total-Count, while Yii collapses the duplicate primary keys
     * when it populates the models — so a page reports more results than it can
     * ever show. It affects nothing else in ProductSearch.
     */
    public static PRODUCT_DISPLAY_FOR_ADVANCE_FILTER = 'advance_filter';
    public static PRODUCT_MAKER_MARK_SALE = 'SALE';
    public static PRODUCT_RESOURCE_DOWNLOAD = 'PR';
    public static PRODUCT_SEAMLESS_TEXTURE_IMAGE_DOWNLOAD = 'STI';
    public static PRODUCT_RECOMMENDED_INSTALLATION_DOWNLOAD = 'PIR';
    public static ACCESSORY_IS_NOT_AVAILABLE_AT_THIS_MOMENT = 'Accessory is currently unavailable.';
    public static ROLE_TRADE_USER = 'Trade';
    public static ROLE_GENERAL_USER = 'General';

    public static QUOTE_CREATE_AS_DRAFT_SUCCESS_MESSAGE = 'Quote created as a draft.';
    public static QUOTE_ORDER_CREATE_SUCCESS_MESSAGE = 'Quote created.';
    public static QUOTE_ORDER_UPDATED_SUCCESSFULLY_MESSAGE = 'Quote updated.';
    public static QUOTE_ITEM_ADDED_SUCCESSFULLY_MESSAGE = 'Product added.';
    public static QUOTE_ORDER_CLONE_SUCCESSFULLY_MESSAGE = 'Quote cloned.';
    public static SAMPLE_CREATED_SUCCESS_MESSAGE = 'Sample request created.';
    public static SAMPLE_UPDATE_SUCCESS_MESSAGE = 'Sample request updated.';
    public static SAMPLE_CLONE_SUCCESS_MESSAGE = 'Sample request cloned.';
    public static SAMPLE_SPLIT_SUCCESS_MESSAGE = 'Sample request split.';

    // public static SHIPPING_IMPORT_SUCCESSFULLY = 'Shipping data has been imported successfully with 15 warnings.';
    public static SHIPPING_IMPORT_SUCCESSFULLY(warningCount) {
        return `Shipping data imported with ${warningCount} warnings.`
    };

    public static SHIPPING_IMPORT_ERROR_MESSAGE = 'Shipping import failed.'

    public static FILE_UPLOAD_STATUS_COMPLETED = 'C'
    public static FILE_UPLOAD_STATUS_FAILED = 'F'
    public static FILE_SELECTION_REQUIRED = 'Select a file to import data.'

    public static COLLECTION_IMPORT_SUCCESSFULLY(warningCount) {
        return `Collection data imported with ${warningCount} warnings.`
    };

    public static COLLECTION_IMPORT_ERROR_MESSAGE = 'Collection import failed.'

    public static ERROR_MESSAGE_MIN_STOCK_QUANTITY = 'Min order quantity will be.';
    public static PRODUCT_OUT_OF_STOCK = 'Product is Out of stock right now.';
    public static SUCCESS_MESSAGE_SCAN_COMPLETED = 'Product added for packing.';
    public static SUCCESS_PRODUCT_SCANNED = 'Product scanned.';
    public static SUCCESS_PRODUCT_SCANNED_ERROR = 'Product sku is not found.';
    public static ERROR_MESSAGE_SAMPLE_PRODUCT_NOT_EXIST = 'Scanned SKU does not exist.';
    public static PRODUCT_NOT_SCANNED = 'Not all products in this order are included in the packaging. add all products to the packaging to change the position.';
    public static CONFIRMATION_BIG_POST_TITLE = 'BigPost Push';
    public static CONFIRMATION_BIG_POST_MESSAGE = 'Are you sure you want to create a job on BigPost platform?';
    public static BIG_POST_JOB_ADDED_SUCCESSFULLY = 'Big post job added.';
    public static BIG_POST_JOB_ALREADY_PUSHED = 'Big post job already created for this sample request';
    public static CONFIRMATION_OFFLINE_PAYMENT_TITLE = 'Offline Payment';
    public static CONFIRMATION_OFFLINE_PAYMENT_MESSAGE = 'Are you sure you want to process offline payment for the order?';
    public static CONFIRMATION_QUOTE_OFFLINE_PAYMENT_MESSAGE = 'Are you sure you want to process offline payment for the quote?';
    public static CONFIRMATION_FINAL_PAYMENT_OVERDUE_TITLE = 'Confirm Status Change';
    public static CONFIRMATION_FINAL_PAYMENT_OVERDUE_MESSAGE = 'Are you sure you want to change this order’s status to Final Payment Outstanding?';

    public static ACTIVITY_TYPE_CREATE_QUOTE = 1;
    public static ACTIVITY_TYPE_QUOTE_STATUS = 2;
    public static ACTIVITY_TYPE_QUOTE_UPDATE = 3;
    public static ACTIVITY_TYPE_DEPOSIT_PAID = 4;
    public static ACTIVITY_TYPE_FINAL_PAYMENT_PAID = 5;
    public static ACTIVITY_TYPE_ORDER_REVIEW = 6;
    public static ACTIVITY_TYPE_PRODUCT_FAVOURITE = 7;
    public static ACTIVITY_TYPE_NEW_CONTACT = 8;
    public static ACTIVITY_TYPE_ACCOUNT_APPROVE = 9;
    public static ACTIVITY_TYPE_ACCOUNT_SUSPEND = 10;
    public static ACTIVITY_TYPE_FULL_PAYMENT_PAID = 11;
    public static ACTIVITY_TYPE_QUOTE_REACTIVATED = 12;
    public static ACTIVITY_TYPE_CONTACT_DECLINED = 13;
    public static ACTIVITY_TYPE_CONTACT_SUSPENDED = 14;
    public static ACTIVITY_TYPE_CONTACT_RESUME = 15;
    public static ACTIVITY_TYPE_ACCOUNT_RESUME = 16;
    public static ACTIVITY_TYPE_PARTIAL_PAYMENT_PAID = 19;

    public static GATHER_COMMITMENT_ADJUST_BALANCE_SUCCESS = 'The client\'s credit balance adjusted.';
    public static GATHER_COMMITMENT_ADJUST_BALANCE_ERROR = 'The client\'s credit balance has not been adjusted.';
    public static GATHER_COMMITMENT_ADJUST_BALANCE_ZERO_ERROR = 'Adjust balance should not be zero.';

    public static DEPOSIT_AMOUNT_MORE_THAN_ONE_PERCENTAGE_ERROR = 'Quote deposit amount should not be less than 1%. set the deposit amount more than 1%.';
    public static DEPOSIT_AMOUNT_LESS_THAN_NINETYNINE_PERCENTAGE_ERROR = 'Quote deposit amount should not be more than 99%. set the deposit amount less than 99%.';

    public static DASHBOARD_PAY_COMMITMENT_DONATE_SUCCESS = 'Commitment payable amount donated.';
    public static DASHBOARD_PAY_COMMITMENT_DONATE_ERROR = 'Commitment payable amount has not been donated.';

    public static DONATION_AMOUNT_RESTRICTION_MESSAGE = 'Donation amount must be greater then zero.';

    public static DASHBOARD_COUNT_FILTER_OF_TODAY = 'today';
    public static DASHBOARD_COUNT_FILTER_OF_WEEK = 'week';
    public static DASHBOARD_COUNT_FILTER_OF_MONTH = 'month';
    public static DASHBOARD_COUNT_FILTER_OF_YEAR = 'year';
    public static SAMPLE_ORDER_PACKED_QUANTITY_UPDATED = 'Packed Quantity updated.'
    public static SHIPPING_PENDING_APPROVED = 'SPA'
    public static ACTIVITY_NOT_IN_ARRAY = [4, 5, 12, 7];
    public static ACTIVITY_TYPE_NOT_IN_ARRAY = [16, 17, 18];

    public static RESOURCE_ADD_SUCCESSFULLY = 'Resource added.';
    public static RESOURCE_EDIT_SUCCESSFULLY = 'Resource updated.';
    public static RESOURCE_DELETE_SUCCESSFULLY = 'Resource deleted.';

    public static RESOURCE_CONFIRM_DELETE_MESSAGE = 'Are you sure you want to delete this resource?';

    public static CATEGORY_ADD_SUCCESSFULLY = 'Category added.';
    public static CATEGORY_EDIT_SUCCESSFULLY = 'Category updated.';
    public static CATEGORY_DELETE_SUCCESSFULLY = 'Category deleted.';
    public static CATEGORY_CONFIRM_DELETE_MESSAGE = 'Are you sure you want to delete this category?';


    public static SUBCATEGORY_ADD_SUCCESSFULLY = 'Subcategory added.';
    public static SUBCATEGORY_EDIT_SUCCESSFULLY = 'Subcategory updated.';
    public static SUBCATEGORY_DELETE_SUCCESSFULLY = 'Subcategory deleted.';
    public static SUBCATEGORY_CONFIRM_DELETE_MESSAGE = 'Are you sure you want to delete this subcategory?';

    public static PROJECT_ADD_SUCCESSFULLY = 'Project added.';
    public static PROJECT_EDIT_SUCCESSFULLY = 'Project updated.';
    public static PROJECT_DELETE_SUCCESSFULLY = 'Project deleted.';
    public static PROJECT_CONFIRM_DELETE_MESSAGE = 'Are you sure you want to delete this project?';


    public static PROJECT_CATEGORY_CONSTANTS = {
        PROJECT_CATEGORY_ADD_SUCCESSFULLY: 'Project category has been added successfully.',
        PROJECT_CATEGORY_EDIT_SUCCESSFULLY: 'Project category has been updated successfully.',
        PROJECT_CATEGORY_DELETE_SUCCESSFULLY: 'Project category has been deleted successfully.',
        PROJECT_CATEGORY_CONFIRM_DELETE_MESSAGE: 'Are you sure you want to delete this project category?',
    }

    public static ARTICLE_CATEGORY_CONSTANTS = {
        ARTICLE_CATEGORY_ADD_SUCCESSFULLY: 'Article category has been added successfully.',
        ARTICLE_CATEGORY_EDIT_SUCCESSFULLY: 'Article category has been updated successfully.',
        ARTICLE_CATEGORY_DELETE_SUCCESSFULLY: 'Article category has been deleted successfully.',
        ARTICLE_CATEGORY_CONFIRM_DELETE_MESSAGE: 'Are you sure you want to delete this article category?',
    }

    public static BLOG_CRUD_CONSTANTS = {
        CREATE: "Blog category has been added successfully.",
        DELETE_CONFIRM: "Are you sure you want to delete this blog?",
        UPDATE: "Blog category has been updated successfully.",
        DELETE: "Blog category has been deleted successfully.",
    }


    public static BLOG_CATEGORY_CONSTANTS = {
        BLOG_CATEGORY_ADD_SUCCESSFULLY: 'Blog category has been added successfully.',
        BLOG_CATEGORY_EDIT_SUCCESSFULLY: 'Blog category has been updated successfully.',
        BLOG_CATEGORY_DELETE_SUCCESSFULLY: 'Blog category has been deleted successfully.',
        BLOG_CATEGORY_CONFIRM_DELETE_MESSAGE: 'Are you sure you want to delete this blog category?',
    }

    public static PROJECT_CATEGORY_FORM_CATEGORY = {
        MAIN_HEADING: 'Add Project Category',
        LABEL: 'Project Category Name',
        REQUIRED_ERROR_MESSAGE: 'Please enter project category name'
    }


    public static ARTICLE_CATEGORY_FORM_CATEGORY = {
        MAIN_HEADING: 'Add Article Category',
        LABEL: 'Article Category Name',
        REQUIRED_ERROR_MESSAGE: 'Please enter article category name'
    }

    public static EMAIL_TITLE_UPDATE = {
        MAIN_HEADING: 'Update Email Template Title',
        LABEL: 'Email Title',
        REQUIRED_ERROR_MESSAGE: 'Please enter email title'
    }


    public static BLOG_CATEGORY_FORM_CATEGORY = {
        MAIN_HEADING: 'Add Blog Category',
        LABEL: 'Blog Category Name',
        REQUIRED_ERROR_MESSAGE: 'Please enter blog category name'
    }

    public static PROJECT_CATEGORY_EDIT_FORM_CATEGORY = {
        MAIN_HEADING: 'Edit Project Category',
        LABEL: 'Project Category Name',
        REQUIRED_ERROR_MESSAGE: 'Please enter project category name'
    }


    public static ARTICLE_CATEGORY_EDIT_FORM_CATEGORY = {
        MAIN_HEADING: 'Edit Article Category',
        LABEL: 'Article Category Name',
        REQUIRED_ERROR_MESSAGE: 'Please enter article category name'
    }

    public static BLOG_CATEGORY_EDIT_FORM_CATEGORY = {
        MAIN_HEADING: 'Edit Blog Category',
        LABEL: 'Blog Category Name',
        REQUIRED_ERROR_MESSAGE: 'Please enter blog category name'
    }

    public static SUPPLIER_MANAGEMENT = {
        EDIT_LEAD_TIME_HEADING: 'Edit Lead Time',
        LEAD_TIME_LABEL: 'Production Completion Lead Time (weeks)',
        LEAD_TIME_PLACEHOLDER: 'e.g. 6',
        LEAD_TIME_FILTER_PLACEHOLDER: 'Weeks',
        LEAD_TIME_HINT: 'Leave blank to mark this supplier as not configured. Purchase orders synced from Unleashed will then have no APCD until a lead time is set.',
        SYNC_CONFIRM_TITLE: 'Sync Suppliers',
        SYNC_CONFIRM_MESSAGE: 'Fetch the latest supplier list from Unleashed? New suppliers will be added and existing details refreshed. Configured lead times are not changed.',
        SYNC_FAILED: 'Suppliers could not be synced from Unleashed. Please try again.',
        LEAD_TIME_NOT_CONFIGURED: 'Not configured (4 weeks default)',
        LEAD_TIME_UPDATED: 'Lead time updated.',
        LEAD_TIME_UPDATE_FAILED: 'Lead time could not be updated. Please try again.'
    }

    public static DRAG_SINGLE_RESTRICTION = 'Please drag single file'
    public static DRAG_SINGLE_IMAGE_VALIDATION = 'Please drag only image file';
    public static IMAGE_SIZE_46_X_46 = '?tr=w-46,h-46';
    public static IMAGE_SIZE_25_X_25 = '?tr=w-25,h-25';
    public static IMAGE_SIZE_590_X_590 = '?tr=w-590,h-590';
    public static IMAGE_SIZE_74_X_74 = '?tr=w-74,h-74';
    public static IMAGE_SIZE_150 = '?tr=w-150';
    public static IMAGE_SIZE_62_X_62 = '?tr=w-62,h-62';
    public static IMAGE_SIZE_50_X_50 = '?tr=w-50,h-50';
    public static IMAGE_SIZE_35_X_35 = '?tr=w-35,h-35';
    public static IMAGE_SIZE_165_X_165 = '?tr=w-165,h-165';
    public static IMAGE_SIZE_300 = '?tr=w-300';
    public static IMAGE_SIZE_60_X_60 = '?tr=w-60,h-60,q-100,f-webp,e-sharpen-50';


    public static IMAGE_SIZES = {
        IMAGE_100_X_300: '?tr=w-300,h-100',
        IMAGE_453_X_453: '?tr=w-453,h-453',
        IMAGE_591_X_964: '?tr=w-964,h-591',
        IMAGE_WIDTH_964: '?tr=w-964',
        IMAGE_WIDTH_453: '?tr=w-453',
    }

    public static BLOGS_CONSTANTS = {
        CREATE: 'Blog has been added successfully.',
        UPDATE: 'Blog has been updated successfully.',
        DELETE: 'Blog has been deleted successfully.',
        DELETE_CONFIRM: 'Are you sure you want to delete this blog?'
    }

    public static ARTICLE_CONSTANTS = {
        CREATE: 'Article has been added successfully.',
        UPDATE: 'Article has been updated successfully.',
        DELETE: 'Article has been deleted successfully.',
        DELETE_CONFIRM: 'Are you sure you want to delete this article?'
    }

    public static PROJECT_IMAGE_CONSTANTS = {
        IMAGE_DELETE: "Project image has been deleted successfully.",
        IMAGE_UPDATE: "Project image has been updated successfully.",
    }

    public static PROJECT_CONTENT = {
        SECTION_DELETE: "Project content section has been deleted successfully.",
        IMAGE_DELETE: "Project content image has been deleted successfully.",
        IMAGE_UPDATE: "Project image has been updated successfully.",
    }


    public static DELETE_PRODUCT_FROM_QUOTE_CONSTANTS = {
        TITLE: 'Delete Product',
        BODY_MESSAGE: 'Are you sure you want delete this product from this quote?',
        ORDER_BODY_MESSAGE: 'Are you sure you want delete this product from this order?',
        NO: "No",
        YES: "Yes"
    }

    public static DELETE_PRODUCT_FROM_SAMPLE_REQUEST_CONSTANTS = {
        TITLE: 'Delete Product',
        BODY_MESSAGE: 'Are you sure you want delete this product from this sample request?',
        NO: "No",
        YES: "Yes"
    }

    public static DELETE_QUOTE_ORDER_CONSTANTS = {
        TITLE: 'Delete Quote/Order',
        BODY_MESSAGE: 'Are you sure you want delete this quote/order?',
        NO: "No",
        YES: "Yes"
    }

    public static DELETE_QUOTE_CONSTANTS = {
        TITLE: 'Delete Quote',
        BODY_MESSAGE: 'Are you sure you want delete this quote?',
        NO: "No",
        YES: "Yes"
    }

    public static UNLINK_QUOTE_CONSTANTS = {
        TITLE: 'Unlink Quote',
        BODY_MESSAGE: 'Are you sure you want to unlink this quote?',
        NO: "No",
        YES: "Yes"
    }

    public static UNLINK_ORDER_CONSTANTS = {
        TITLE: 'Unlink Order',
        BODY_MESSAGE: 'Are you sure you want to unlink this order?',
        NO: "No",
        YES: "Yes"
    }

    public static UNLINK_SAMPLE_ORDER_CONSTANTS = {
        TITLE: 'Unlink Sample Request',
        BODY_MESSAGE: 'Are you sure you want to unlink this sample request?',
        NO: "No",
        YES: "Yes"
    }

    public static CLONE_QUOTE_CONSTANTS = {
        TITLE: 'Clone Quote',
        BODY_MESSAGE: 'Are you sure you want clone this quote?',
        NO: "No",
        YES: "Yes"
    }

    public static CLONE_DRAFT_QUOTE_CONSTANTS = {
        TITLE: 'Clone Draft',
        BODY_MESSAGE: 'Are you sure you want clone this draft?',
        NO: "No",
        YES: "Yes"
    }

    public static CLONE_SAMPLE_CONSTANTS = {
        TITLE: 'Clone Sample Request',
        BODY_MESSAGE: 'Are you sure you want clone this sample request?',
        ZERO_QUANTITY_PRODUCT_NOTE: 'Products with a packed quantity of zero (0) will be removed from this sample request.',
        NO: "No",
        YES: "Yes"
    }

    public static SPLIT_DELIVERY_SAMPLE_CONSTANTS = {
        TITLE: 'Split Delivery Sample Request',
        BODY_MESSAGE: 'Are you sure you want to split this sample request?',
        SPLIT_CONDITION_NOTE: 'Products with a remaining "Packed Qty" will be pulled across to the new sample request.',
        NO: "No",
        YES: "Yes"
    }

    public static DELETE_ORDER_CONSTANTS = {
        TITLE: 'Delete Order',
        BODY_MESSAGE: 'Are you sure you want delete this order?',
        NO: "No",
        YES: "Yes"
    }

    public static CLONE_ORDER_CONSTANTS = {
        TITLE: 'Clone Order',
        BODY_MESSAGE: 'Are you sure you want clone this order?',
        NO: "No",
        YES: "Yes"
    }

    public static DELETE_SAMPLE_REQUEST_CONSTANTS = {
        TITLE: 'Delete Sample Request',
        BODY_MESSAGE: 'Are you sure you want delete this sample request?',
        NO: "No",
        YES: "Yes"
    }

    public static QUOTE_ORDER_MESSAGE_CONSTANTS = {
        PRODUCT_REMOVE: "Product remove successfully",
        PRODUCT_RESTRICTION_FOR_LAST_PRODUCT: "You cannot delete the last product from the sample request.",
        QUOTE_PRODUCT_RESTRICTION_FOR_LAST_PRODUCT: "You cannot delete the last product from the quote.",
        ORDER_PRODUCT_RESTRICTION_FOR_LAST_PRODUCT: "You cannot delete the last product from the order.",
        QUOTE_ORDER_REMOVE: "Quote/Order has been deleted successfully.",
        QUOTE_REMOVE: "Quote has been deleted successfully.",
        QUOTE_DELETED: "The quote has been deleted.",
        ORDER_REMOVE: "Order has been deleted successfully.",
        ORDER_DELETED: "The order has been deleted.",
        UNLINK_QUOTE: "Quote has been unlinked successfully.",
        UNLINK_ORDER: "Order has been unlinked successfully.",
        UNLINK_SAMPLE_ORDER: "Sample requests has been unlinked successfully.",
        UNAUTHORIZED_UNLINK_QUOTE: 'You are not authorized to unlink this quote.',
        UNAUTHORIZED_UNLINK_ORDER: 'You are not authorized to unlink this order.',
        UNAUTHORIZED_UNLINK_SAMPLE_REQUEST: 'You are not authorized to unlink this sample request.',
        UNAUTHORIZED_UNLINK_SAMPLE_ORDER: 'You are not authorized to unlink this sample request.'
    }

    public static SAMPLE_REQUEST_MESSAGE_CONSTANTS = {
        SAMPLE_REQUEST_REMOVE: "The sample request has been deleted successfully.",
        SAMPLE_REQUEST_DELETED: "The sample request has been deleted.",
        PRODUCT_REMOVE: "The product has been removed from the sample request successfully.",
        PRODUCT_RESTRICTION_FOR_LAST_PRODUCT: "You cannot delete the last product from the sample request.",
        SAMPLE_PRODUCT_NOTES_UPDATE: "Product notes have been updated successfully.",
    }

    public static PDF_UPLOAD_ALERT = 'Upload pdf files only.'

    public static PEBBL_ADD_SUCCESSFULLY = 'Pebbl added.';
    public static PEBBL_EDIT_SUCCESSFULLY = 'Pebbl updated.';
    public static PEBBL_DELETE_SUCCESSFULLY = 'Pebbl deleted.';
    public static PEBBL_CONFIRM_DELETE_MESSAGE = 'Are you sure you want to delete this Pebbl entry?';
    public static PEBBL_FILE_UPLOAD_ALERT = 'Only PDF, DOC, DOCX, XLS, XLSX, PNG, JPG, and JPEG files are allowed.';

    // Pebbl Ingest toast / banner copy (used as fallbacks when the polling
    // response omits its own message/error text).
    public static PEBBL_INGESTION_COMPLETED_MESSAGE = 'Ingestion completed.';
    public static PEBBL_INGESTION_FAILED_MESSAGE = 'Ingestion failed.';
    public static PEBBL_INGESTION_PREVIOUS_FAILED_MESSAGE = 'Previous ingestion failed.';
    public static PEBBL_INGESTION_STATUS_CHECK_FAILED_MESSAGE = 'Status check failed after retries.';
    public static PEBBL_INGESTION_POLLING_ERROR_MESSAGE = 'Polling error.';
    public static PEBBL_INGESTION_START_FAILED_MESSAGE = 'Failed to start ingestion.';
    public static PEBBL_INGESTION_OPERATION_FAILED_MESSAGE = 'Operation failed.';

    public static FAQ_CONSTANTS = {
        UPDATE: "FAQ has been updated successfully.",
        ARTICLE_ASSIGN_SUCCESSFULLY: "Article has been assigned successfully.",
        SELECT_ARTICLE_MESSAGE: "Please select articles",
        CREATE: 'FAQ has been added successfully.',
        DELETE: 'FAQ has been deleted successfully.',
        DELETE_CONFIRM: 'Are you sure you want to delete this FAQ?',
        DELET_VIDEO: 'Video has been deleted successfully'
    }

    public static BIG_POST = {
        CREATE: 'Big post job has been added successfully.',
        CARTON_TYPE: 'Carton'
    }
    public static USER_UPDATE_PROFILE_CONSTANTS = {
        IMAGE_TYPE_VALIDATION_MESSAGE: 'Please upload image',
        IMAGE_SIZE_VALIDATION: 'Maximum file size to upload is 25MB . If you are uploading a photo, try to reduce its resolution to make it under 25MB.'
    }

    public static FILE_UPLOAD_CONSTANTS = {
        IMAGE_TYPE_VALIDATION_MESSAGE: 'Please upload image',
        IMAGE_SIZE_VALIDATION: 'Maximum file size to upload is 40MB .'
    }

    public static SUCCESS_RESET_PASSWORD = 'Password updated.';
    public static SUCCESS_USER_PROFILE_UPDATE = 'Profile updated.';

    public static CART_PRODUCT_EXIST_MESSAGE = 'This product is already exist in your quote.';
    public static SAMPLE_CART_PRODUCT_EXIST_MESSAGE = 'This product is already exist in your sample request.';


    public static NOMINATE_DATE_MAIL = {
        TITLE: 'Confirm Nominate Date Mail Send',
        MESSAGE: 'Are you sure you want to send Nominate Delivery Date email to a User?',
        MAIL_SEND: 'Mail has been send to user successfully.',
        MAIL_NOT_SEND: 'Mail has been not send to user.',
    }

    public static DELIVERY_DATE_SET = `Delivery date has been successfully set to this Order.`

    // public static EMAIL_FOOTER_CONTENT = `This email is intended only to be read or used by the addressee. It is confidential and may contain legally privileged information. If you are not the addressee indicated in this message (or responsible for delivery of the message to such person), you may not copy or deliver this message to anyone, and you should destroy this message and kindly notify the sender by reply email. Confidentiality and legal privileges are not waived or lost by reason of mistaken delivery to you. Please consider the environment before printing this email.`;
    public static EMAIL_FOOTER_CONTENT = `This email is intended only to be read or used by the addressee. It is confidential and may contain legally privileged information. If you are not the addressee indicated in this message (or responsible for delivery of the message to such person), you may not copy or deliver this message to anyone, and you should destroy this message and kindly notify the sender by reply email. Confidentiality and legal privileges are not waived or lost by reason of mistaken delivery to you. Please consider the environment before printing this email.`;

    public static EMAIL_TEMPLATE_TITLE = {
        ADMIN: 'Preview of Admin/Sales Rep. Email',
        CUSTOMER: 'Preview of Customer Email',
        CARRIER: 'Preview of Carrier Email',
        PAYMENT_PENDING: 'Preview of Final Payment Pending Email'
    }

    public static EMAIL_TEMPLATE_TITLE_MESSAGE = {
        SUCCESS_EMAIL_TEMPLATE_TITLE: 'Email template title has been updated successfully.',
        SUCCESS_EMAIL_TEMPLATE_BODY: 'Email template body has been updated successfully.',
        SUCCESS_EMAIL_TEMPLATE_INSTRUCTION: 'Email template instruction has been updated successfully.'
    }
    public static EMAIL_TEMPLATE_KEY_NAME = [
        { ID: 0, EMAIL_FOR: 'NOMINATE_DELIVERY_DATE', KEY_NAME: 'nominate-delivery-date', status: ['ONDD'], TYPE: 'O' },
        { ID: 1, EMAIL_FOR: 'SHIPPING_QUOTE_REQUIRED', KEY_NAME: 'shipping-quote-added', status: ['MDQR'], TYPE: 'O' },
        { ID: 2, EMAIL_FOR: 'GOODS_DISPATCH', KEY_NAME: "order-dispatched", status: ['DD'], TYPE: 'O' },
        { ID: 3, EMAIL_FOR: 'ARRIVAL_AT_WAREHOUSE', KEY_NAME: 'stock-landed', status: ['AAW'], TYPE: 'O' },
        { ID: 4, EMAIL_FOR: 'FINAL_PAYMENT_PENDING', KEY_NAME: 'final-payment-reminder', status: ['FPP'], TYPE: 'O' },
        { ID: 5, EMAIL_FOR: 'IN_TRANSIT', KEY_NAME: 'in-transit', status: ['IT'], TYPE: 'O' },
        { ID: 6, EMAIL_FOR: 'ORDER_COMPLETED', KEY_NAME: 'order-complete', status: ['C'], TYPE: 'O' },
        { ID: 7, EMAIL_FOR: 'SHIPPING_REQUIRED', KEY_NAME: 'shipping-quote-required', status: ['SR'], TYPE: 'O' },
        { ID: 8, EMAIL_FOR: 'SAMPLE_ORDER_COMPLETED', KEY_NAME: 'sample-order-complete', status: ['SDEL'], TYPE: 'SO' },
        { ID: 9, EMAIL_FOR: 'SAMPLE_DISPATCH', KEY_NAME: 'sample-dispatched', status: ['SDESP'], TYPE: 'SO' },
        {
            ID: 10,
            EMAIL_FOR: 'GOODS_DISPATCH_PENDING',
            KEY_NAME: 'trade-user-carrier-added',
            status: ['DOTT'],
            TYPE: 'O'
        },
    ]

    public static EMAIL_TEMPLATE_KEY_NAME_FOR_ADD_RECIPIENT = [
        'nominate-delivery-date',
        'shipping-quote-added',
        'order-dispatched',
        'stock-landed',
        'final-payment-reminder',
        'in-transit',
        'order-complete',
        'shipping-quote-required',
        'sample-order-complete',
        'sample-dispatched',
        'trade-user-carrier-added',
        'trade-user-sample-create',
        'trade-user-quote-create',
        'sample-request-received',
        'trade-existing-user-quote-create'
    ];

    public static ACCOUNT_STATUS_EMAIL_KEY_NAME = {
        SUSPENDED_ACCOUNT: 'trade-user-account-suspended',
        DECLINED_ACCOUNT: 'trade-user-account-denied'
    }

    public static EMAIL_FOR_NEW_CONTACT = {
        SAMPLE: 'trade-user-sample-create',
        QUOTE: 'trade-user-quote-create'
    }

    public static EMAIL_FOR_SHIPPING_QUOTE_REQUIRED = 'shipping-quote-required'
    public static EMAIL_FOR_SHIPPING_QUOTE_PENDING = 'shipping-quote-pending'

    public static EMAIL_KEYS = {
        SAMPLE_REQUEST_RECEIVED: 'sample-request-received',
        NOMINATED_DELIVERY_DATE: 'nominate-delivery-date',
        QUOTE_CREATE_FOR_EXISTING_USER: 'trade-existing-user-quote-create',
        TRADE_ACCOUNT_APPROVED: 'trade-user-account-approved'
    }

    public static CREATE_QUOTE_WITH_NEW_ACCOUNT = 'Submit your contact form after that you can submit Quote.';
    public static CREATE_SAMPLE_WITH_NEW_ACCOUNT = 'Submit your contact form after that you can submit Sample Request.';
    public static STREET_NO_REQUIRED = 'Enter valid address street number is missing'
    public static TRACKING_NUMBER_COPIED = 'Tracking number copied!'
    public static ADDRESS_COPIED = 'Collection Address copied!'
    public static CRN_COPIED = 'BPAY Client Reference copied!'
    public static BILLER_CODE_COPIED_SUSSCEES = 'Biller Code copied to clipboard!'
    public static BILLER_CODE_COPIED_FAILED = 'Failed to copy Biller Code!'


    public static CONTACT_INFO = 'contactInfo'
    public static DELIVERY_DETAIL_INFO = 'deliveryDetailInfo'
    public static TRUCK_TYPE_INFO = 'truckTypeInfo'
    public static STOCK_REQUIRED_DATE_INFO = 'stockRequiredDateInfo'

    public static QUOTE_ORDER_CONTACT_UPDATE = 'Contact updated.'
    public static SITE_CONTACT_DELIVERY_DETAIL_UPDATE = 'Site contact delivery detail updated.'
    public static QUOTE_ORDER_DELIVERY_DETAIL_UPDATE = 'Delivery address updated.'
    public static CRATES_UPDATE = 'Crates updated.'
    public static TRUCK_TYPE_DETAIL_UPDATE = 'Truck type detail has been updated successfully.'
    public static STOCK_REQUIRED_DATE_UPDATE = 'Stock required date updated.'
    public static DELIVERY_INSTRUCTION_UPDATE = 'Delivery instruction updated.'
    public static DELIVERY_INSTRUCTION_ADDED = 'Delivery instruction added.'
    public static QUOTE_ORDER_PROJECT_DETAIL_UPDATE = 'Project detail updated.'
    public static CREDIT_SPECIFIER_UPDATE = 'Credit specifier updated.'
    public static DELIVERY_CHARGE_UPDATE = 'Delivery charge updated.'

    public static PO_BOX_ADDRESS_REGEX = /\b(?:p\.?\s*o\.?|post\s+office)(\s+)?(?:box|[0-9]*)?\b/i

    public static IN_SHIPPING_ZONE_YES = 'Y';
    public static IN_SHIPPING_ZONE_NO = 'N';

    public static IS_EXPIRED_YES = 'YES';
    public static IS_EXPIRED_NO = 'NO';
    public static S3_BUCKET_URL = 'https://ik.imagekit.io/gatherco/WEBSITE_ASSET_STAGING';
    public static NO_IMAGE_PRODUCT = 'https://ik.imagekit.io/gatherco/WEBSITE_ASSET_STAGING/no-image-product.svg';
    static S3_PRODUCT_IMAGES_IMAGE_KIT_URL = environment.imageKitEndpoint;
    public static S3_PRODUCT_RESOURCE_IMAGE_KIT_URL = environment.imageKitEndpoint + ''
    public static S3_ACCOUNT_LOGO_IMAGE_KIT_URL = environment.imageKitEndpoint + '/account-logo'
    public static S3_FINISH_SPECIFICATION_IMAGE_KIT_FOLDER = '/ICONS/Specifications'
    public static S3_MAKER_MARK_IMAGE_KIT_URL = environment.imageKitEndpoint + '/ICONS/MakersMarks'
    // Backend user avatars live in the ImageKit /profile-picture/ folder. Build them from this
    // constant, never from S3_PRODUCT_IMAGES_IMAGE_KIT_URL (the bare endpoint) — that drops the
    // folder and every avatar 404s. Note it already ends in '/'.
    public static S3_USER_LOGO_IMAGE_KIT_URL = environment.imageKitEndpoint + '/profile-picture/'
    // Person placeholder shown when a user has no profile picture, or when the one on record is
    // missing from ImageKit. Resolves per environment (WEBSITE_ASSETS in production,
    // WEBSITE_ASSET_STAGING elsewhere) — do not hardcode either folder at the call site, and do
    // not point this at the S3 bucket: gatherco.s3…/WEBSITE_ASSETS/default_profile.png is a 404.
    public static DEFAULT_PROFILE_IMAGE = environment.imageKitEndpoint + '/' + environment.imageKitAssetFolder + '/default_profile.png'
    public static S3_PRODUCT_IMPORT_KIT_URL = environment.imageKitEndpoint + '/product-import/'


    public static IS_QUOTE_EXPIRED_TRUE = true;
    public static IS_QUOTE_EXPIRED_FALSE = false;
    public static IS_QUOTE_DELETED_FALSE = false;

    // activity.is_quote_expired is the Y/N enum on the activity row (not the boolean quote.is_expired):
    // reactivation flags every superseded activity with 'Y' so consumers can ignore the previous cycle.
    public static ACTIVITY_IS_QUOTE_EXPIRED_YES = 'Y';


    public static IS_ORDER = 'Y';

    public static ACCEPT_QUOTE_CONSTANTS = {
        TITLE: 'Accept Quote',
        BODY_MESSAGE: 'Are you sure you want to accept this quote on behalf of your client?',
        NO: "Cancel",
        YES: "Accept Quote"
    }

    public static REACTIVE_QUOTE_CONSTANTS = {
        TITLE: 'Reactivate Quote',
        BODY_MESSAGE: 'Are you sure you want to reactivate this quote?',
        // SQ (offline) quotes are re-priced from Unleashed as part of reactivation, so the admin is
        // told up front — the sync can take a few seconds and it may change items/prices/delivery.
        OFFLINE_BODY_MESSAGE: 'Are you sure you want to reactivate this quote? It will be re-synced from Unleashed, so items, prices and the delivery charge will be refreshed.',
        NO: "Cancel",
        YES: "Reactivate Quote"
    }

    public static SUCCESS_QUOTE_ACCEPTED = 'Quote accepted.';
    public static QUOTE_ACCEPT_HAS_DISCONTINUED_PRODUCTS = 'Discontinued product(s) found. Please remove or replace them before accepting the quote.';
    public static QUOTE_PAYMENT_HAS_DISCONTINUED_PRODUCTS = 'Discontinued product(s) found. Please remove or replace them before making a payment.';
    public static SUCCESS_QUOTE_NOT_ACCEPTED = 'Quote has not been accepted.';
    public static SUCCESS_QUOTE_REACTIVATED = 'Quote reactivated.';

    public static QUOTE_QUANTITY_UPDATED = 'Quantity updated.';
    // An allocated assembled line raised past what its own sources have free (ClickUp 86d49wbtt).
    public static ASSEMBLED_LINE_RELEASED_AFTER_QUANTITY_INCREASE = 'There is not enough free stock on this line\'s source for the extra quantity, so the whole line has moved to Unassigned Stock.';
    public static QUOTE_DISCOUNT_PRICE_UPDATED = 'Discount Price updated.';
    public static INTERNAL_NOTE_ADDED = 'Internal note added.';
    public static INTERNAL_NOTE_UPDATE = 'Internal note updated.';
    public static EXTERNAL_NOTE_ADDED = 'External note added.';
    public static EXTERNAL_NOTE_UPDATE = 'External note updated.';
    public static MILESTONE_NOTE_ADDED = 'Milestone note added.';
    public static MILESTONE_NOTE_UPDATE = 'Milestone note updated.';

    public static PACKING_SLIP_UPLOAD = 'Packing slip uploaded.';
    public static PACKING_SLIP_REQUIRED = 'Select file.';
    public static SUCCESS_MESSAGE_ON_PACKING_SLIP_DELETE = 'Packing slip deleted.';

    public static PARTIAL_PAYMENT_RECEIPT_UPLOAD = 'Payment remittance uploaded.';
    public static PARTIAL_PAYMENT_RECEIPT_REQUIRED = 'Select file.';
    public static SUCCESS_MESSAGE_ON_PARTIAL_PAYMENT_RECEIPT_DELETE = 'Payment receipt deleted.';
    public static ERROR_MESSAGE_UPLOAD_PAYMENT_REMITTANCE = 'Failed to upload payment receipt. Try again.'
    public static AMOUNT_NOT_NULL_MESSAGE = 'Input an amount that is greater than or equal to zero to continue.';
    /**
     * Identify download type
     */
    public static DOWNLOAD_QUOTE = 'DQ';
    public static DOWNLOAD_INVOICE = 'DI';
    public static DOWNLOAD_PAYMENT_RECEIPT = 'DPR';
    public static DOWNLOAD_CONSUMABLES = 'DCS';

    public static GATHERCO = {
        ABN_NUMBER: '65 601 533 144',
        ADDRESS: 'Sydney - AUSTRALIA',
        CONTACT_NUMBER: '1800 428 437',
        EMAIL_ADDRESS: 'hi@gatherco.com.au',
        INFO: '@gathercoaustralia',
    }

    public static DELIVERY_ADDRESS_REQUIRED_AFTER_COLLECTION = `Enter the delivery address - the address shown was GatherCo's collection address.`;

    public static WAREHOUSE_COLLECTION_ADDRESS = {
        ADDRESS_LINE_1: '89 Fairey Road',
        ADDRESS_LINE_2: '',
        SUBURB: 'South Windsor',
        TOWN_CITY: 'Hawkesbury City Council',
        STATE_REGION: 'NSW',
        POST_CODE: '2756',
        COUNTRY: 'Australia',
    }

    public static EMAIL_IS_REQUIRED_FOR_PDF_EXPORT = `Please select or enter email address`;

    /**
     * Identify share type
     */
    public static SHARE_QUOTE = 'SQ';
    public static SHARE_INVOICE = 'SI';
    public static SHARE_PAYMENT_RECEIPT = 'SPR';

    public static SUCCESS_MESSAGE_ON_SHARE_QUOTE = 'Check your email address for the shared quote PDF.';
    public static SUCCESS_MESSAGE_ON_SHARE_ORDER = 'Check your email address for the shared invoice PDF.';
    public static SUCCESS_MESSAGE_ON_SHARE_PAYMENT = 'Check your email address for the shared receipt PDF.';
    public static INFO_MESSAGE_ON_FINAL_PAYMENT = "You can't make the final payment until deposit paid.";

    public static GOOGLE_MAP_URL = 'https://www.google.com/maps/place/';

    public static UNSPECIFIED_CARRIER_STATUS = 'Unspecified';
    public static CARRIER_CREATED = 'Carrier created.';
    public static CARRIER_UPDATED = 'Carrier updated.';
    public static CARRIER_QUOTE_UPDATED = 'Carrier set.';

    public static SUCCESS_MESSAGE_ON_EXPORT_PDF_QUOTE = 'Export quote link sent to the entered email address.';
    public static SUCCESS_MESSAGE_ON_EXPORT_PDF_INVOICE = 'Export invoice link sent to the entered email address.';

    public static SUCCESS_MESSAGE_ON_EXPORT_PDF_TRACKING_LINK = 'Export order tracking link sent to the entered email address.';

    public static SUCCESS_MESSAGE_ON_EXPORT_PDF_CONSUMABLE_SUMMARY = 'Consumable summary PDF attached and sent to the entered email address.';

    public static SUCCESS_MESSAGE_ON_STATUS_MAIL_SENT = 'Status update mail sent, check your email.';
    public static ERROR_MESSAGE_ON_STATUS_MAIL_SENT = 'Couldn’t send status update mail.';

    public static SUCCESS_MESSAGE_ON_MDQR_MAIL_SENT = 'Shipping quote required mail sent.';
    public static ERROR_MESSAGE_ON_MDQR_MAIL_SENT = 'Couldn’t send shipping quote required mail.';

    public static SUCCESS_MESSAGE_ON_NOMINATE_DELIVERY_DATE_MAIL_SENT = 'Nominate delivery date mail sent, check your email.';
    public static ERROR_MESSAGE_ON_NOMINATE_DELIVERY_DATE_MAIL_SENT = 'Couldn’t send nominate delivery date mail.';


    public static SUCCESS_MESSAGE_ON_COLLECTION_AND_DELIVERY_REQUEST_MAIL_SENT = 'Collection and delivery request mail sent to the carrier.';
    public static ERROR_MESSAGE_ON_COLLECTION_AND_DELIVERY_REQUEST_MAIL_SENT = 'Couldn’t send collection and delivery request mail.';

    public static TAX_GST_PERCENTAGE = 10;
    public static PRODUCT_PRICE_ZERO_MESSAGE = 'Product price must be grater than 0.';

    public static NOTES = {
        NOTE_TYPE_INTERNAL: 'internal',
        NOTE_TYPE_EXTERNAL: 'external',
        NOTE_TYPE_MILESTONE: 'milestone',
        NOTE_TYPE_REPLY: 'reply'
    }

    public static PRODUCT_ADDED_SUCCESS_MESSAGE(moduleName: String) {
        return `Product added to the ${moduleName}.`
    };

    public static IS_FINAL_PAYMENT_PAID(paymentBadgeTitle?: string) {
        if (paymentBadgeTitle && paymentBadgeTitle.includes('<br>')) {
            paymentBadgeTitle = paymentBadgeTitle.replace(/<br>/g, '');
        }
        if (paymentBadgeTitle == Constants.BADGE_TITLE_FINAL_PAYMENT_OUTSTANDING) {
            return `Final Payment Outstanding:`;
        } else {
            return `Remaining Balance:`;
        }
    }

    public static SUCCESS_MESSAGE_ON_SAMPLE_REQUEST_MAIL_SEND = 'Sample request email sent to the entered email address.';

    public static DELIVERY_ADDRESS_REQUIRED = 'Delivery Address Required';
    public static MANUAL_DELIVERY_QUOTE_REQUIRED = 'Manual Delivery Quote Required';
    public static MANUAL_DELIVERY_QUOTE_ADDED = 'Manual Delivery Quote Added';
    public static MANUAL_DELIVERY_QUOTE_APPLIED = 'Manual Delivery Quote Applied';
    public static AUTO_DELIVERY_QUOTE_APPLIED = 'Auto Delivery Quote Applied';
    public static GOODS_DISPATCHED_UNSPECIFIED = 'Goods Despatch Unspecified';
    public static GOODS_DISPATCHED_PENDING = 'Delivery Out To Tender';
    // public static GOODS_DISPATCHED_CONFIRMED = 'Goods Despatch Despatched';
    public static GOODS_DISPATCHED_CONFIRMED = 'Delivery Despatched';
    public static GOODS_DISPATCHED_DELIVERD = 'Delivered';
    public static GOODS_DISPATCHED_ACCEPTED = 'Delivery Confirmed';
    public static GOODS_DISPATCHED_DECLINED = 'Delivery Unconfirmed';
    public static GOODS_DISPATCHED_CLIENT_ORGANIZED = 'Delivery Client Organized';
    public static QUOTE_EXPIRED_STATUS_NAME = 'Expired';
    public static QUOTE_FOLLOW_UP_STATUS_NAME = 'Follow Up';
    public static QUOTE_ACCEPTED_STATUS_NAME = 'Quote Accepted';
    public static ORDER_FINAL_PAYMENT_OUTSTANDING_STATUS_NAME = 'Final Payment Outstanding';
    public static ORDER_FINAL_PAYMENT_PAID_STATUS_NAME = 'Final Payment Paid';
    public static ORDER_COMPLETE_STATUS_NAME = 'Complete';
    public static QUOTE_STATUS_ARCHIVED = 'Archived';
    public static QUOTE_STATUS_BAD_DEBT = 'Bad Debt';
    public static CLIENT_ORGANIZED_ORDER_COLLECTION = 'Client Organized Order Collection';
    public static ORDER_WAREHOUSE_COLLECTION_CARRIER_NAME = 'Client Organized';

    // public static CREDIT_APPLY_ERROR(profileName: any) {
    //     return `Credit applied may only amount to a maximum ${profileName}% of total order value.`
    // };

    public static VIEW_QUOTE_CONSTANTS = {
        //#region   SINGLE CONSTANTS
        NEW_CREDIT_EARN: `This is calculated on the sub-total minus the credit applied to this order.`,
        CREDIT_APPLY_ERROR: `Credit applied may only amount to a maximum 49% of total order value.`,
        CREDIT_APPLY_SLIDER_DESCRIPTION: `Shift the slider below to assign part or all of your available credit to this quote. The credit applied will <u><b>not</b></u> be deducted from the initial deposit sum, but rather - will be subtracted from your final payment.`,

        ORDER_DETAIL_AND_DELIVERY: `This platform operates our automated trucking calculator. For many destinations, completing the below form instantly generates an applicable delivery fee.`,
        //#endregion

        // EXPIRE QUOTE
        EXPIRE_QUOTE: {
            TOP_DESCRIPTION_FOR_REACTIVE: ` You can reactivate this quote, however, the availability of certain
    stock and sizes as well as prices may have changed.`
        },

        //TERM & CONDITIONS
        TERMS_CONDITIONS: {
            FOOTER: `Acceptance:<br>You willing accept our full set of Terms when:<br>
      (a) you provide us with a completed account application form or apply for an online account;<br>
      (b) you submit an Order (online or otherwise);<br>
      (c) you accept Delivery of, or any part of ,any Goods pursuant to an Order; or<br>
      (d) you make Payment ,or partial Payment, for any Goods supplied by us,<br>
      whichever occurs first.`
        },

        //#region TOP DESCRIPTION
        TOP_DESCRIPTION: {
            DEPOSIT_PAYMENT__IS_REQUIRED: `Delivery on this order must be calculated manually, and will be billed on
      a<br /> separate invoice. We will be in contact shortly.`,
            QUOTE_CREATED_SHORT_IN_SHIPPING_ZONE_NO: `Due to address delivery a <b>Request delivery quote</b>
      must be made. The delivery cost will then be added to your order prior to deposit payment. Response will be within 24 - 48 hrs.`,
            QUOTE_MANUAL_DELIVERY_QUOTE_ADDED_SHORT: `Delivery cost has been calculated and added below. Please approved<br />
      delivery cost to create
      final order.`
        },

        //#endregion

        //#region FOOTER DESCRIPTION
        FOOTER_DESCRIPTION: {
            OUT_OF_DELIVERY_ZONE: ` Transport must be calculated manually, and will be billed on a separate
      invoice. We will be in
      contact shortly.`,
            IN_SHIPPING_ZONE: `Delivery is
      calculated
      automatically,
      based on pre-determined
      parameters. Please <a href="/contact-us">make contact</a> for further information.`,
            QUOTE_CREATED_SHORT_IN_SHIPPING_ZONE: ` Delivery must be calculated manually, and will be added to your order
      prior to deposit payment.
      Once you request a delivery quote, it will become an Order. Response
      will be within 24 - 48 hrs.`
        }
        //#endregion
    }

    public static DELETE_NOTE_CONSTANTS = {
        TITLE: 'Delete Note',
        BODY_MESSAGE: 'Are you sure you want delete this note?',
        NO: "No",
        YES: "Yes"
    }

    public static DELETE_INTERNAL_NOTE_CONSTANTS = {
        TITLE: 'Delete Internal Note',
        BODY_MESSAGE: 'This internal note will be permanently removed. This can’t be undone.',
        NO: "No",
        YES: "Yes"
    }

    public static DELETE_ATTACH_FILE_CONSTANTS = {
        TITLE: 'Delete Attached File',
        BODY_MESSAGE: 'Are you sure you want to delete this attached file?',
        NO: "No",
        YES: "Yes"
    }

    public static DELETE_PACKING_SLIP = {
        TITLE: 'Delete Packing Slip',
        BODY_MESSAGE: 'Are you sure you want to delete this Packing Slip?',
        NO: "No",
        YES: "Yes"
    }

    public static DELETE_PARTIAL_PAYMENT_RECEIPT = {
        TITLE: 'Delete Payment Receipt',
        BODY_MESSAGE: 'Are you sure you want to delete this Payment Remittance?',
        NO: "No",
        YES: "Yes"
    }

    public static DELETE_EXTERNAL_NOTE_CONSTANTS = {
        TITLE: 'Delete External Note',
        BODY_MESSAGE: 'Are you sure you want to delete this external note?',
        NO: "No",
        YES: "Yes"
    }
    public static SUCCESS_MESSAGE_ON_EXTERNAL_NOTE_DELETE = 'The external note deleted.';

    public static SUCCESS_MESSAGE_ON_INTERNAL_NOTE_DELETE = 'Internal note deleted.';
    public static SUCCESS_MESSAGE_ON_MILESTONE_NOTE_DELETE = 'Milestone note deleted.';
    public static SUCCESS_MESSAGE_ON_ATTACH_FILE_DELETE = 'Attached File deleted.';

    public static ERROR_MESSAGE_ON_INTERNAL_NOTE_DELETE = 'Couldn’t delete internal note.';

    public static TRUCK_TYPE_FORK_TRUCK_ID = 1;
    public static TRUCK_TYPE_HI_AB_TRUCK_ID = 2;
    public static TRUCK_TYPE_FLATBED_TRUCK_ID = 3;
    public static TRUCK_TYPE_OTHER_ID = 4;

    public static UPDATE_PRODUCT_QUANTITY_AFTER_DELIVERY_CHARGE_ADDED = {
        //BODY_MESSAGE: 'Applied delivery charge will be re-calculated. Are you sure you want update the quantity of this product?',
        BODY_MESSAGE: 'This quote has already had a manual delivery fee calculated. Proceeding with further edits will require a new delivery fee to be manually applied. Do you still want to continue?',
    }

    public static UPDATE_PRODUCT_QUANTITY_AFTER_DELIVERY_CHARGE_ADDED_MESSAGE(type: string) {
        return `This ${type} has already had a manual delivery fee calculated. Proceeding with further edits will require a new delivery fee to be manually applied. Do you still want to continue?`;
    };

    public static UPDATE_PRODUCT_DISCOUNT_AFTER_DELIVERY_CHARGE_ADDED = {
        //BODY_MESSAGE: 'Applied delivery charge will be re-calculated. Are you sure you want update the quantity of this product?',
        BODY_MESSAGE: 'This quote has already had a manual delivery fee calculated. Proceeding with further edits will require a new delivery fee to be manually applied. Do you still want to continue?',
    }

    public static UPDATE_QUOTES_AFTER_DEPOSIT_PAYMENT_DUE_CREDIT_APPLIED = {
        BODY_MESSAGE: 'The current status of this quote is "DEPOSIT PAYMENT DUE." Once you add the product or update the quantity, the quote will become editable, and consequently, its status will change. Please confirm this information.',
    }

    public static DEPOSIT_PAYMENT_PAID_SUCCESS = 'Deposit payment processed.';
    public static FINAL_PAYMENT_PAID_SUCCESS = 'Final payment processed.';
    public static FULL_PAYMENT_PAID_SUCCESS = 'Full payment processed.';
    public static PARTIAL_PAYMENT_PAID_SUCCESS = 'Partial payment processed.';
    public static INSTALLMENT_CREATED_SUCCESS = 'Installment created.';
    public static INSTALLMENT_DELETED_SUCCESS = 'Installment deleted.';
    public static INSTALLMENT_DELETE_CONFIRM = 'Delete this installment? The amount will be merged into the next installment (or the final payment).';
    public static INSTALLMENT_AMOUNT_TOO_LARGE = 'The split amount must be less than the current installment amount.';
    public static INSTALLMENT_AMOUNT_INVALID = 'Enter a valid amount greater than zero.';

    public static NOTE_TYPE_ACCOUNT = 'AN';
    public static NOTE_TYPE_CONTACT = 'CN';
    public static NOTE_TYPE_QUOTE = 'QN';
    public static NOTE_TYPE_FEEDBACK = 'FN';
    public static PRODUCT_TYPE_SM = '';
    public static SAMPLE_PRODUCT_TYPE_SM = 'SM';
    public static IS_DEFAULT_ADDRESS_YES = 'Y';
    public static IS_DEFAULT_ADDRESS_NO = 'N';
    public static IS_DISPLAY_FOR_SAMPLE_LIBRARY_YES = 'Y';
    public static IS_DISPLAY_FOR_SAMPLE_LIBRARY_NO = 'N';
    public static IS_NEW_USER_YES = '1';
    public static IS_NEW_USER_NO = '0';

    public static PRODUCT_IS_PREVIOUSLY_ORDERED_IN_SAMPLE_REQUEST = 'This account has previously requested these samples.<br> Would you still like to proceed?';
    public static ERROR_FOR_DOWNLOAD_TABLE = 'Select the user to download the table.';

    public static CONSUMABLE_QUANTITY_TYPE_GOLD = 'GOLD';
    public static CONSUMABLE_QUANTITY_TYPE_DEFENDER = 'DEFENDER';
    public static CONSUMABLE_QUANTITY_TYPE_LATICRETE_ADHESIVE = 'LATICRETE_ADHESIVE';

    public static SUCCESS_MESSAGE_OF_FINISH_VALUE_UPDATE = 'Finish value updated.';
    public static SUCCESS_MESSAGE_OF_FINISH_VALUE_ADDED = 'Finish value added.';
    public static SUCCESS_MESSAGE_OF_FINISH_VALUE_REMOVED = 'Finish value removed.';

    public static SUCCESS_MESSAGE_OF_PRICE_VALUE_UPDATE = 'Price value updated.';
    public static SUCCESS_MESSAGE_OF_PRICE_VALUE_ADDED = 'Price value added.';
    public static SUCCESS_MESSAGE_OF_PRICE_VALUE_REMOVED = 'Price value removed.';

    public static SUCCESS_MESSAGE_OF_ADHESIVE_COVERAGE_VALUE_UPDATE = 'Adhesive coverage value updated.';
    public static SUCCESS_MESSAGE_OF_ADHESIVE_COVERAGE_VALUE_ADDED = 'Adhesive coverage value added.';
    public static SUCCESS_MESSAGE_OF_ADHESIVE_COVERAGE_VALUE_REMOVED = 'Adhesive coverage value removed.';

    // Shown on the Delivery row of a consumables-only quote/order. Consumables are stock items with
    // no freight weight, so they ship free — and the charge has to read as a deliberate $0 rather than
    // the "manual delivery quote required" state a blank rate produces.
    public static FREE_DELIVERY_APPLIED = 'Free Delivery';
    public static FREE_DELIVERY_APPLIED_TOOLTIP = 'Delivery is free because this quote contains consumables only. Consumables are supplied from stock and are not rate-carded, so no delivery quote is calculated for them. A delivery charge can still be entered by hand if one applies.';

    // Why the Products tab is closed off once a quote holds only consumables: on an ordinary quote the
    // consumable quantities are derived from the product quantities, so having both kinds on one quote
    // would put two competing sets of figures for the same goods on the same document.
    public static PRODUCT_ON_CONSUMABLES_QUOTE_BLOCKED = 'This quote contains consumables only. Consumable quantities on a product quote are calculated from the product, so the two cannot be combined - raise a separate quote for products.';
    public static CONSUMABLE_ON_PRODUCT_QUOTE_BLOCKED = 'This quote contains products. Consumable quantities for a product are calculated from the product itself and applied on its line, so standalone consumables cannot be added here - raise a separate quote for consumables.';

    public static CONSUMABLE_CALCULATOR_TYPE_ADHESIVE = 'laticrete_adhesive';
    public static CONSUMABLE_CALCULATOR_TYPE_DEFENDER = 'defender';
    public static CONSUMABLE_CALCULATOR_TYPE_GOLD = 'gold';
    public static CONSUMABLE_CALCULATOR_TYPE_STORAGE_FEES = 'storage_fees';

    public static CONSUMABLE_CALCULATOR_CONSTANTS = {
        TYPE_ADHESIVE: `Laticrete 335 Adhesive`,
        TYPE_DEFENDER: `Actichem CSD Defender`,
        TYPE_GOLD: `Actichem Solv Sealer Gold`,
        // ADDED_TO_QUOTE_SUCCESS_MESSAGE: `Consumable calculator has been added to the quote successfully.`,
        // ADDED_TO_QUOTE_ERROR_MESSAGE: `Consumable calculator has not been added to the quote.`,
        // REMOVED_FROM_QUOTE_SUCCESS_MESSAGE: `Consumable calculator has been removed from the quote successfully.`,
        // REMOVED_FROM_QUOTE_ERROR_MESSAGE: `Consumable calculator has not been removed from the quote.`,
        ADDED_TO_QUOTE_SUCCESS_MESSAGE: `Selected item successfully added to your quote.`,
        ADDED_TO_QUOTE_ERROR_MESSAGE: `Selected item successfully not added to your quote`,
        REMOVED_FROM_QUOTE_SUCCESS_MESSAGE: `Selected item successfully removed from your quote.`,
        REMOVED_FROM_QUOTE_ERROR_MESSAGE: `Selected item successfully not removed from your quote.`,
        ADDED_TO_ORDER_SUCCESS_MESSAGE: `Selected item successfully added to your order.`,
        ADDED_TO_ORDER_ERROR_MESSAGE: `Selected item successfully not added to your order`,
        REMOVED_FROM_ORDER_SUCCESS_MESSAGE: `Selected item successfully removed from your order.`,
        REMOVED_FROM_ORDER_ERROR_MESSAGE: `Selected item successfully not removed from your order.`,
    }

    public static EMAIL_TEMPLATE_TRADE_USER_CARRIER_ADDED_ID = 42;
    public static EMAIL_TEMPLATE_TRADE_USER_CARRIER_ADDED_KEY_NAME = 'trade-user-carrier-added';
    public static JOB_ACCEPTED = 'A';
    public static JOB_DECLINED = 'D';
    public static IS_USED_OWN_TRANSPORT_YES = 'Y';
    public static IS_USED_OWN_TRANSPORT_NO = 'N';
    public static CONFIRMATION_MESSAGE_FOR_MARK_ALL_AS_READ_NOTIFICATION = 'Are you sure you want to mark all as read notifications?';
    public static CONFIRMATION_MESSAGE_FOR_ACCEPT_JOB = 'Are you sure you want to accept job?';
    public static CONFIRMATION_MESSAGE_FOR_DECLINE_JOB = 'Are you sure you want to decline job?';
    public static SUCCESS_MESSAGE_OF_ACCEPT_JOB = 'The job accepted.';
    public static SUCCESS_MESSAGE_OF_DECLINE_JOB = 'The job declined.';
    public static ERROR_MESSAGE_OF_ORDER_FINAL_PAYMENT = 'This order hasn\'t paid final payment.';
    public static ERROR_MESSAGE_OF_ORDER_SITE_CONTACT_DATA_EMPTY = 'Add the site contact information before order Goods despatch process.';
    public static IS_DISPATCH_YES = 'Y';
    public static IS_DISPATCH_NO = 'N';
    public static IS_STOCK_REQUIRED_YES = 'Y';
    public static IS_STOCK_REQUIRED_NO = 'N';
    public static PRODUCT_RECOMMENDED_COLOR_TYPE_WJ = 'WJ';
    public static PRODUCT_RECOMMENDED_COLOR_TYPE_FG = 'FG';
    public static DEPOSIT_PAYMENT_ADJUSTMENT_SUCCESS = 'Deposit payment adjusted.';
    public static DEPOSIT_PAYMENT_ADJUSTMENT_ERROR = 'Deposit payment has not been adjusted.';
    public static DEPOSIT_PAYMENT_ADJUSTMENT_PERCENTAGE_ERROR_0 = 'Number zero is prohibited. enter some value.';
    public static DEPOSIT_PAYMENT_ADJUSTMENT_PERCENTAGE_ERROR_100 = 'Adjust deposit payment value should be 100% only.';
    public static DEPOSIT_PAYMENT_ADJUSTMENT_VALUE_ERROR_0 = 'Number zero is prohibited. enter some value.';
    public static DEPOSIT_PAYMENT_ADJUSTMENT_VALUE_ERROR_100 = 'Adjusted deposit payment value should be less than or equal to the final total.';
    public static APPLIED_CREDIT_ADJUSTMENT_PERCENTAGE_ERROR_0 = 'Number zero is prohibited. enter some value.';
    public static APPLIED_CREDIT_ADJUSTMENT_PERCENTAGE_ERROR_100 = 'Adjusted applied credit value should be 100% only.';
    public static APPLIED_CREDIT_ADJUSTMENT_VALUE_ERROR_0 = 'Number zero is prohibited. enter some value.';
    public static APPLIED_CREDIT_ADJUSTMENT_VALUE_ERROR_100 = 'Adjusted applied credit value should be less than or equal to available credit.';
    public static APPLIED_CREDIT_ADJUSTMENT_VALUE_ERROR_SUBTOTAL = 'Adjusted applied credit value should be less than or equal to the sub total.';
    public static PRODUCT_PRICE_GREATER_THAN_ERROR = 'Product price must be less than or equal to 10000.';
    public static PRODUCT_PERCENTAGE_GREATER_THAN_ERROR = 'Product applied discount should be 100% only.';
    public static IS_ARCHIVED_YES = 'Y';
    public static IS_ARCHIVED_NO = 'N';
    public static IS_DRAFT_YES = 'Y';
    public static IS_DRAFT_NO = 'N';
    public static IS_PAYMENT_DONE = 'Y';
    public static IS_PAYMENT_NOT_DONE = 'N';

    public static ADD_PRODUCT_TO_QUOTE_AFTER_DELIVERY_CHARGE_ADDED = {
        BODY_MESSAGE: 'This quote has already had a manual delivery fee calculated. Proceeding with further edits will require a new delivery fee to be manually applied. Do you still want to continue? ',
    }

    // GC-339 Phase 2: shown when a standard (non-outlet) product is added to an order that is currently
    // all-outlet and already has its SFCD stamped (at the shorter outlet grace). The SFCD is immutable
    // once set, so the admin must update it manually to reflect the standard grace.
    public static ADD_STANDARD_PRODUCT_TO_OUTLET_ORDER = {
        BODY_MESSAGE: 'This order currently contains only outlet stock, which uses a shorter storage-fee grace period. Adding a standard product changes the storage terms, but the Storage Commencement Date has already been set. Please update the Storage Commencement Date (SFCD) after adding this product. Do you want to continue?',
    }
    public static BIG_POST_JOB_ERROR_MESSAGE = 'This address has not delivered by big post.select another carrier.'

    public static UPDATE_PRODUCT_QUANTITY_AFTER_CREDIT_APPLIED = {
        BODY_MESSAGE: 'The quote applied credit will be removed. Are you sure you want update the quantity of this product?',
    }

    public static UPDATE_PRODUCT_QUANTITY_AFTER_CREDIT_APPLIED_MESSAGE(type: string) {
        return `The ${type} applied credit will be removed. Are you sure you want update the quantity of this product?`;
    }

    public static UPDATE_PRODUCT_DISCOUNT_AFTER_CREDIT_APPLIED = {
        BODY_MESSAGE: 'The quote applied credit will be removed. Are you sure you want update the discount of this product?',
    }

    public static UPDATE_AVAILABLE_BALANCE_AFTER_CREDIT_APPLIED = {
        BODY_MESSAGE: 'The quote applied credit will be removed. Are you sure you want update the available balance of this account?',
    }

    public static DELETE_PRODUCT_AFTER_CREDIT_APPLIED = {
        BODY_MESSAGE: 'The quote applied credit will be removed. Are you sure you want delete this product?',
    }

    public static ADD_PRODUCT_AFTER_CREDIT_APPLIED = {
        BODY_MESSAGE: 'The quote applied credit will be removed. Are you sure you want add this product?',
    }

    public static ADD_CONSUMABLE_CALCULATOR_AFTER_CREDIT_APPLIED = {
        BODY_MESSAGE: 'The quote applied credit will be removed. Are you sure you want add this consumable calculator?',
    }

    // Standalone consumable LINE ITEMS added from the Add Item sidebar — distinct from the
    // calculator message above, which is about applying sealer/adhesive to an existing product.
    public static ADD_CONSUMABLE_AFTER_CREDIT_APPLIED = {
        BODY_MESSAGE: 'The quote applied credit will be removed. Are you sure you want to add these consumables?',
    }

    public static REMOVE_CONSUMABLE_CALCULATOR_AFTER_CREDIT_APPLIED = {
        BODY_MESSAGE: 'The quote applied credit will be removed. Are you sure you want removed this consumable calculator?',
    }

    public static EXPORT_QUOTE_INVOICE_URL = environment.hostUrl + '/quote-invoice';
    public static QUOTE_INVOICE_LINK_COPIED = 'Quote/Invoice link copied.'

    public static EXPORT_ORDER_TRACKING_URL = environment.hostUrl + '/track-order';
    public static ORDER_TRACKING_LINK_COPIED = 'Order tracking link copied.'

    public static PRODUCT_DELETED_YES = 1;
    public static PRODUCT_DELETED_NO = 0;

    public static FORk_TRUCK_ID = 1;
    public static HIAB_TRUCK_ID = 2;
    public static FLATAB_TRUCK_ID = 3;
    public static OTHER_TRUCK_ID = 4;
    public static WAREHOUSE_COLLECTION_TRUCK_ID = 5;


    public static LEAD_TIME = 'Available'
    public static LEAD_TIME_AVAILABLE = 'available'


    public static FILTER_AVAILABLE_STRING = [
        'Available',
        'available',
    ];
    public static FILTER_LOW_AVAILABLE_STRING = [
        'Low Stock',
    ];
    public static LEAD_TIME_IN_STOCK = 'In Stock'
    public static LEAD_TIME_LOW_STOCK = 'Low Stock'
    public static APPLY_GLUE_CALCULATOR_YES = 'YES';
    public static APPLY_GLUE_CALCULATOR_NO = 'NO';

    public static PRODUCT_DETAIL_SIZING_TABLE_TOOLTIP = "Lead times are indicative only. Although we take great care to ensure they're kept accurate, stock levels are constantly fluctuating. Our best advice is always to place orders as early as possible, thus reducing the likelihood of supply related delays disrupting your construction schedule.";

    public static STOCK_REQUIRED_DATE_TOOLTIP = `
        <div class="srd-tooltip-content">
            <div class="srd-tooltip-title">Stock Required Date (SRD) Rules:</div>
            <ul>
                <li>SRD cannot be set in the past.</li>
                <li>If the product is <strong>in stock</strong>, SRD can be set to <strong>today or any future date</strong>.</li>
                <li>If the product has a <strong>lead time</strong>, SRD should be selected based on the <strong>lead time requirement</strong>.</li>
                <li>For <strong>split delivery orders</strong>, SRD rules apply per delivery.</li>
            </ul>
            <div class="srd-tooltip-title">Important Notes:</div>
            <ul>
                <li>Validation will be applied when the <strong>quote is accepted</strong> or <strong>first payment is made</strong>.</li>
                <li>If any products have <strong>Lead Time = TBC</strong>, the selected SRD may be impacted once availability is confirmed.</li>
            </ul>
        </div>
    `;

    // Confirmation Already Exist Product
    public static CONFIRMATION_CART_PRODUCT_DELETE_TITLE = 'Delete product';

    public static SHIPPING_RATE_LENGTH = 9999;

    public static QUANTITY_LENGTH = 9999;
    public static QUANTITY_LENGTH_ERROR_MESSAGE = 'Quantity should not be greater than 9999.';
    public static PRODUCT_QUANTITY_LENGTH_ERROR_MESSAGE = 'Enter the product quantity.';
    public static PRODUCT_ADDED_SUCCESSFULLY = 'Product added.';

    public static STATUS_ADJUST_GC_BALANCE = 'AGB';
    public static STATUS_QUOTE_GC_CREDIT = 'QGC';
    public static STATUS_EXPIRED_GC_CREDIT = 'EGC';
    public static STATUS_EXPIRED_GC_DEBIT = 'EGD';
    public static STATUS_DONATED_GC_CREDIT = 'DGC';
    public static STATUS_QUOTE_GC_CREDIT_APPLY = 'QGCA';
    public static STATUS_DEDUCT_EARNED_GC_CREDIT = 'DEQGC';
    public static STATUS_REVERT_QUOTE_GC_CREDIT_FOR_DELETE = 'RQGCFD';
    public static STATUS_REVERT_QUOTE_GC_CREDIT_FOR_RESET = 'RQGCFR';
    public static STATUS_QUOTE_GC_CREDIT_RE_APPLY = 'QGCRA';
    public static STATUS_REVERT_ORDER_GC_CREDIT_FOR_CANCEL = 'RQGCFC';
    public static STATUS_REVERT_ORDER_GC_CREDIT_FOR_STATUS_REVERT = 'RQGCFSR';

    public static QUOTE_ORDER_DELETED_YES = 1;
    public static QUOTE_ORDER_DELETED_NO = 0;

    public static INVALID_URL_ERROR = 'Invalid URL. enter valid url.';

    public static IS_MANUAL_SHIPPING_RATE_CHANGE_YES = true;
    public static IS_MANUAL_SHIPPING_RATE_CHANGE_NO = false;

    public static IS_WAREHOUSE_COLLECTION_YES = true;
    public static IS_WAREHOUSE_COLLECTION_NO = false;

    public static IS_DEFAULT_BUSSINESS_ADDRESS_YES = true;

    public static IS_SALES_REPRESENTATIVE_YES = true;
    public static IS_SALES_REPRESENTATIVE_NO = false;

    public static DISPATCHED_MAIL_TEMPLATE_KEY_NAME = 'trade-user-carrier-added';

    public static PDF_FILE_UPLOAD_ALERT = 'Upload pdf file only.'

    public static ALLOWED_FILE_TYPE_UPLOAD_PDF = ["pdf"];

    public static ALLOWED_UPLOAD_SIZE_MB_PDF = 40;


    public static UPLOAD_PDF = {
        UPLOAD_PDF_TYPE_VALIDATION_MESSAGE: 'Please upload PDF only',
        UPLOAD_PDF_SIZE_VALIDATION: 'Please upload less than 40 MB file'
    }

    public static MULTI_UPLOAD_FILE = {
        UPLOAD_FILE_TYPE_VALIDATION_MESSAGE: 'Only files with the following extensions are allowed: jpg, jpeg, png, heic, heif and pdf.',
        UPLOAD_FILE_TYPE_VALIDATION_NEW_MESSAGE: 'Only files with the following extensions are allowed: jpg, jpeg and pdf.',
        UPLOAD_FILE_TYPE_VALIDATION_MESSAGE_PARTIAL_PAYMENT: 'Only images and PDF files are allowed.',
        UPLOAD_FILE_SIZE_VALIDATION: 'Please upload less than 40 MB file'
    }

    public static UPLOADED_PDF = {
        UPLOAD_PDF_TYPE_MESSAGE: 'PDF Should be uploaded'
    }

    public static PLEASE_SELECT_PDF_FILE = {
        ONLY_UPLOAD_PDF_FILE_MESSAGE: 'Please select PDF file only.'
    }

    public static UPLOAD_PDF_FILE = 1

    //public static LOCALSTORAGE_REMOVE_ARRAY = ['dashboard-bkp','accounts', 'contacts','sample-requests','quotes','orders','products','settings','users','resource','projects','blogs','articles','faq','carrier','email-templates','consumables-calculator'];

    public static LOCALSTORAGE_REMOVE_ARRAY = ['accounts', 'contacts', 'sample-requests', 'quotes', 'orders', 'despatch', 'products', 'tasks'];

    /**
     * Routes whose URL pattern (kebab-case) does not directly match their localStorage
     * key prefix (camelCase). Each entry preserves keys whose name contains `keyPrefix`
     * while the current URL contains `routeSegment`; otherwise those keys are cleared.
     */
    public static LOCALSTORAGE_REMOVE_MAP: { routeSegment: string; keyPrefix: string }[] = [
        { routeSegment: 'pebbl-feedback', keyPrefix: 'pebblFeedbackPage' },
        { routeSegment: 'pebbl-knowledge-feedback', keyPrefix: 'pebblKnowledgeFeedbackPage' },
    ];

    public static OFFLINE_PAYMENT = [
        { ID: 'C', OFFLINE_PAY: 'Cash' },
        { ID: 'BT', OFFLINE_PAY: 'Bank Transfer' },
        { ID: 'CC', OFFLINE_PAY: 'Credit Card' },
        { ID: 'BP', OFFLINE_PAY: 'BPAY' },
        { ID: 'PI', OFFLINE_PAY: 'PayID' },
        { ID: 'PT', OFFLINE_PAY: 'PayTo' },
        { ID: 'UN', OFFLINE_PAY: 'Unknown' }
    ]

    public static ACCOUNT_FILE_UPLOAD = 'Upload image file only.'

    public static DELIVERY_CHARGE = 'Delivery charge should not be greater than 10000.'


    public static IS_DISABLE_EXPIRY_DEADLINE_YES = true;
    public static IS_DISABLE_REACTIVATE_EXPIRY_DEADLINE_NO = 'N';

    public static QUOTE_IS_DISABLE_EXPIRY_DEADLINE_YES_CONFIRMATION_MESSAGE = 'Are you sure you want to disable the expiry deadline of this quote?';
    public static QUOTE_IS_REACTIVE_DISABLE_EXPIRY_DEADLINE_NO_CONFIRMATION_MESSAGE = 'Are you sure you want to enable expiry deadline of this quote?'

    public static QUOTE_SUCCESS_MESSAGE_IS_DISABLE_DEADLINE = 'Quote expiry deadline disabled.';
    public static QUOTE_SUCCESS_MESSAGE_IS_REACTIVE_EXPIRY_DEADLINE = 'Quote expiry deadline enabled.';

    // Credit card availability: the website offers Credit Card for a payment only while the amount being paid
    // (deposit, full sum, final payment, instalment or outstanding balance; before any card surcharge) is strictly
    // under this limit, unless "accept credit card" is switched on for the specific quote/order, which offers it for
    // every payment. One-way override: it can only ADD the option. Mirrors the website and PHP constants.
    public static CREDIT_CARD_ORDER_LIMIT = 2000;
    public static QUOTE_ACCEPT_CREDIT_CARD_YES_CONFIRMATION_MESSAGE = 'At least one scheduled payment on this quote/order is $2,000 or more, so the customer is not offered credit card for it. Accept credit card for every payment on this quote/order anyway?';
    public static QUOTE_ACCEPT_CREDIT_CARD_NO_CONFIRMATION_MESSAGE = 'Stop accepting credit card payment for this quote/order? The customer will no longer see the credit card option.';
    public static QUOTE_SUCCESS_MESSAGE_ACCEPT_CREDIT_CARD_YES = 'Credit card payment accepted for this quote/order.';
    public static QUOTE_SUCCESS_MESSAGE_ACCEPT_CREDIT_CARD_NO = 'Credit card payment no longer accepted for this quote/order.';
    public static NOTE_ACCEPT_CREDIT_CARD_YES = 'Credit card payment accepted for this quote/order by admin (a scheduled payment at/above the credit card limit).';
    public static NOTE_ACCEPT_CREDIT_CARD_NO = 'Credit card payment acceptance removed for this quote/order by admin.';

    public static IS_CONFIRM_STOCK_REQUIRED_DATE_YES = true;
    public static IS_CONFIRM_STOCK_REQUIRED_DATE_NO = false;

    public static FILTER_PREMIUM_SEALER = ['DEFENDER', 'GOLD'];

    public static IS_DISABLE_GPSM_SYNC_YES = 'Y';
    public static IS_DISABLE_GPSM_SYNC_NO = 'N';

    public static ORDER_IS_DISABLE_GPSM_SYNC_CONFIRMATION_MESSAGE = 'Are you sure you want to disable the GPSM sync of this order?';
    public static ORDER_IS_ENABLE_GPSM_SYNC_CONFIRMATION_MESSAGE = 'Are you sure you want to enable GPSM sync of this order?'

    public static ORDER_SUCCESS_MESSAGE_FOR_DISABLE_GPSM_SYNC = 'GPSM sync disabled.';
    public static ORDER_SUCCESS_MESSAGE_FOR_ENABLE_GPSM_SYNC = 'GPSM sync enabled.'
    public static ORDER_SUCCESS_MESSAGE_FOR_UPDATE_GPSM_SHIPMENT_REFERENCE = 'GPSM shipment reference updated.';
    public static UNABLE_EXPIRE_QUOTE_WHEN_DEADLINE_DISABLE = 'Expiry deadline is disabled - enable first.';

    public static UNABLE_ORDER_STATUS_CHANGE_TO_DELIVERED = 'You can’t update the order status due to final payment outstanding.';
    public static UNABLE_ORDER_STATUS_CHANGE_TO_FOLLOW_UP_OR_COMPLETED = 'You can’t update the order status because the order has not been delivered.';

    public static IS_USER_CREATE_FROM_QUOTE = 'Y';
    public static IS_USER_CREATE_FROM_SAMPLE = 'Y';

    public static IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'tiff', 'tif', 'webp', 'heif', 'heic', 'ico'];
    public static DOCUMENT_EXTENSIONS = ['pdf', 'doc', 'docx', 'ppt', 'pptx', 'xls', 'xlsx', 'odt', 'ods', 'odp', 'txt', 'xml', 'csv'];

    public static IS_DEPOSIT_PAYMENT_PROCESSING_YES = 'Y';
    public static IS_DEPOSIT_PAYMENT_PROCESSING_NO = 'N';

    public static IS_FINAL_PAYMENT_PROCESSING_YES = 'Y';
    public static IS_FINAL_PAYMENT_PROCESSING_NO = 'N';

    public static IS_FULL_PAYMENT_PROCESSING_YES = 'Y';
    public static IS_FULL_PAYMENT_PROCESSING_NO = 'N';

    public static IS_DEPOSIT_SLIDER_ADJUST_YES = 'Y';
    public static IS_DEPOSIT_SLIDER_ADJUST_NO = 'N';

    public static IS_DOMESTIC_STOCK_TRANSFER_YES = 'Y';
    public static IS_DOMESTIC_STOCK_TRANSFER_NO = 'N';

    public static CONFIRMATION_MESSAGE_FOR_CONVERT_DRAFT_TO_QUOTE = {
        BODY_MESSAGE: 'Are you sure you want to convert this Draft into quote?',
    }

    public static USER_NOT_SELECT_ERROR_FOR_CONVERT_DRAFT_TO_QUOTE = 'Select any one contact from existing.';
    public static PROJECT_NAME_NOT_ENTER_ERROR_FOR_CONVERT_DRAFT_TO_QUOTE = 'Enter project name.';
    public static PRODUCT_NOT_SELECT_ERROR_FOR_CONVERT_DRAFT_TO_QUOTE = 'Add product.';

    public static QUOTE_ORDER_TOTAL_AMOUNT_FOR_DEPOSIT_SLIDER = 1000;

    public static TASK_CATEGORY = {
        'CC': 'Contact Create',
        'SD': 'Sample Delivered',
        'QC': 'Quote Create',
        'QEI': 'Quote Expiry Immanent',
        'DPD': 'Deposit Payment Due',
        'PP': 'Payment Processing',
        'FPO': 'Final Payment Outstanding',
        'OF': 'Order Followup',
    };

    public static QUOTE_CREATE = 'QC'
    public static CONTACT_CREATE = 'CC'
    public static SAMPLE_DELIVERED = 'SD'
    public static QUOTE_EXPIRY_IMMANENT = 'QEI'
    public static DEPOSIT_PAYMENT_DUE = 'DPD'
    public static PAYMENT_PROCESSING = 'PP'
    public static FINAL_PAYMENT_OUTSTANDING = 'FPO'
    public static ORDER_FOLLOW_UP = 'OF'

    public static TASK_PENDING = 'P'
    public static TASK_COMPLETED = 'C'
    public static TASK_RESCHEDULED = 'R'
    public static TASK_COMPLETED_MESSAGE = 'Task completed.';
    public static TASK_PENDING_MESSAGE = 'Task is pending.';
    public static TASK_RESCHEDULED_MESSAGE = 'Task rescheduled.';
    public static TASK_RESCHEDULE_UPDATED_MESSAGE = 'Rescheduled date updated.';
    public static CONFIRM_RESCHEDULE_PRODUCTION_DELAY = 'Reschedule this task to the new date? Add a note so sales know why production is delayed.';
    public static CONFIRM_COMPLETE_TASK = 'Are you sure you want to complete this task?';
    public static CONFIRM_PENDING_TASK = 'Are you sure you want to revert this task to pending?';
    public static RESCHEDULE = 'reschedule';
    public static STATUS_CHANGES_SUCCESS = 'Assigned user changed.';


    public static UNABLE_SAMPLE_REQUEST_PUSH_BIG_POST_JOB = 'You are not allowed to push the big post job until the status change to "packaged".';

    public static UNABLE_TO_UPLOAD_PAYMENT_REMITTANCE = 'You are not able to upload payment remittance until quote is not accepted.';
    public static UNABLE_TO_UPLOAD_OTHER_PAYMENT_REMITTANCE = 'You cannot upload another payment remittance until your processing payment not verified. If you need any assist contact to administrator.';
    public static ANTICIPATED_WAD_GREATER_ANTICIPATED_PCD = 'Anticipated warehouse arrival date should be greater or equal to Anticipated production completion date.';
    /**
     * Identify task category
     */
    public static TASK_CATEGORIES = {
        'CONTACTS_REVIEW_CONTACT': 1,
        'CONTACTS_WELCOME_MESSAGE': 2,
        'CONTACTS_WELCOME_NEW_CONTACT_CALL': 34,
        'CONTACTS_FOLLOW_UP_NEW_CONTACT': 30, // Canceled task
        'CONTACTS_CONFIRM_RESPONSE': 31, // Canceled task

        'SAMPLE_REQUEST_SEEK_FEEDBACK': 3,
        'SAMPLE_REQUEST_VERIFY_DELIVERY': 18,
        'SAMPLE_REQUEST_SECONDARY_FEEDBACK': 20,
        'SAMPLE_REQUEST_COMPLETE_SAMPLE_REQUEST': 21,
        'SAMPLE_REQUEST_SAMPLE_PROCESSING_DELAY': 54,
        // GC-353: the single active follow-up task a "Samples Delayed" request
        // carries. Distinct from SAMPLE_PROCESSING_DELAY (54), which the v3 API
        // raises on its own when a request lingers at Samples Requested and is
        // NOT tied to an expected-ready date.
        'SAMPLE_REQUEST_FOLLOW_UP_DELAYED_SAMPLES': 70,
        'SAMPLE_REQUEST_LOG_FEEDBACK_DATA': 55,
        'SAMPLE_REQUEST_HAND_DELIVER_SAMPLES_SYD_METRO': 35, // Canceled task

        'QUOTES_OFFER_ASSISTANCE': 4,
        'QUOTES_REQUEST_ADDRESS': 5,
        'QUOTES_QUOTE_DELIVERY': 6,
        'QUOTES_INFORM_CLIENT': 7,
        'QUOTES_CONVERT_SALE': 8,
        'QUOTES_REQUEST_PAYMENT': 9,
        'QUOTES_VERIFY_PAYMENT': 10,
        'QUOTES_LOG_FEEDBACK_DATA': 56,
        'QUOTES_ASSIGN_SPECIFIER': 32,
        'QUOTES_QUOTE_CONVERSION_PENDING': 47,
        'MERGE_STOCK': 41,

        'ORDERS_INSPECT_ORDER': 11, // Canceled task
        'ORDER_CONFIRM_PRODUCTION_COMPLETION': 29, // Canceled task
        'ORDERS_SHIPMENT_DELAYED': 12,
        'ORDERS_CONFIRM_SRD': 13,
        'ORDERS_REQUEST_PAYMENT': 14,
        'ORDERS_VERIFY_PAYMENT': 15,
        'ORDERS_REBOOK_DELIVERY': 16,
        'ORDERS_SEEK_FEEDBACK': 17,
        'ORDER_VERIFY_DELIVERY': 19,
        'ORDER_SECONDARY_FEEDBACK': 22,
        'ORDER_COMPLETE_ORDER': 23,
        'ORDER_RECONCILE_ORDER': 27,
        'ORDER_MERGE_DOMESTIC_STOCK': 36,
        'ORDER_RE_ALLOCATE_STOCK': 40,
        'ORDER_ASSIGN_CREDIT_SPECIFIER': 45,
        'ORDER_MERGE_INTERNATIONAL_STOCK': 46, // Canceled task
        'ORDER_QUOTE_CONVERSION_PENDING': 47,
        'ORDER_REVISE_QUOTE_DELIVERY': 49,
        'ORDER_ADD_GPSM_REFERENCE': 50,
        'STOCK_ALLOCATION_REQUIRED': 51,
        'ORDER_CONTAINER_RE_ALLOCATE_STOCK': 52,
        'ORDER_VERIFY_SPECIFIER': 33, // Canceled task
        'NOMINATE_SPLIT_DELIVERY_SRD': 57,
        'ORDER_QUOTE_SPLIT_DELIVERY': 58,
        'OTHER_STORAGE_FEE_CONFLICT': 59,
        'ORDER_SRD_CONFLICT_PRODUCTION': 60,
        'ORDER_TWO_WEEK_FROM_ARRIVAL': 61,
        'ORDER_REQUEST_FINAL_PAYMENT_SRD': 62,
        'ORDER_REQUEST_FINAL_PAYMENT_STORAGE': 63,
        'ORDER_SRD_CONFLICT_WAREHOUSE': 64,
        'ORDER_SALE_STOCK_IMMEDIATE_DESPATCH': 69,
        'OTHER_OVERPAYMENT_RECEIVED': 48,

        // 'ORDER_UNASSIGNED_STOCK': 39, // TODO: Need to check & delete
        // 'ORDERS_INSPECT_ORDER': 11, // TODO: Need to check & delete
        // 'ORDER_VERIFY_SPECIFIER': 33, // TODO: Need to check & delete
        // 'ORDER_VISIT_SITE_SYD_METRO': 36, // TODO: Need to check & delete
        // 'CONTAINER_ARRIVED': 42, // TODO: Need to check & delete

        'OTHER_BAD_DEBT': 24,
        'OTHER_SRD_CONFLICT': 25, // Canceled task
        'OTHER_SRD_EXPIRED': 26, // Canceled task
        'OTHER_WAREHOUSE_ARRIVAL_CONFLICT': 28, // Canceled task
        'OTHER_CONTACT_CUSTOMER': 37,
        'OTHER_RE_GENERATE_SITEMAP': 38,
        'OTHER_DISCONTINUED_ITEM': 65,
    };
    public static CLIENT_RESPONDED = 1;
    public static CLIENT_NOT_RESPONDED = 0;
    public static RECIPIENT_DETAIL_UPDATE = 'Recipient detail updated.'

    public static RE_ASSIGN_USER = 'reassign'

    public static AUTOMATED_CHANGE_ORDER_STATUS_TOOLTIP = 'Milestone update was automated.';

    public static ORDER_AWAITING_FEEDBACK_NOTE_ID = 37;
    public static SAMPLE_AWAITING_FEEDBACK_NOTE_ID = 20;

    public static SRD_EXPIRED = 'SRD Expired';
    public static SRD_CONFLICT = 'SRD Conflict'
    public static PICKUP_DATE_EXPIRED = 'Pickup Date Expired';
    public static PICKUP_DATE_CONFLICT = 'Pickup Date Conflict';

    public static GOODS_DISPATCHED_MILESTONE_STATUS_ARRAY = ['DUNCONF', 'DOTT', 'DCONF', 'DCO', 'DD', 'DEL', 'AF', 'C', 'OCAN'];

    // Link Payment Receipt Messages
    public static PAYMENT_RECEIPT_LINKED_SUCCESS_MESSAGE = 'Payment receipt linked.';
    public static PAYMENT_STATUS_INCOMPLETE_ERROR_MESSAGE = "Receipt can't be linked until the payment is completed.";
    public static RECEIPT_ALREADY_LINKED_TO_PAYMENT_ERROR_MESSAGE = 'Payment receipt has already been linked to this payment.';
    public static DIFFERENT_RECEIPT_LINKED_ERROR_MESSAGE = 'Payment receipt has already been linked. select a different receipt.';
    public static RECEIPT_ALREADY_LINKED_ERROR_MESSAGE = 'Payment receipt has already been linked.';

    // Add Comment Message
    public static ADD_COMMENT_SUCCESS_MESSAGE = 'Comment added.';
    public static UPDATE_COMMENT_SUCCESS_MESSAGE = 'Comment updated.';
    public static NO_COMMENTS_AVAILABLE_ERROR_MESSAGE = 'No comments are available.';

    // Notification Message
    public static ALL_NOTIFICATION_MARKED_AS_READ_SUCCESS_MESSAGE = 'All notifications marked as read.';
    public static NOTIFICATION_MARKED_AS_READ_SUCCESS_MESSAGE = 'Notification marked as read.';
    public static NOTIFICATION_MARKED_AS_UNREAD_SUCCESS_MESSAGE = 'Notification marked as unread.';

    // Notification Status
    public static NOTIFICATION_STATUS_ACTIVE = 1;
    public static NOTIFICATION_STATUS_INACTIVE = 0;

    public static QUOTE_ORDER_STATUS_DROPDOWN_USED_IN = 'T';
    public static ASSIGNED_TO_AND_COMPLETED_BY_USER_DROPDOWN_USED_IN = 'T';

    public static EMPTY_PARAGRAPH_HTML = '<p><br></p>';

    public static NOTE_NOTIFICATION = 'N';
    public static QUOTE_PRODUCT_NOTIFICATION = 'QP';


    // Sample Image Message
    public static CAPTURE_IMAGE_UPLOAD_SUCCESS_MESSAGE = 'Captured image uploaded.';
    public static NO_CAMERA_ERROR_MESSAGE = 'No camera found. ensure a camera is connected and try again.';
    public static CAMERA_ACCESS_DENIED_ERROR_MESSAGE = 'Camera Access Denied. Try again by approving the access.';
    public static UNSUPPORTED_BROWSER_ERROR_MESSAGE = 'Unsupported Browser. use a modern browser.';
    public static PERMISSION_DENIED_ERROR_MESSAGE = 'Permission denied';
    public static PACKED_QUANTITY_EXCEEDS_MESSAGE = 'Packed quantity should be less than or equal to required quantity.'
    public static SAMPLE_IMAGE_DELETE_SUCCESSFULLY = 'Sample image deleted.';
    public static QUANTITY_MUST_BE_GREATER_THAN_ZERO_ERROR_MESSAGE = 'Quantity must be greater than 0.';

    public static VALID_QUANTITY_ERROR_MESSAGE = 'Enter a valid quantity.';
    public static INVALID_DATE_ERROR_MESSAGE = 'Invalid Date';
    public static VALID_DATE_ERROR_MESSAGE = 'Enter a valid date.';

    public static DELETE_SAMPLE_REQUEST_IMAGE_CONSTANTS = {
        TITLE: 'Delete Sample Image',
        BODY_MESSAGE: 'Are you sure you want delete this sample image?',
        NO: "No",
        YES: "Yes"
    }
    // Tender Messages
    public static UPDATE_QUOTE_CONFIRMATION = {
        TITLE: 'Update Tender Totals',
        BODY_MESSAGE: 'Quantities in this quote differ from the master tender. Would you like these revised totals to be applied to all existing quotes under this tender?',
        NO: "No",
        YES: "Yes"
    }

    public static TENDER_UPDATE_ALL_SUCCESS = 'All existing tenders are updated.';
    public static TENDER_PROMOTED_TO_PRIMARY_SUCCESS_MESSAGE = 'The secondary tender set as the primary tender.';
    public static TENDER_UPDATE_ALL_FAILURE = 'Failed to update existing tenders.';
    public static TENDER_STATUS_PRIMARY = 'new';
    public static TENDER_STATUS_SECONDARY = 'existing';
    public static TENDER_TYPE_PRIMARY = true;
    public static TENDER_TYPE_SECONDARY = false;
    public static TENDER_SELECTION_ACTIVE = 1;
    public static TENDER_SELECTION_INACTIVE = 0;
    public static TENDER_FILTER_ACTIVE = true;

    public static AVERAGE_DELIVERED_TIME = 'avg_delivered_time';

    public static IS_ACCEPTED_YES = 'Y';
    public static IS_ACCEPTED_NO = 'N';

    // Scan Tracking Number Message
    public static ADD_TRACKING_NUMBER_SUCCESS_MESSAGE = 'Scanned tracking number added.';
    public static VIDEO_INPUT = 'videoinput';
    public static NOT_ALLOWED_ERROR = 'NotAllowedError';
    public static PERMISSION_DENIED_ERROR = 'PermissionDeniedError';
    public static SAMPLE_PRODUCT_IMAGE = 'SPI';
    public static SAMPLE_FORMAT_HERO_IMAGE = 'SFHI';
    public static PRODUCT_IMAGE = 'PI';
    public static FORMAT_HERO_IMAGE = 'FHI';
    public static PRODUCT_IMAGES = 'Images';
    public static SAMPLE_PRODUCT_IMAGES = 'Sample'
    public static METRO_COUPON_VALUE = 15.50;
    public static LINK_COUPON_VALUE = 5.50;
    public static COUPON_QUANTITY_GREATER_THAN_ZERO = "Number of coupon quantity must be greater than zero.";
    /* The GoLogistics COUPON ROLLS, and nothing else. 1 and 2 are the Metro
       roll, 9 is the Link roll — which is why the two `..._VALUE` prices above
       are read off the same first digit. No other carrier's consignment is
       numbered this way and no server rule has ever required it: both APIs
       store `tracking_number` as a plain varchar(100) with no format check, so
       anywhere this is applied outside GoLogistics it is refusing a number the
       carrier itself issued. */
    public static METRO_COUPON_START = '1';
    public static ANOTHER_METRO_COUPON_START = '2';
    public static LINK_COUPON_START = '9';
    public static TRACKING_NUMBER_STARTS_WITH = "A GoLogistics sticker number starts with 1 or 2 (Metro) or 9 (Link).";
    public static DELETE_TRACKING_NUMBER_SUCCESS_MESSAGE = "Tracking number deleted.";

    // Scan Sample Product Sku Message
    public static INVALID_SKU_MESSAGES = {
        TITLE: 'Invalid SKU',
        BODY_MESSAGE: 'Entered sample SKU is Invalid. Please try again',
        NO: "No",
        YES: "Yes"
    };
    public static VALID_SKU_MESSAGES = {
        TITLE: 'Confirm Add Product',
        BODY_MESSAGE: "The entered SKU is valid, but it's not currently part of the sample request. Would you like to add it to this request?",
        NO: "No",
        YES: "Yes"
    };
    public static INVALID_SKU_ERROR_MESSAGE = 'Invalid SKU. Try again.';
    public static ADD_PRODUCT_DEFAULT_PACKED_QUANTITY = 1

    // Split Delivery Message
    public static ERROR_MESSAGE_ZERO_QUANTITY_PRODUCTS = "You cannot split sample request as product have zero packed quantity.";

    // Logistic Type Id's
    public static LOGISTIC_TYPE_IDS = {
        GO_LOGISTIC: 2,
        BIGPOST: 1,
        AUS_POST: 3,
        OTHER: 4
    };

    public static SAMPLE_PACKAGES_STATUS_ID = 17;
    public static SAMPLE_AWAITING_FEEDBACK_STATUS_ID = 20;
    public static SAMPLE_COMPLETED_STATUS_ID = 21;
    public static SAMPLE_STATUS_COMPLETE_ID = 21;
    public static PREVIEW_LABEL_PDF_TITLE = 'Preview PDF Label';
    public static PACKAGE_DETAIL_UPDATE_SUCCESS_MESSAGE = 'Package detail updated.'
    public static CUSTOM_SAMPLE_PACKING_BOX_ID = 6;

    /** Why a contact's FIRST sample request went out without the GatherCo sample
     *  bag (`quote_logistic.sample_bag_skip_reason`). api-v2 validates against its
     *  own copy (`SAMPLE_BAG_SKIP_REASONS` in configConstants.ts) - keep the two
     *  in step. "Other" makes the note required. */
    public static SAMPLE_BAG_SKIP_REASONS: ReadonlyArray<string> = [
        'Out of sample bags',
        'Bag does not fit in the box',
        'Customer already has one',
        'Customer asked not to include it',
        'Other',
    ];
    public static SAMPLE_BAG_SKIP_REASON_OTHER = 'Other';
    public static SAMPLE_BAG_SKIP_NOTE_MAX_LENGTH = 1000;
    public static SAMPLE_BAG_REASON_RECORDED = 'Reason recorded';
    public static SAMPLE_BAG_REASON_RECORDED_DETAIL = 'Packing can go ahead without the sample bag.';

    // Product Row Drag And Drop Message
    public static PRODUCT_ORDER_SEQUENCE_UPDATE_SUCCESS_MESSAGE = "Product sequence updated.";

    // Sku Copied Message
    public static SKU_COPIED = 'Sku copied!';

    // For flooring and walling type
    public static FLOORING_TYPE_NAME = 'flooring';
    public static WALLING_TYPE_NAME = 'walling';
    public static FLOORING_TYPE_ID = 1;
    public static WALLING_TYPE_ID = 2;
    public static IMAGE_SIZE_38_X_38 = '?tr=w-38,h-38';

    public static SAMPLE_LIST_VIEW_LIMIT = 12;

    public static WASTAGE_TOOLTIP = 'To guarantee sufficient material for your project, we recommend adding an additional 15% to account for potential cuts and unexpected breaks or splits during installation to the listed available quantity.'

    public static PAYMENT_MODE_CASH_SHORT = 'C';
    public static PAYMENT_MODE_ONLINE_SHORT = 'ON';
    public static PAYMENT_MODE_BANK_TRANSFER_SHORT = 'BT';
    public static PAYMENT_MODE_CREDIT_CARD_SHORT = 'CC';
    public static PAYMENT_MODE_UNKNOWN_SHORT = 'UN';
    public static PAYMENT_MODE_BPAY_SHORT = 'BP';
    public static PAYMENT_MODE_PAYID_SHORT = 'PI';

    public static ACTIVITY_TYPES = {
        'ACCOUNT_CREATE': 1,
        'ACCOUNT_UPDATE': 2,
        'ACCOUNT_APPROVE': 3,
        'ACCOUNT_SUSPEND': 4,
        'ACCOUNT_RESUME': 5,
        'ACCOUNT_BUSINESS_ADDRESS_ADD': 6,
        'ACCOUNT_BUSINESS_ADDRESS_SET_DEFAULT': 7,
        'ACCOUNT_BUSINESS_ADDRESS_DELETE': 8,
        'ACCOUNT_INTERNAL_NOTE_ADD': 9,
        'ACCOUNT_INTERNAL_NOTE_UPDATE': 10,
        'ACCOUNT_INTERNAL_NOTE_DELETE': 11,
        'ACCOUNT_GATHERCO_CREDIT_ADJUST': 12,
        'CONTACT_CREATE': 13,
        'CONTACT_UPDATE': 14,
        'PROFILE_UPDATE': 15,
        'CONTACT_SUSPEND': 16,
        'SEND_PASSWORD_RESET': 17,
        'CONTACT_BUSINESS_ADDRESS_SET_DEFAULT': 19,
        'CONTACT_HOME_ADDRESS_ADD': 21,
        'CONTACT_HOME_ADDRESS_SET_DEFAULT': 22,
        'CONTACT_HOME_ADDRESS_DELETE': 23,
        'CONTACT_INTERNAL_NOTE_ADD': 24,
        'CONTACT_INTERNAL_NOTE_UPDATE': 25,
        'CONTACT_INTERNAL_NOTE_DELETE': 26,
        'SAMPLE_QUOTE_ORDER_CREATE': 27,
        'SAMPLE_QUOTE_ORDER_UPDATE': 28,
        'SAMPLE_QUOTE_ORDER_DELETE': 29,
        'SAMPLE_QUOTE_ORDER_ARCHIVE': 30,
        'SAMPLE_QUOTE_ORDER_CLONE': 31,
        'QUOTE_ORDER_CANCEL': 32,
        'QUOTE_ORDER_CLOSED': 96,
        'PRODUCT_ADD': 33,
        'PRODUCT_DETAIL_UPDATE': 34,
        'SAMPLE_QUOTE_ORDER_CONTACT_UPDATE': 35,
        'PROJECT_DETAIL_UPDATE': 36,
        'DELIVERY_DETAIL_UPDATE': 37,
        'STATUS_UPDATE': 38,
        // GC-353 — the delay's own timeline rows. 101 is written by the
        // diff-based classifier when `quote.expected_ready_date` moves; 102/103
        // are written explicitly when the follow-up task is resolved, and carry
        // a null `status_id` so they file under "system" rather than "status".
        'SAMPLE_EXPECTED_READY_DATE_CHANGE': 101,
        'SAMPLE_DELAY_TASK_COMPLETED': 102,
        'SAMPLE_DELAY_TASK_CANCELLED': 103,
        // The stage's customer email, recorded once it has SETTLED — sent,
        // failed, or deliberately skipped. Written by
        // `POST /activity/customer-email`, which the console calls from its
        // email preview because two of the three outcomes have no server-side
        // event at all: Skip closes the dialog and calls nothing, and the send
        // itself goes to the v3 PHP API, which writes no activity. All three
        // carry a null `status_id`, so they file under the "system" kind
        // beside the status move rather than duplicating it.
        'CUSTOMER_EMAIL_SENT': 104,
        'CUSTOMER_EMAIL_FAILED': 105,
        'CUSTOMER_EMAIL_SKIPPED': 106,
        // The "Help us understand why" answers, written by api-v2's
        // FeedbackAnswerService as ONE row per submission. The reasons
        // themselves ride in `new_value`, because a question can be reworded
        // later and this row has to keep saying what was chosen on the day.
        'NOT_CONVERTED_REASON': 107,
        // A record linked to / unlinked from another (Linked Records), written by
        // api-v2's LinkQuoteOrderService as one row on EACH record of the pair.
        // The other record's id, number and type ride in `new_value`.
        'RECORD_LINKED': 109,
        'RECORD_UNLINKED': 110,
        // A converted sample request's linked quote/order was changed from the
        // Conversion card; `new_value` freezes {from, to} as {type, number}.
        'SAMPLE_CONVERSION_CHANGED': 111,
        // Failed to Reach Destination. 112 is the reason edited on a failed
        // request (composed from new_value); 113/114 are the follow-up tasks it
        // closed and the Verify Delivery task its undo reopened — their stored
        // description is the finished sentence, as for the delay's 102/103.
        'SAMPLE_FAILED_REASON_CHANGE': 112,
        'SAMPLE_FAILED_TASKS_CLOSED': 113,
        'SAMPLE_FAILED_TASK_REOPENED': 114,
        'INTERNAL_NOTE_ADD': 39,
        'INTERNAL_NOTE_UPDATE': 40,
        'INTERNAL_NOTE_DELETE': 41,
        'DEPOSIT_PAID': 42,
        'FINAL_PAYMENT_PAID': 43,
        'FULL_PAYMENT_PAID': 44,
        'PARTIAL_PAYMENT_PAID': 45,
        'STOCK_REQUIRED_DATE_CHANGE': 46,
        'STOCK_REQUIRED_DATE_CONFIRM': 47,
        'SAMPLE_REQUEST_SPLITTED': 48,
        'ACCOUNT_DECLINE': 49,
        'QUOTE_REACTIVE': 50,
        'CONTACT_RESUME': 51,
        'ACCOUNT_BUSINESS_ADDRESS_UPDATE': 52,
        'CONTACT_REGISTER': 53,
        'CONTACT_HOME_ADDRESS_UPDATE': 54,
        'PRODUCT_DELETE': 55,
        'SAMPLE_QUOTE_ORDER_UNARCHIVE': 56,
        'PRODUCT_FAVOURITE_ADD': 57,
        'PRODUCT_FAVOURITE_REMOVE': 58,
        'STOCK_REQUIRED_DATE_UNCONFIRM': 59,
        'ORDER_REVIEW': 60,
        'SITE_CONTACT_ADD': 61,
        'SITE_CONTACT_UPDATE': 62,
        'RECIPIENT_DETAIL_ADD': 63,
        'RECIPIENT_DETAIL_UPDATE': 64,
        'QUOTE_DRAFT': 65,
        'EXTERNAL_NOTE_ADD_ID': 66,
        'EXTERNAL_NOTE_UPDATE_ID': 67,
        'EXTERNAL_NOTE_DELETE_ID': 68,
        'CONTAINER_CREATE_ID': 69,
        'CONTAINER_UPDATE_ID': 70,
        'CONTAINER_DELETE_ID': 71,
        'CONTAINER_PRODUCT_ADD_ID': 72,
        'CONTAINER_PRODUCT_DETAIL_UPDATE_ID': 73,
        'CONTAINER_PRODUCT_DELETE_ID': 74,
        'MOVE_TO_UNASSIGNED_STOCK_ID': 75,
        'MOVE_TO_STOCK_ON_HAND_ID': 76,
        'MOVE_TO_CONTAINER_ID': 77,
        'SAMPLE_QUOTE_ORDER_ADDRESS_CREATE': 78,
        'SAMPLE_QUOTE_ORDER_ADDRESS_UPDATE': 79,
        'SAMPLE_QUOTE_ORDER_ADDRESS_DELETE': 80,
        'SAMPLE_QUOTE_ORDER_SUMMARY_CREATE': 81,
        'SAMPLE_QUOTE_ORDER_SUMMARY_UPDATE': 82,
        'REFUND_OVERPAID_AMOUNT': 83,
        'SAMPLE_LOGISTIC_CREATE': 84,
        'SAMPLE_LOGISTIC_UPDATE': 85,
        'SCHEDULE_DELIVERY_CREATE': 86,
        'SCHEDULE_DELIVERY_UPDATE': 87,
        'SCHEDULE_DELIVERY_DELETE': 88,
        'SCHEDULE_DELIVERY_SITE_CONTACT_ADD': 89,
        'SCHEDULE_DELIVERY_SITE_CONTACT_UPDATE': 90,
        'PRODUCT_STOCK_REQUIRED_DATE_CHANGE': 91,
        'PRODUCT_STOCK_REQUIRED_DATE_CONFIRM': 92,
        'PRODUCT_STOCK_REQUIRED_DATE_UNCONFIRM': 93,
        'SITE_CONTACT_DELETE': 94,
        'SCHEDULE_DELIVERY_SITE_CONTACT_DELETE': 95,
        // Add GPSM Reference task rescheduled: the container's In Production delay, with its note.
        'CONTAINER_PRODUCTION_DELAY_ID': 108,
    };

    public static PERFORM_FROM_WEBSITE = 1;
    public static PERFORM_FROM_ADMIN = 2;
    public static PERFORM_FROM_CRONJOB = 3;

    public static SOCIAL_MEDIA_URLS = {
        FACEBOOK: 'https://www.facebook.com/gathercoaustralia/',
        INSTAGRAM: 'https://www.instagram.com/gathercoaustralia/',
        PINTEREST: 'https://www.pinterest.com.au/gathercoaus/'
    };

    public static FRONTEND_LINKS = {
        FRONTEND_URL: environment.hostUrl,
        PRIVACY_POLICY: `${environment.hostUrl}/privacy-policy`,
        TERMS_AND_CONDITIONS: `${environment.hostUrl}/terms-and-conditions`,
        CONTACT_US: `${environment.hostUrl}/contact`
    }
    public static IMAGE_KIT_ENDPOINT = environment.imageKitEndpoint;

    public static EMAIL_TEMPLATE_IMAGES = {
        WALLING_IMAGE: `${environment.emailImageUrl}walling.png`,
        FLOORING_IMAGE: `${environment.emailImageUrl}flooring.png`,
        RESOURCE_HUB_ICON: `${environment.emailImageUrl}resource-hub-icon.png`,
        SUPPORT_ICON: `${environment.emailImageUrl}support-icon.png`,
        REWARDS_ICON: `${environment.emailImageUrl}rewards-icon.png`,
        SAMPLE_ICON: `${environment.emailImageUrl}samples-icon.png`
    }

    public static DEFAULT_ADDRESS_NOT_FOUND = 'Default address not found.';
    public static DELIVERY_ADDRESS_NOT_FOUND = 'Delivery address not found.';

    public static DEfAULT_ADDRESS_NOT_FOUND = 'No default address available. Set a default address before proceeding.';

    public static UNIT_OF_MEASUREMENT_SQM = 'SQM';
    public static UNIT_OF_MEASUREMENT_LM = 'LM';
    public static PIECE_CONSTANT_LONG_NAME = 'PIECE'
    public static TILES_CONSTANT_LONG_NAME = 'TILES'
    public static UNIT_OF_MEASUREMENT_PCS = 'PCS'

    public static CONFIRMATION_MESSAGE_DISABLED_CREDIT = "Are you sure you want to disable credit earn for this account?";
    public static CONFIRMATION_MESSAGE_ENABLED_CREDIT = "Are you sure you want to enable credit earn for this account?";

    public static SUCCESS_MESSAGE_DISABLED_EARN_CREDIT = "Credit Earn disabled.";
    public static SUCCESS_MESSAGE_ENABLED_EARN_CREDIT = "Credit Earn enabled.";

    public static STOCK_ALLOCATION_CONSTANTS = {
        MERGE_CONFIRM: 'Are you sure you want to merge this container in your stock on hand?',
        CONTAINER_EDIT: 'Container has been updated successfully.',
        CONTAINER_ADD: 'Container has been added successfully.',
        CONTAINER_MERGE: 'Container has been merged successfully.',
        PRODUCT_DRAG_DROP: 'Product has been moved successfully.',
        LESS_AVAILABLE_QTY_ERROR: 'This product cannot be moved as the available quantity is insufficient.',
        CONTAINER_DELETE_CONFIRM: 'Are you sure you want to delete this container?',
        CONTAINER_DELETE: 'Container has been deleted successfully.',
        MERGE_CONFIRM_NOTE: 'This action will merge all products that belong to this container.',
        CONTAINER_SYNC_PO: 'Containers synced successfully.',
        NEW_PO: 'new',
        EXISTING_PO: 'existing',
        SYNC_NEW_PO_TITLE: 'Sync New POs',
        SYNC_NEW_PO_CONFIRM: 'Are you sure you want to sync the new placed POs from Unleashed?',
        SYNC_EXISTING_PO_TITLE: 'Sync Existing POs',
        SYNC_EXISTING_PO_CONFIRM: 'Are you sure you want to sync the existing POs from Unleashed?',
        SYNC_PO_FAILED: 'Failed to sync PO containers. Please try again.',
        MERGE_CONTAINERS_SUCCESS: 'Containers have been merged successfully.',
        MERGE_CONTAINERS_FAILED: 'Something went wrong while merging the containers. Please try again.',
        SELECT_FILTER_MESSAGE: 'Please select the filter option',
        NO_RECORDS_FOUND: 'No records found',
    }

    public static STOCK_ALLOCATION_STATUSES = {
        IP: 'In Production',
        IT: 'In Transit',
        CH: 'Customs Held',
        AAW: 'Available At Warehouse',
        DFT: 'Draft',
        ARCH: 'Archive',
        DST: 'Domestic Stock Transfer'
    }
    public static ENABLED_EARN_CREDIT = 0
    public static DISABLED_EARN_CREDIT = 1
    public static ENABLED_EARN_CREDIT_TEXT = 'Enabled GatherCo Credit Earn'

    public static CREATE_EXTERNAL_NOTES = 66
    public static UPDATE_EXTERNAL_NOTES = 67
    public static SELECT_VALID_CREDIT_SPECIFIER_ERROR_MESSAGE = "Select a valid credit specifier user before proceeding.";

    public static VIDEO_SIZE_EXCEEDS = "File size exceeds 200MB. select a smaller video."
    public static VIDEO_INVALID_FILE_TYPE = "Invalid file type. select a video."

    public static FAQ_CATEGORY_CONSTANTS = {
        FAQ_CATEGORY_ADD_SUCCESSFULLY: 'FAQ category has been added successfully.',
        FAQ_CATEGORY_EDIT_SUCCESSFULLY: 'FAQ category has been updated successfully.',
        FAQ_CATEGORY_DELETE_SUCCESSFULLY: 'FAQ category has been deleted successfully.',
        FAQ_CATEGORY_CONFIRM_DELETE_MESSAGE: 'Are you sure you want to delete this FAQ category?',
    }

    public static FAQ_CATEGORY_EDIT_FORM_CATEGORY = {
        MAIN_HEADING: 'Edit FAQ Category',
        LABEL: 'FAQ Category Name',
        REQUIRED_ERROR_MESSAGE: 'Please enter FAQ category name'
    }

    public static FAQ_CATEGORY_FORM_CATEGORY = {
        MAIN_HEADING: 'Add FAQ Category',
        LABEL: 'FAQ Category Name',
        REQUIRED_ERROR_MESSAGE: 'Please enter FAQ category name'
    }

    public static FAQ_VIDEO = {
        DELETE_CONFIRM: 'Are you sure you want to delete this video?',
        DELET_VIDEO: 'Video has been deleted successfully'
    }
    public static SENT_EMAIL_HISTORY = 'Sent Email History';
    public static BLOG_SAVED_AS_DRAFT_SUCCESSFULLY = 'Blog saved as draft.';
    public static PROJECT_SAVED_AS_DRAFT_SUCCESSFULLY = 'Project saved as draft.';

    public static BLOG_PROJECT_STATUS = {
        DRAFT: 'D',
        PUBLISHED: 'P'
    };

    public static BLOG_PROJECT_STATUS_LABEL = {
        DRAFT: 'Draft',
        PUBLISHED: 'Published'
    };

    public static TYPE_TO_SELECTED_KEY_MAP: Record<string, string> = {
        CONTENT: 'content',
        VIDEO: 'video',
        IMAGES: 'images',
        RENDER_SERVICES: 'render_services',
        IMAGE_CONTENT: 'image_content',
        FEATURED_PRODUCT: 'featured_product'
    };

    public static SECTION_TYPES = [
        { key: 'content', label: 'Text', type: 'CONTENT' },
        { key: 'images', label: 'Images', type: 'IMAGES' },
        { key: 'render_services', label: 'Render Services', type: 'RENDER_SERVICES' },
        { key: 'featured_product', label: 'Feature Product', type: 'FEATURED_PRODUCT' },
        { key: 'image_content', label: 'Images + Content', type: 'IMAGE_CONTENT' },
        { key: 'video', label: 'Video', type: 'VIDEO' }
    ];

    public static CONTENT_TYPES = {
        CONTENT_TYPES_VIDEO: 'VIDEO',
        CONTENT_TYPES_IMAGES: 'IMAGES',
        CONTENT_TYPES_IMAGE_CONTENT: 'IMAGE_CONTENT',
        CONTENT_TYPES_FEATURED_PRODUCT: 'FEATURED_PRODUCT'
    }

    public static ORDER_INDEX_CHANGE_SUCCESS = 'The project sequence updated.';
    public static ORDER_INDEX_CHANGE_FAILED = 'Failed to update the project content order.';

    public static PROJECT_SECTION = {
        DELETE_CONFIRM: 'Are you sure you want to delete this section?',
        DELET_VIDEO: 'Section has been deleted successfully',
        CONFIRM_TITLE: 'Confirm Delete Section'
    }

    public static PROJECT_VIDEO = 'project_video';
    public static PROJECT_IMAGES = 'image';

    public static FORM_KEYS = {
        NAME: 'name',
        META_TITLE: 'meta_title',
        CATEGORY_ID: 'project_category_id',
        DESCRIPTION: 'description',
        META_DESCRIPTION: 'meta_description',
        DISPLAY_AT_HOME_PAGE: 'is_display_at_home_page',
        DISPLAY_AT_HOME_PAGE_YES: 'Y',
        DISPLAY_AT_HOME_PAGE_NO: 'N',
        MEET_THE_MAKERS_HOME_PAGE: 'is_display_at_meet_the_makers',
        MEET_THE_MAKERS_HOME_PAGE_YES: 'Y',
        MEET_THE_MAKERS_HOME_PAGE_NO: 'N',
        VIDEO_LINK: 'video_link',
        STATUS: 'status',
        FEATURED_IMAGE: 'featured_image',
        ID: 'id',
        PROJECT_ID: 'project_id',
        LOCATION: 'location',

        // keys related to saveProjectSections
        TYPE: 'type',
        INDEX: 'index',
        TITLE: 'title',
        CONTENT: 'content',
        BACKGROUND_COLOR: 'background_color',
        TEXT_COLOR: 'text_color',
        VIDEO_PATH: 'video_path',
        IMAGE: 'image_1',
        IMAGE_2: 'image_2',
        IMAGE_3: 'image_3',
        DIRECTION: 'direction',
        LEFT_TO_RIGHT: 'LTR',
        RIGHT_TO_LEFT: 'RTL',
        FORM_DATA: 'data',
        PROJECT_IDS: 'product_ids',
    };

    public static DIRECTIONS = {
        MOVE_UP: 'up',
        MOVE_DOWN: 'down'
    }

    public static CONTENT_IMAGE_DELETE_SUCCESS = 'Content image deleted.';
    public static CONTENT_IMAGE_DELETE_FAILED = 'Failed to delete content image.';
    public static readonly WRONG_EXTENSION_MESSAGE = 'Only files with these extensions are allowed: png, jpg, jpeg, gif.';
    public static MEET_THE_MAKERS_CATEGORY_ID = 23;
    public static ORIGINS_CATEGORY_ID = 20;
    public static ERROR_MESSAGE_SLUG_NOT_FOUND = 'The requested project not be found. check the slug and try again.';
    public static ERROR_MESSAGE_FOR_VIDEO_SECTION = `Youtube Link is required for 'Video' section type.`;
    public static ERROR_MESSAGE_FOR_RENDER_SERVICE_SECTION = `Content is required for 'Render Service' section type.`;
    public static ERROR_MESSAGE_FOR_FEATURED_PRODUCT_SECTION = `Please select any product for 'Feature product' section type.`;
    public static ERROR_MESSAGE_FOR_IMAGE_CONTENT_SECTION = `Image is required for 'Image + Content' section type.`;
    public static ERROR_MESSAGE_FOR_TEXT_CONTENT_SECTION = `Content or title is required fro text section.`;
    public static ERROR_MESSAGE_FOR_MEET_THE_MAKERS_VIDEO = `Unable to mark this project as 'Meet the Makers'. Please add a YouTube video link first.`;

    public static SAVE_AS_DRAFT_TITLE = 'Save Draft'
    public static SAVE_AS_DRAFT_MESSAGE = 'Would you like to save this record as a draft?'
    public static LOGOUT_CONFIRMATION_TITLE = 'Logout'
    public static LOGOUT_CONFIRMATION_MESSAGE = 'Are you sure you want to log out of your account?'
    public static LOGOUT_CONFIRMATION_BUTTON = 'Logout'

    public static EXTERNAL_NOTES_ICON_INFORMATION = 'These notes will be displayed on the client facing version of this quote.'
    public static INTERNAL_NOTES_ICON_INFORMATION = 'These notes are not visible to the customer. Use this text box to detail important information relevant to this record.'

    public static IS_TRUE = true;
    public static IS_FALSE = false;

    public static IS_Y = 'Y';
    public static IS_N = 'N';

    public static IS_YES = 'YES';
    public static IS_NO = 'NO';

    public static THREAD_NOTES = {
        REQUIRED_FIELD_MESSAGE: 'Please enter message.',
        ERROR_MESSAGE: 'Failed to add note. Please try again.',
        ADDED_SUCCESSFULLY: 'Thread note added successfully.',
        DELETED_SUCCESSFULLY: 'Thread note deleted successfully.',
        UPDATE_SUCCESSFULLY: 'Thread note updated successfully.',
        BODY_MESSAGE: 'This thread note will be permanently removed. This can’t be undone.'
    }

    public static CUSTOME_SAMPLE_BOX_id = 6;
    public static NO_PRODUCT_ADDED = 'This delivery has no products added.';

    public static ORDER_STATUS_ARRAY_ID = {
        DFT: 1,
        IP: 24,
        DST: 27,
        AAW: 29
    }

    public static CONFIRMATION_QUOTE_SITE_CONTACT_DELETE_TITLE = 'Delete site contact';
    public static QUOTE_SITE_CONTACT_REMOVE = "Site contact deleted.";

    public static STATUS_DELIVERY_UNCONFIRMED_ID = 31;
    public static STATUS_DELIVERY_OUT_TO_TENDER_ID = 32;
    public static STATUS_DELIVERY_DESPATCHED_ID = 35;
    public static STATUS_DELIVERY_CONFIRMED_ID = 33;
    public static STATUS_DELIVERY_CLIENT_ORGANIZED_ID = 34;
    public static STATUS_DELIVERD_ID = 36;
    public static STATUS_AWAITING_FEEDBACK_ID = 37;
    public static STATUS_COMPLETED_ID = 38;

    public static ORDER_STATUS_DISPLAY_FOR_DESPATCH = 'despatch';

    public static ORDER_SYNC_FROM_UNLEASHED_SUCCESSFULLY_MESSAGE = 'Order synced from unleashed.';

    public static QUOTE_SYNC_FROM_UNLEASHED_SUCCESSFULLY_MESSAGE = 'Quote synced from unleashed.';

    public static SAMPLE_REQUEST_CLOSE = 'sample_request_close';
    public static QUOTE_CLOSE = 'quote_close';
    /** `feedback_questions.response_type` — what a ticked reason asks for beyond the tick. */
    public static FEEDBACK_RESPONSE_TEXT = 'TEXT';
    public static FEEDBACK_RESPONSE_PRODUCTS = 'PRODUCTS';

    public static ARE_YOU_SURE_YOU_WANT_TO = 'Are you sure you want to ';
    public static SAMPLE_REQUEST_CONVERT_INTO_QUOTE_CONFIRM_MESSAGE = 'Do you want to proceed with converting this sample request into quote?'

    public static DEFAULT_DEPOSIT_AMOUNT_PERCENTAGE = 70;

    public static TRADE_DISCOUNT_PERCENTAGE = 20;

    public static AI_CONSTANTS = {
        COPY: 'Content copied to clipboard',
        UPDATE: 'Conversation title has been updated successfully.',
        DELETE: 'Conversation has been deleted successfully.',
        DELETE_CONFIRM: 'Are you sure you want to delete this conversation?'
    }

    public static UNABLE_CONVERSION_SAMPLE_ORDER_STATUS_CHANGE = 'You can’t update the sample order status. This sample order is not completed.';
    public static LEAD_TIME_TBC_TOOLTIP = 'Please liaise with the despatching manager for confirmation of inbound stock ETA.'

    public static STOCK_REQUIRED_DATE_MESSAGE = 'Select the Stock Required Date (SRD) before accepting the quote.'

    public static ORDER_PAYMENT_PAID = 'Paid';
    public static ORDER_PAYMENT_UNPAID = 'Unpaid'

    public static FILE_UPLOAD_INVALID_TYPE_MESSAGE = 'Invalid file type. upload PDF, DOC, DOCX, JPG, or PNG files only.'

    public static STORAGE_COMMENCEMENT_DATE_UPDATE = 'Storage fees commencement date has been updated successfully.'
    public static SRD_HISTORY_MODAL_TITLE = 'Stock Required Date (SRD) Histories'
    public static SCD_HISTORY_MODAL_TITLE = 'Storage Fees Commencement Date (SFCD) Histories'
    public static SRD_HISTORY_TYPE = 'SRD'
    public static SCD_HISTORY_TYPE = 'SCD'
    // GC-339 Phase 1: manual (admin-fixed) storage fee.
    public static MSF_HISTORY_TYPE = 'MSF'
    public static MSF_HISTORY_MODAL_TITLE = 'Manual Storage Fee Histories'
    public static PRODUCTION_DELAY_HISTORY_MODAL_TITLE = 'In Production Delay Histories'
    public static MANUAL_STORAGE_FEE_UPDATE = 'Manual storage fee has been updated successfully.'
    public static SELECT_VALID_DATE_ERROR = 'Select a valid stock required date.'

    public static TASK_CATEGORY_ASSIGN_SALES_REP_CONFIRM = {
        TITLE: 'Assign to contact\'s sales rep',
        BODY_MESSAGE: 'Are you sure you want to assign this task category to the contact\'s sales rep?',
    }

    public static TASK_CATEGORY_CHANGE_ADMIN_CONFIRM = {
        TITLE: 'Change Responsible Admin',
        BODY_MESSAGE: 'Are you sure you want to change the Responsible Admin?',
    }

    public static TASK_CATEGORY_ASSIGN_ADMIN_SUCCESS = 'Assigned admin updated.';
    public static TASK_CATEGORY_ASSIGN_ADMIN_FAILED = 'Failed to update assigned admin.';
    public static TASK_CATEGORY_SALES_REP_ASSIGNED_SUCCESS = "Assigned to contact's sales rep.";
    public static TASK_CATEGORY_SALES_REP_ASSIGNED_FAILED = 'Failed to update sales rep assignment.';
    public static TASK_CATEGORY_ASSIGN_REQUIRED = 'Assign a task category to either an admin or the contact\'s sales representative.';
    public static TASK_CATEGORY_TITLE_UPDATE_SUCCESS = 'Task category name updated.';
    public static TASK_CATEGORY_TITLE_UPDATE_FAILED = 'Failed to update task category name.';
    public static TASK_CATEGORY_DESCRIPTION_UPDATE_SUCCESS = 'Task category description updated.';
    public static TASK_CATEGORY_DESCRIPTION_UPDATE_FAILED = 'Failed to update task category description.';
}
